import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Input, Textarea } from '@heroui/input';
import { Select, SelectItem } from '@/components/ui/AppSelect';
import { DatePicker } from '@heroui/date-picker';
import { TimeInput } from '@heroui/date-input';
import { Switch } from '@heroui/switch';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createTicket, updateTicket, deleteTicket, fetchOrganizerEventById, fetchEventOrdersById, updateEvent, uploadBanner, uploadSocialImage, uploadAdditionalBanners, deleteEventAttachments, createWalkinOrder, updateOrderStatus, deleteEvent, connectEventToOrganizer, checkHandleAvailability, resendOrderConfirmation, updateOrderInfo, getPayoutAccount, fetchTierSummary, fetchPendingCommentCount } from '../apiEndpoints';
import { describeFeeFormula } from '@/utils/fees';
import EventSalesDashboard from '@/components/EventSalesDashboard';
import { refundSummary } from '@/utils/dashboardMetrics';
import { exportOrdersToPDF, type OrderData, type EventData } from '../utils/pdfExport';
import { downloadOrdersAsCSV } from '../utils/csvExport';
import { createContact, createListAndAddContacts, createContactList, bulkImportContactsFromCSV } from '../apiEndpoints';
import { fetchVenue, createVenue, fetchEventVenues } from '../api/venueApi';
import VenuePicker from './venue-picker/VenuePicker';
import { useAuth } from '@/contexts/auth-context';
import useIsMobile from '@/hooks/useIsMobile';
import { canUserCheckInForEvent, canUserEditEvent } from '@/utils/eventAuthorization';
import { RoleBasedAccess, useEventAccess } from './RoleBasedAccess';
import { CalendarDate, getLocalTimeZone, parseDate, parseTime, Time, today } from '@internationalized/date';
import { GoogleMap, useJsApiLoader, Autocomplete } from '@react-google-maps/api';
import Trash from '@/icons/Trash';
import Plus from '@/icons/Plus';
import Search from '@/icons/Search';
import EditIcon from '@/icons/EditIcon';
import { Button, LinkIcon, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, useDisclosure } from '@heroui/react';
import { Drawer, DrawerContent, DrawerHeader, DrawerBody, DrawerFooter } from '@heroui/drawer';
import TicketCheckInModal from './TicketCheckInModal';
import GlobalQRScannerModal from './GlobalQRScannerModal';
import RefundModal from './RefundModal';
import { useUserRoles } from '@/utils/roleUtils';
import EmailModal from './EmailModal';
import BulkEmailModal from './BulkEmailModal';
import BulkFreeTicketsModal from './BulkFreeTicketsModal';
import UploadImagePlaceholder from './UploadImagePlaceholder';
import MoreIcon from '@/icons/MoreIcon';
import AuthorizedEmailsManager from './AuthorizedEmailsManager';
import DeleteConfirmModal from './DeleteConfirmModal';
import FAQsTab from './FAQsTab';
import CommentsTab, { pendingCommentsKey } from './CommentsTab';
import VenueMapPreview from './tickets/VenueMapPreview';
import TierPricingPanel from './tickets/TierPricingPanel';
import SeatMapSelector from './SeatMapSelector';
import TicketTierPricing from './tickets/TicketTierPricing';
import { tiersFromSeatMap, validateTierSelection, nonEmptyOverrides, minEffectivePrice } from '@/utils/tierPricing';
import { IconLayoutDashboard as LayoutDashboard, IconShoppingBag as ShoppingBag, IconTicket as Ticket, IconHelpCircle as HelpCircle, IconInfoCircle as InfoIcon, IconPhotoPlus as ImagePlus, IconAlertTriangle as AlertTriangle, IconCalendar as Calendar, IconMapPin as MapPin, IconCurrencyDollar as DollarSign, IconSettings as SettingsIcon, IconBuildingSkyscraper as Building2, IconChevronDown as ChevronDown, IconMail as Mail, IconScan as Scan, IconUsers as Users, IconFileDownload as FileDown, IconFileUpload as FileUp } from '@tabler/icons-react';
import { addToast } from '@heroui/toast';
import { hasAspectRatio, BANNER_RATIO_ERROR } from '@/utils/imageRatio';
import moment from 'moment';
import { CURRENCY_OPTIONS, currencySymbol, resolveCurrency, type EventCurrency } from '@/utils/currency';
const libraries: ("places")[] = ["places"];

const ecFieldWrap = 'rounded-2xl bg-grey99 shadow-none ring-1 ring-black/[0.04] data-[hover=true]:bg-grey95 group-data-[focus=true]:bg-grey99 group-data-[focus=true]:ring-2 group-data-[focus=true]:ring-primary/50 dark:bg-white/[0.06] dark:ring-white/10 dark:data-[hover=true]:bg-white/10 dark:group-data-[focus=true]:bg-white/[0.06] dark:group-data-[focus=true]:ring-nightAccent/60';
const ecFieldText = 'text-base text-grey10 placeholder:text-grey60 dark:text-white dark:placeholder:text-nightMuted';
const ecInputCls = { inputWrapper: `min-h-14 ${ecFieldWrap}`, input: ecFieldText };
const ecTextareaCls = { inputWrapper: ecFieldWrap, input: ecFieldText };
const ecSelectCls = { trigger: ecFieldWrap, value: ecFieldText };
const ecDateCls = { inputWrapper: `min-h-14 ${ecFieldWrap}` };
const ecLabel = 'mb-2 block text-sm font-semibold text-grey20 dark:text-nightTextSecondary';
const ecPill = 'inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold';
const ecStatTile = 'rounded-[28px] bg-grey99 p-5 ring-1 ring-black/[0.04] dark:bg-white/[0.04] dark:ring-white/[0.06]';
const ecStatLabel = 'text-xs font-bold uppercase tracking-[0.14em] text-grey40 dark:text-nightMuted';
const ecStatValue = 'text-[clamp(28px,3vw,40px)] font-bold leading-none tracking-[-0.04em] text-grey10 dark:text-white';
const ecH2 = 'text-[clamp(28px,4vw,44px)] font-bold leading-[1] tracking-[-0.04em] text-grey10 dark:text-white';
const ecCard = 'rounded-[28px] bg-white ring-1 ring-black/[0.06] dark:bg-white/[0.04] dark:ring-white/10';
const ecMenu = 'rounded-2xl bg-white p-1.5 shadow-xl ring-1 ring-black/[0.06] dark:bg-nightElevated dark:ring-white/10';
const ecMenuItem = 'w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-semibold transition-colors';
const ecIconBtn = 'flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-grey99 text-grey20 transition-colors hover:bg-grey95 dark:bg-white/[0.06] dark:text-nightTextSecondary dark:hover:bg-white/10';
const ecBtnAccent = 'inline-flex h-11 items-center justify-center gap-2 rounded-full bg-primary px-5 text-sm font-semibold text-white transition-colors hover:bg-primary30 disabled:opacity-60 dark:bg-nightAccent dark:text-nightBg dark:hover:bg-primary70';
const ecBtnGhost = 'inline-flex h-11 items-center justify-center gap-2 rounded-full px-5 text-sm font-semibold text-grey10 ring-1 ring-grey90 transition-colors hover:bg-grey99 disabled:opacity-60 dark:text-white dark:ring-white/15 dark:hover:bg-white/[0.06]';

const EVENT_SETTINGS_ANCHOR = 'event-settings';
const EVENT_VENUE_ANCHOR = 'event-venue';

function InfoSection({
    id,
    icon,
    title,
    subtitle,
    children,
}: {
    id?: string;
    icon: React.ReactNode;
    title: string;
    subtitle?: string;
    children: React.ReactNode;
}) {
    return (
        <div id={id} className="scroll-mt-24 rounded-[28px] bg-white ring-1 ring-black/[0.06] dark:bg-white/[0.04] dark:ring-white/10 overflow-hidden">
            <div className="px-5 sm:px-7 pt-6 pb-1">
                <div className="text-xl font-bold tracking-[-0.02em] text-grey10 dark:text-white">{title}</div>
                {subtitle && <div className="text-sm text-grey40 dark:text-nightMuted mt-1">{subtitle}</div>}
            </div>
            <div className="px-5 sm:px-7 pt-5 pb-7 flex flex-col gap-6">
                {children}
            </div>
        </div>
    );
}

function PillChoice({
    options,
    value,
    onChange,
}: {
    options: { value: string; label: string; icon?: React.ReactNode }[];
    value: string;
    onChange: (value: string) => void;
}) {
    return (
        <div className="flex flex-wrap gap-2">
            {options.map(opt => {
                const active = value === opt.value;
                return (
                    <button
                        key={opt.value}
                        type="button"
                        onClick={() => onChange(opt.value)}
                        className={`flex h-10 items-center gap-2 px-4 rounded-full text-sm font-semibold transition-colors ${active ? 'bg-primary text-white dark:bg-nightAccent dark:text-nightBg' : 'bg-grey99 text-grey20 hover:bg-grey95 dark:bg-white/[0.06] dark:text-nightTextSecondary dark:hover:bg-white/10'}`}
                    >
                        {opt.icon}
                        {opt.label}
                    </button>
                );
            })}
        </div>
    );
}

function formatDurationMinutes(totalMinutes: number): string {
    if (totalMinutes < 0) return '';
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours === 0) return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
    if (minutes === 0) return `${hours} hour${hours !== 1 ? 's' : ''}`;
    return `${hours} hour${hours !== 1 ? 's' : ''} ${minutes} minute${minutes !== 1 ? 's' : ''}`;
}

// Helper function to generate ObjectId-like string
function generateObjectId(): string {
    const timestamp = Math.floor(new Date().getTime() / 1000).toString(16);
    const randomPart = Array.from({ length: 16 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
    return (timestamp + randomPart).padEnd(24, '0').substring(0, 24);
}

// PromocodeForm component - simplified for per-ticket use
function PromocodeForm({ dateRanges, promocode, onSave, onCancel, ticketId, formRef }: {
    dateRanges: any[],
    promocode: any,
    onSave: (data: any) => void,
    onCancel: () => void,
    ticketId: string,
    formRef?: React.MutableRefObject<(() => void) | null>
}) {
    const [formData, setFormData] = useState({
        code: promocode?.codeName || '',
        description: promocode?.appliedTo || '',
        maxUsage: promocode?.limit ? parseInt(promocode.limit) : 100,
        startDate: promocode?.startDate ? parseDate(promocode.startDate) : null as CalendarDate | null,
        endDate: promocode?.endDate ? parseDate(promocode.endDate) : null as CalendarDate | null,
        discountType: promocode?.discountType || 'fixed' as 'fixed' | 'percentage',
        offPrice: promocode?.discountType === 'fixed' ? parseFloat(promocode?.discount || '0') : 0,
        offPercent: promocode?.discountType === 'percentage' ? parseFloat(promocode?.discount || '0') : 0,
        oneTimePerUser: promocode?.oneTimePerUser || false,
        isActive: promocode?.isActive !== false,
        ticketId: promocode?.ticketId || '',
    });

    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    const validateForm = () => {
        const newErrors: { [key: string]: string } = {};

        if (!formData.code.trim()) {
            newErrors.code = 'Code is required';
        }

        if (!formData.description.trim()) {
            newErrors.description = 'Description is required';
        }

        if (!formData.startDate) {
            newErrors.startDate = 'Start date is required';
        }

        if (!formData.endDate) {
            newErrors.endDate = 'End date is required';
        }

        if (formData.startDate && formData.endDate && formData.startDate >= formData.endDate) {
            newErrors.endDate = 'End date must be after start date';
        }

        if (formData.discountType === 'fixed' && formData.offPrice <= 0) {
            newErrors.offPrice = 'Discount amount must be greater than 0';
        }

        if (formData.discountType === 'percentage' && (formData.offPercent <= 0 || formData.offPercent > 100)) {
            newErrors.offPercent = 'Discount percentage must be between 1 and 100';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e: React.FormEvent) => {
        if (!validateForm()) {
            return;
        }

        const data = {
            _id: promocode?._id || generateObjectId(),
            codeName: formData.code,
            appliedTo: formData.description,
            limit: formData.maxUsage.toString(),
            uses: promocode?._id ? (promocode.uses || '0') : '0', // Use existing uses when editing, otherwise set to 0 for new promocodes
            startDate: formData.startDate ? formData.startDate.toString() : '',
            endDate: formData.endDate ? formData.endDate.toString() : '',
            discountType: formData.discountType,
            discount: formData.discountType === 'fixed' ? formData.offPrice.toString() : formData.offPercent.toString(),
            oneTimePerUser: formData.oneTimePerUser,
            isActive: formData.isActive,
            ticketId: ticketId,
            ticketName: '',
        };
        onSave(data);
    };

    useEffect(() => {
        if (formRef) {
            formRef.current = () => handleSubmit({} as React.FormEvent);
        }
    }, [formRef, formData]);

    const generatePromoCode = () => {
        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        let code = '';
        for (let i = 0; i < 6; i++) {
            code += characters.charAt(Math.floor(Math.random() * characters.length));
        }
        setFormData({ ...formData, code });
    };

    // Calculate date range from event times
    const eventStartDate = dateRanges && dateRanges.length > 0 && dateRanges[0]?.start
        ? dateRanges[0].start
        : today(getLocalTimeZone());

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className={ecLabel}>Code *</label>
                    <div className="flex gap-2">
                        <Input
                            value={formData.code}
                            onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                            placeholder="e.g., SAVE20"
                            required
                            className="flex-1"
                            classNames={ecInputCls}
                            isInvalid={!!errors.code}
                            errorMessage={errors.code}
                        />
                        <Button type="button" size="sm" variant="bordered" onClick={generatePromoCode} className={`${ecBtnGhost} h-12 min-w-0 border-0 bg-transparent px-4`}>
                            Generate
                        </Button>
                    </div>
                </div>
                <div>
                    <label className={ecLabel}>Description *</label>
                    <Input
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="e.g., 20% off for early birds"
                        required
                        classNames={ecInputCls}
                        isInvalid={!!errors.description}
                        errorMessage={errors.description}
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className={ecLabel}>Max Usage *</label>
                    <Input
                        type="number"
                        value={formData.maxUsage.toString()}
                        onChange={(e) => setFormData({ ...formData, maxUsage: parseInt(e.target.value) })}
                        placeholder="100"
                        required
                        classNames={ecInputCls}
                    />
                </div>
                <div>
                    <label className={ecLabel}>Discount Type *</label>
                    <Select aria-label="Select"
                        classNames={ecSelectCls}
                        selectedKeys={[formData.discountType]}
                        onSelectionChange={(keys) => {
                            const value = Array.from(keys)[0] as string;
                            setFormData({ ...formData, discountType: value as 'fixed' | 'percentage' });
                        }}
                    >
                        <SelectItem key="fixed">Fixed Amount</SelectItem>
                        <SelectItem key="percentage">Percentage</SelectItem>
                    </Select>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className={ecLabel}>Start Date *</label>
                    <DatePicker
                        visibleMonths={2}
                        value={formData.startDate}
                        onChange={(date) => setFormData({ ...formData, startDate: date, endDate: date && formData.endDate && formData.endDate.compare(date) < 0 ? date : formData.endDate })}
                        label="Select start date"
                        classNames={ecDateCls}
                        //minValue={today(getLocalTimeZone())}
                        //maxValue={eventStartDate}
                        isRequired
                        isInvalid={!!errors.startDate}
                        errorMessage={errors.startDate}
                    />
                </div>
                <div>
                    <label className={ecLabel}>End Date *</label>
                    <DatePicker
                        visibleMonths={2}
                        value={formData.endDate}
                        onChange={(date) => setFormData({ ...formData, endDate: date })}
                        label="Select end date"
                        classNames={ecDateCls}
                        minValue={formData.startDate}
                        //maxValue={eventStartDate}
                        isRequired
                        isInvalid={!!errors.endDate}
                        errorMessage={errors.endDate}
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {formData.discountType === 'fixed' ? (
                    <div>
                        <label className={ecLabel}>Discount Amount ($) *</label>
                        <Input
                            type="number"
                            step="0.01"
                            value={formData.offPrice.toString()}
                            onChange={(e) => setFormData({ ...formData, offPrice: parseFloat(e.target.value) })}
                            placeholder="10.00"
                            required
                            classNames={ecInputCls}
                            isInvalid={!!errors.offPrice}
                            errorMessage={errors.offPrice}
                        />
                    </div>
                ) : (
                    <div>
                        <label className={ecLabel}>Discount Percentage (%) *</label>
                        <Input
                            type="number"
                            min="0"
                            max="100"
                            value={formData.offPercent.toString()}
                            onChange={(e) => setFormData({ ...formData, offPercent: parseFloat(e.target.value) })}
                            placeholder="20"
                            required
                            classNames={ecInputCls}
                            isInvalid={!!errors.offPercent}
                            errorMessage={errors.offPercent}
                        />
                    </div>
                )}
                <div className="flex flex-wrap items-center gap-4">
                    <div className="flex items-center">
                        <Switch
                            isSelected={formData.oneTimePerUser}
                            onValueChange={(value) => setFormData({ ...formData, oneTimePerUser: value })}
                        />
                        <label className="ml-2 text-sm font-medium text-grey20 dark:text-nightTextSecondary">One-time per user</label>
                    </div>
                    <div className="flex items-center">
                        <Switch
                            isSelected={formData.isActive}
                            onValueChange={(value) => setFormData({ ...formData, isActive: value })}
                        />
                        <label className="ml-2 text-sm font-medium text-grey20 dark:text-nightTextSecondary">Active</label>
                    </div>
                </div>
            </div>
        </form>
    );
}

// Helper to format time object to 'HH:mm' string
function formatTimeObjToString(timeObj: any) {
    if (!timeObj || typeof timeObj.hour !== 'number' || typeof timeObj.minute !== 'number') return '';
    const hh = String(timeObj.hour).padStart(2, '0');
    const mm = String(timeObj.minute).padStart(2, '0');
    return `${hh}:${mm}`;
}

// Helper to format date object to 'YYYY-MM-DD' string
function formatDateObjToString(dateObj: any) {
    if (!dateObj || typeof dateObj.toString !== 'function') return '';
    return dateObj.toString();
}

// Helper to compare date and time objects
function isEndAfterStart(startDate: any, startTime: any, endDate: any, endTime: any) {
    if (!startDate || !endDate) return true; // handled by required checks elsewhere
    const startDateStr = formatDateObjToString(startDate);
    const endDateStr = formatDateObjToString(endDate);
    // If dates differ, end date must be >= start date
    if (endDateStr > startDateStr) return true;
    if (endDateStr < startDateStr) return false;
    // Same date: compare times if both provided
    if (!startTime || !endTime) return true; // if either missing, separate required checks will handle
    const startTimeStr = formatTimeObjToString(startTime);
    const endTimeStr = formatTimeObjToString(endTime);
    return endTimeStr > startTimeStr;
}

// Types for event, ticket, and order (based on backend prompt)
interface Ticket {
    _id: string;
    name: string;
    quantity: number;
    price: number;
    type: string;
    description?: string;
    status: boolean;
    saleStart?: string;
    saleStartTime?: string;
    saleEnd?: string;
    saleEndTime?: string;
    refundPolicy?: string;
    soldCount?: number;
}

interface Event {
    _id: string;
    title: string;
    description: string;
    category: string;
    banner: string;
    socialImages?: string[];
    additionalImages?: string[];
    times: Array<{
        startDate: string;
        startTime?: string;
        endDate: string;
        endTime?: string;
    }>;
    address: string;
    venue: string;
    map?: string;
    details?: string;
    organizer: {
        id: string;
        name: string;
        avatar: string;
    };
    likes?: number;
    isNew?: boolean;
    status?: string;
    createdAt?: string;
    updatedAt?: string;
    tickets?: Ticket[];
    totalSold?: number;
    totalCapacity?: number;
    totalRevenue?: number;
    attachments?: any;
    refundable?: boolean;
    refundDaysBeforeStart?: number;
    feeMode?: 'added' | 'included';
    taxMode?: 'added' | 'included';
    currency?: EventCurrency;
    authorizedEmails?: string[];
}

interface Order {
    _id: string;
    orderId: string;
    userId: string;
    eventId: string;
    tickets: Array<{
        ticketId: string;
        quantity: number;
        price: number;
        type: string;
    }>;
    totalAmount: number;
    status: string;
    paymentId?: string;
    qrCode?: string;
    attendeeName?: string;
    email?: string;
    phone?: string;
    createdAt: string;
    updatedAt: string;
}

// Accept eventId prop
export default function EventComponent({ eventId, activeTab }: { eventId?: string; activeTab?: string }) {
    // Move activeTab state to the very top
    const { user } = useAuth();
    const isMobile = useIsMobile();
    const { eid } = useParams<{ eid?: string }>();
    const navigate = useNavigate();
    const { hash: locationHash } = useLocation();

    // Resolve eventId from URL params or prop
    const [effectiveEventId, setEffectiveEventId] = useState<string>('');
    React.useEffect(() => {
        setEffectiveEventId((eid || eventId || '').toString());
    }, [eid, eventId]);

    // Fetch event info (including tickets)
    const { data: event, isLoading: isEventLoading, isError: isEventError, refetch: refetchEvent } = useQuery<any>({
        queryKey: ['organizerEvent', effectiveEventId],
        queryFn: () => fetchOrganizerEventById(effectiveEventId),
        enabled: !!effectiveEventId,
        refetchOnWindowFocus: activeTab === 'Comments',
    });
    // Null-proof event data based on real API response
    const eventData = event?.data || {};
    const cur = currencySymbol(eventData.currency);

    // Fetch venue data if event has venueId
    const { data: venueResponse, isLoading: isVenueLoading, isError: isVenueError, refetch: refetchVenue } = useQuery({
        queryKey: ['venue', eventData.venueId],
        queryFn: () => fetchVenue(eventData.venueId!),
        enabled: Boolean(eventData.venueId),
    });

    const venue = venueResponse?.data;
    const tierList = useMemo(() => tiersFromSeatMap(venue?.seatMap), [venue?.seatMap]);
    const tierMode = tierList.length > 0;
    const { data: drawerTierSummary } = useQuery({
        queryKey: ['tierSummaryDrawer', eventData._id],
        queryFn: () => fetchTierSummary(eventData._id),
        enabled: tierMode && Boolean(eventData._id),
    });

    const { data: pendingCommentsData } = useQuery<any>({
        queryKey: pendingCommentsKey(effectiveEventId),
        queryFn: () => fetchPendingCommentCount(effectiveEventId),
        enabled: Boolean(effectiveEventId) && eventData?.eventType === 'created',
        refetchInterval: 30000,
        retry: false,
    });
    const pendingCommentCount: number = pendingCommentsData?.data?.pending || 0;

    // Use activeTab from props or default to 'Dashboard'
    const currentActiveTab = (activeTab === 'SeatPlan' ? 'Tickets' : activeTab) as 'Dashboard' | 'Orders' | 'Info' | 'Tickets' | 'FAQs' | 'Comments' || 'Dashboard';

    // Function to handle tab changes and update URL
    const handleTabChange = (newTab: 'Dashboard' | 'Orders' | 'Info' | 'Tickets' | 'FAQs' | 'Comments') => {
        const currentEventId = eid || eventId;
        if (currentEventId) {
            navigate(`/organizations/events/${currentEventId}/${newTab}`);
        }
    };
    const openVenueField = () => {
        const currentEventId = eid || eventId;
        if (currentEventId) navigate(`/organizations/events/${currentEventId}/Info#${EVENT_VENUE_ANCHOR}`);
    };
    const openCommentSettings = () => {
        const currentEventId = eid || eventId;
        if (currentEventId) navigate(`/organizations/events/${currentEventId}/Info#${EVENT_SETTINGS_ANCHOR}`);
    };
    // Fetch event orders
    const { data: ordersData, isLoading: isOrdersLoading, isError: isOrdersError, refetch: refetchOrders } = useQuery<any>({
        queryKey: ['eventOrders', effectiveEventId],
        queryFn: () => fetchEventOrdersById(effectiveEventId),
        enabled: !!effectiveEventId,
    });
    useEffect(() => {
        const anchor = locationHash === `#${EVENT_SETTINGS_ANCHOR}` ? EVENT_SETTINGS_ANCHOR : locationHash === `#${EVENT_VENUE_ANCHOR}` ? EVENT_VENUE_ANCHOR : null;
        if (!anchor || currentActiveTab !== 'Info') return;
        let attempts = 0;
        const timer = window.setInterval(() => {
            const target = document.getElementById(anchor);
            attempts += 1;
            if (target) {
                target.scrollIntoView({ behavior: 'smooth', block: anchor === EVENT_VENUE_ANCHOR ? 'center' : 'start' });
                window.clearInterval(timer);
            } else if (attempts >= 50) {
                window.clearInterval(timer);
            }
        }, 100);
        return () => window.clearInterval(timer);
    }, [locationHash, currentActiveTab, effectiveEventId]);
    const orders: any[] = Array.isArray(ordersData?.data?.data) ? ordersData.data.data : [];
    const currencyLocked = orders.some(order => Number(order?.totalAmount) > 0);

    // Add state for expanded row and search/filter
    const [expandedOrderIdx, setExpandedOrderIdx] = useState<number | null>(null);
    const [search, setSearch] = useState('');
    const [ticketFilter, setTicketFilter] = useState('all');
    const [ticketFilterOpen, setTicketFilterOpen] = useState(false);
    const [orderSort, setOrderSort] = useState<'newest' | 'oldest'>('newest');
    const [orderSortOpen, setOrderSortOpen] = useState(false);
    // Close the ticket filter / sort dropdowns when clicking outside them
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Element;
            if (!target.closest('.order-ticket-filter')) setTicketFilterOpen(false);
            if (!target.closest('.order-sort-filter')) setOrderSortOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [openActionMenuIdx, setOpenActionMenuIdx] = useState<number | null>(null);
    const [mobileOrdersActionsOpen, setMobileOrdersActionsOpen] = useState(false);
    const [mobileExpandedOrderIdx, setMobileExpandedOrderIdx] = useState<number | null>(null);
    const [globalQRScannerOpen, setGlobalQRScannerOpen] = useState(false);
    const [ticketCheckInModalOpen, setTicketCheckInModalOpen] = useState(false);
    const [selectedTicketForCheckIn, setSelectedTicketForCheckIn] = useState<any>(null);
    const [selectedOrderForTicketCheckIn, setSelectedOrderForTicketCheckIn] = useState<any>(null);
    const [refundModalOpen, setRefundModalOpen] = useState(false);
    const [selectedOrderForRefund, setSelectedOrderForRefund] = useState<any>(null);
    const [emailModalOpen, setEmailModalOpen] = useState(false);
    const [selectedOrderForEmail, setSelectedOrderForEmail] = useState<any>(null);
    const [bulkEmailModalOpen, setBulkEmailModalOpen] = useState(false);
    const [bulkFreeTicketsModalOpen, setBulkFreeTicketsModalOpen] = useState(false);
    const [statusChangeModalOpen, setStatusChangeModalOpen] = useState(false);
    const [selectedOrderForStatusChange, setSelectedOrderForStatusChange] = useState<any>(null);
    const [newStatus, setNewStatus] = useState('');
    const [statusChangeNotes, setStatusChangeNotes] = useState('');

    const resendOrderEmailMutation = useMutation({
        mutationFn: (order: any) => resendOrderConfirmation(order._id),
        onSuccess: (data: any) => {
            addToast({ title: 'Email sent', description: data?.message || 'Confirmation email sent', color: 'success' });
        },
        onError: (error: any) => {
            const msg = error?.response?.data?.message || error?.message || 'Failed to resend email';
            addToast({ title: 'Resend failed', description: msg, color: 'danger' });
        },
    });

    const [infoOrder, setInfoOrder] = useState<any>(null);
    const [infoForm, setInfoForm] = useState({ name: '', email: '', phone: '' });
    const [infoTickets, setInfoTickets] = useState<{ index: number; label: string; attendeeName: string; attendeeEmail: string; attendeePhone: string }[]>([]);
    const updateOrderInfoMutation = useMutation({
        mutationFn: (order: any) => updateOrderInfo(order._id, {
            name: infoForm.name,
            email: infoForm.email,
            phone: infoForm.phone,
            tickets: infoTickets.map(({ index, attendeeName, attendeeEmail, attendeePhone }) => ({ index, attendeeName, attendeeEmail, attendeePhone })),
        }),
        onSuccess: () => {
            addToast({ title: 'Order updated', description: 'Order info saved', color: 'success' });
            setInfoOrder(null);
            queryClient.invalidateQueries({ queryKey: ['eventOrders'] });
        },
        onError: (error: any) => {
            const msg = error?.response?.data?.message || error?.message || 'Failed to update order';
            addToast({ title: 'Update failed', description: Array.isArray(msg) ? msg.join(', ') : msg, color: 'danger' });
        },
    });
    const openInfoModal = (order: any) => {
        setInfoForm({
            name: order.customerName || order.guestName || '',
            email: order.customerEmail || order.guestEmail || order.email || '',
            phone: order.customerPhone || order.guestPhone || '',
        });
        setInfoTickets((Array.isArray(order.tickets) ? order.tickets : []).map((t: any, index: number) => ({
            index,
            label: `${t.ticketId?.name || t.ticketTitle || 'Ticket'} #${index + 1}`,
            attendeeName: t.attendeeName || '',
            attendeeEmail: t.attendeeEmail || '',
            attendeePhone: t.attendeePhone || '',
        })).filter((t: any) => t.attendeeName || t.attendeeEmail || t.attendeePhone));
        setInfoOrder(order);
    };
    const patchInfoTicket = (index: number, patch: Record<string, string>) =>
        setInfoTickets((rows) => rows.map((r) => (r.index === index ? { ...r, ...patch } : r)));
    const emailPattern = /^\S+@\S+\.\S+$/;
    const infoValid = emailPattern.test(infoForm.email.trim()) && infoTickets.every((t) => !t.attendeeEmail.trim() || emailPattern.test(t.attendeeEmail.trim()));

    // MoreIcon menu state
    const [moreMenuOpen, setMoreMenuOpen] = useState(false);
    const [sidebarMenuOpen, setSidebarMenuOpen] = useState(false);
    const [mobileTabMenuOpen, setMobileTabMenuOpen] = useState(false);
    const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState(false);

    // Ticket deletion confirmation state
    const [ticketDeleteConfirmModalOpen, setTicketDeleteConfirmModalOpen] = useState(false);
    const [ticketToDelete, setTicketToDelete] = useState<number | null>(null);
    const [isDeletingTicket, setIsDeletingTicket] = useState(false);

    // Walk-in order modal state
    const [walkInModalOpen, setWalkInModalOpen] = useState(false);
    const [walkInForm, setWalkInForm] = useState({
        customerName: '',
        customerEmail: '',
        customerPhone: '',
        selectedTickets: [] as Array<{ ticketId: string; quantity: number; price: number; name: string; selectedDate?: string; seatUuids?: string[]; seatTotal?: number }>,
        totalAmount: 0
    });
    const [walkInSeats, setWalkInSeats] = useState<string[]>([]);
    const [walkInUnresolved, setWalkInUnresolved] = useState<string[]>([]);
    const [walkInSeatLabels, setWalkInSeatLabels] = useState<Record<string, string>>({});
    const [walkInAssignments, setWalkInAssignments] = useState<Array<{ seatUuid: string; ticketId: string; status: string; price?: number }>>([]);
    const walkInSeatMode = Boolean(eventData.venueId && venue?.seatMap);

    const { data: eventVenuesResponse } = useQuery({
        queryKey: ['event-venues', effectiveEventId],
        queryFn: () => fetchEventVenues(effectiveEventId),
        enabled: Boolean(effectiveEventId),
    });
    const eventVenues = eventVenuesResponse?.data ?? [];
    // Promocode modal state
    const { isOpen: isPromocodeModalOpen, onOpen: onPromocodeModalOpen, onOpenChange: onPromocodeModalOpenChange } = useDisclosure();
    const [promocodeModalTicketId, setPromocodeModalTicketId] = useState<string | null>(null);
    const [editingPromocode, setEditingPromocode] = useState<string | null>(null);
    const promocodeFormRef = React.useRef<() => void>(null);

    // Helper function to generate ObjectId-like string
    function generateObjectId(): string {
        const timestamp = Math.floor(new Date().getTime() / 1000).toString(16);
        const randomPart = Array.from({ length: 16 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
        return (timestamp + randomPart).padEnd(24, '0').substring(0, 24);
    }

    const [walkInErrors, setWalkInErrors] = useState({
        customerName: '',
        customerEmail: '',
        customerPhone: ''
    });

    // Date selection state
    const [showDateCalendar, setShowDateCalendar] = useState(false);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selectedDateForTicket, setSelectedDateForTicket] = useState<string | null>(null);

    // Filtered orders (UI only)
    const filteredOrders = orders
        .filter(order => {
            if (!search) return true;
            return (
                (order.customerName || order.guestName || '').toLowerCase().includes(search.toLowerCase()) ||
                (order.hashedUniqueId || '').toLowerCase().includes(search.toLowerCase())
            );
        })
        .filter(order => {
            if (ticketFilter === 'all') return true;
            return Array.isArray(order.tickets) && order.tickets.some((t: any) => {
                const ticketId = t.ticketId?._id || t.ticketId;
                return String(ticketId) === String(ticketFilter);
            });
        })
        .sort((a, b) => {
            const diff = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
            return orderSort === 'newest' ? -diff : diff;
        });

    // PDF Export functions
    const handleExportOrdersToPDF = () => {
        try {
            const eventData: EventData = {
                title: event?.data?.title || 'Event',
                currency: event?.data?.currency,
                venue: event?.data?.venue || '',
                address: event?.data?.address.fullAddress || '',
                times: event?.data?.times || []
            };

            const ordersData: OrderData[] = filteredOrders.map(order => ({
                _id: order._id,
                hashedUniqueId: order.hashedUniqueId || order.orderId,
                orderId: order.orderId,
                customerName: order.customerName,
                guestName: order.guestName,
                attendeeName: order.attendeeName,
                customerEmail: order.customerEmail,
                guestEmail: order.guestEmail,
                email: order.email,
                customerPhone: order.customerPhone,
                guestPhone: order.guestPhone,
                phone: order.phone,
                totalAmount: order.totalAmount || 0,
                status: order.status || 'pending',
                createdAt: order.createdAt,
                tickets: order.tickets || []
            }));

            exportOrdersToPDF(ordersData, eventData);
        } catch (error) {
            console.error('Error exporting orders to PDF:', error);
            alert('Failed to export orders to PDF. Please try again.');
        }
    };

    // CSV Export function
    const handleExportOrdersToCSV = () => {
        try {
            const eventData: EventData = {
                title: event?.data?.title || 'Event',
                currency: event?.data?.currency,
                venue: event?.data?.venue || '',
                address: event?.data?.address.fullAddress || '',
                times: event?.data?.times || []
            };

            const ordersData: OrderData[] = filteredOrders.map(order => ({
                _id: order._id,
                hashedUniqueId: order.hashedUniqueId || order.orderId,
                orderId: order.orderId,
                customerName: order.customerName,
                guestName: order.guestName,
                attendeeName: order.attendeeName,
                customerEmail: order.customerEmail,
                guestEmail: order.guestEmail,
                email: order.email,
                customerPhone: order.customerPhone,
                guestPhone: order.guestPhone,
                phone: order.phone,
                totalAmount: order.totalAmount || 0,
                status: order.status || 'pending',
                createdAt: order.createdAt,
                tickets: order.tickets || []
            }));

            downloadOrdersAsCSV(ordersData, eventData);
            addToast({
                title: 'Success!',
                description: 'Orders exported to CSV successfully',
                color: 'success',
            });
        } catch (error) {
            console.error('Error exporting orders to CSV:', error);
            addToast({
                title: 'Error',
                description: 'Failed to export orders to CSV. Please try again.',
                color: 'danger',
            });
        }
    };

    // Create Contact List from Orders
    const createContactListMutation = useMutation({
        mutationFn: async ({ listName, contactData }: { listName: string; contactData: any[] }) => {
            // First, create the contact list
            const listData = {
                name: listName,
                description: `Contact list created from ${event?.data?.title || 'Event'} orders`
            };
            const createdList = await createContactList(listData);
            const listId = createdList.data?._id || createdList._id;

            if (!listId) {
                throw new Error('Failed to create contact list');
            }

            // Filter out invalid contacts (must have name and email)
            const validContacts = contactData.filter(contact => contact.name && contact.email);

            if (validContacts.length === 0) {
                throw new Error('No valid contacts found. Contacts must have name and email.');
            }

            // Create mapped JSON content
            const mappedJSON = JSON.stringify(validContacts, null, 2);

            // Create a temporary JSON file
            const tempFile = new File([mappedJSON], 'contacts.json', { type: 'application/json' });

            // Create FormData for upload
            const formData = new FormData();
            formData.append('jsonFile', tempFile);
            formData.append('listId', listId);
            formData.append('totalContacts', validContacts.length.toString());
            formData.append('importType', 'json');

            // Upload to backend for bulk processing
            const result = await bulkImportContactsFromCSV(formData);

            return {
                list: createdList,
                importResult: result
            };
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['contactLists'] });
            queryClient.invalidateQueries({ queryKey: ['contacts'] });

            const importedCount = data.importResult?.importedCount || 0;
            const updatedCount = data.importResult?.updatedCount || 0;
            const totalProcessed = data.importResult?.totalProcessed || 0;

            let message = `Contact list created successfully! Processed ${totalProcessed} contacts.`;
            if (importedCount > 0 && updatedCount > 0) {
                message = `Contact list created successfully! ${importedCount} new contacts imported, ${updatedCount} contacts updated.`;
            } else if (importedCount > 0) {
                message = `Contact list created successfully! ${importedCount} contacts imported.`;
            } else if (updatedCount > 0) {
                message = `Contact list created successfully! ${updatedCount} contacts updated.`;
            }

            addToast({
                title: 'Success!',
                description: message,
                color: 'success',
            });
        },
        onError: (error: any) => {
            console.error('Error creating contact list:', error);
            addToast({
                title: 'Error',
                description: error?.message || 'Failed to create contact list. Please try again.',
                color: 'danger',
            });
        },
    });

    const handleCreateContactList = async () => {
        if (filteredOrders.length === 0) {
            addToast({
                title: 'No Orders',
                description: 'There are no orders to create a contact list from.',
                color: 'warning',
            });
            return;
        }

        const today = new Date().toISOString().split('T')[0]; // Format: YYYY-MM-DD
        const listName = `${event?.data?.title || 'Event'} - ${today}`;

        // Prepare contact data from orders
        const contactDataMap = new Map<string, any>();

        filteredOrders.forEach(order => {
            // Get primary contact info
            const name = order.customerName || order.guestName || order.attendeeName;
            const email = order.customerEmail || order.guestEmail || order.email;
            const phone = order.customerPhone || order.guestPhone || order.phone;

            // Use email as unique key, or name+phone if no email
            const key = email || `${name}_${phone}`;

            if (!contactDataMap.has(key) && (name || email || phone)) {
                // Split name into first and last name
                const nameParts = (name || '').trim().split(/\s+/);
                const firstName = nameParts[0] || '';
                const lastName = nameParts.slice(1).join(' ') || '';

                contactDataMap.set(key, {
                    name: firstName,
                    lastName: lastName,
                    email: email || undefined,
                    phone: phone || undefined,
                });
            }
        });

        const contactData = Array.from(contactDataMap.values());

        if (contactData.length === 0) {
            addToast({
                title: 'No Contacts',
                description: 'No valid contact information found in orders.',
                color: 'warning',
            });
            return;
        }

        createContactListMutation.mutate({ listName, contactData });
    };

    // Pagination (UI only)
    const totalOrders = filteredOrders.length;
    const totalPages = Math.ceil(totalOrders / rowsPerPage);
    const paginatedOrders = filteredOrders.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

    // Helper functions to calculate sold amounts from orders
    function calculateTicketSoldAmount(ticketId: string) {
        return orders.reduce((total: number, order: any) => {
            if (order.status === 'completed' || order.status === 'confirmed') {
                if (Array.isArray(order.tickets)) {
                    const orderTicketsTotal = order.tickets.reduce((ticketTotal: number, ticket: any) => {
                        // Check if this ticket matches the ticketId
                        if (ticket.ticketId && ticket.ticketId._id === ticketId) {
                            return ticketTotal + (ticket.quantity || 0);
                        }
                        return ticketTotal;
                    }, 0);
                    return total + orderTicketsTotal;
                }
            }
            return total;
        }, 0);
    }

    function calculateTotalSoldAmount() {
        return orders.reduce((total: number, order: any) => {
            if (order.status === 'completed' || order.status === 'confirmed') {
                const orderTicketsTotal = Array.isArray(order.tickets)
                    ? order.tickets.reduce((ticketTotal: number, ticket: any) => ticketTotal + (ticket.quantity || 0), 0)
                    : 0;
                return total + orderTicketsTotal;
            }
            return total;
        }, 0);
    }

    function calculateTotalRevenue() {
        return orders.reduce((total: number, order: any) => {
            if (order.status === 'completed' || order.status === 'confirmed') {
                return total + getOrderPayoutBreakdown(order).net;
            }
            return total;
        }, 0);
    }

    // What the organizer is paid for an order: order total minus platform fee, sales tax, and Stripe fee.
    // Mirrors payouts.service getOrganizerNetForOrder on the backend, which caps payout requests to this amount.
    function getOrderPayoutBreakdown(order: any) {
        const total = Number(order?.totalAmount) || 0;
        const platformFee = Number(order?.feeAmount) || 0;
        const storedStripeFee = Number(order?.stripeFeeAmount) || 0;
        if (storedStripeFee) {
            const tax = Number(order?.taxAmount) || 0;
            return { total, platformFee, tax, stripeFee: storedStripeFee, net: Math.max(0, total - platformFee - tax - storedStripeFee) };
        }
        // Legacy orders placed before tax/Stripe-fee tracking: the Stripe fee is estimated at 2.9% + $0.30
        const ticketAmount = Math.max(0, total - platformFee);
        const stripeFee = ticketAmount > 0 ? Math.ceil((ticketAmount * 0.029 + 0.3) * 100 - 1e-9) / 100 : 0;
        return { total, platformFee, tax: 0, stripeFee, net: Math.max(0, ticketAmount - stripeFee) };
    }

    function calculateTotalOrderAmount() {
        return orders.reduce((total: number, order: any) => {
            if (order.status === 'completed' || order.status === 'confirmed') {
                return total + (Number(order.totalAmount) || 0);
            }
            return total;
        }, 0);
    }

    function calculateTicketCheckInAmount(ticketId: string) {
        return orders.reduce((total: number, order: any) => {
            if (order.status === 'completed' || order.status === 'confirmed') {
                if (Array.isArray(order.tickets)) {
                    const ticketCheckIns = order.tickets.reduce((ticketTotal: number, ticket: any) => {
                        // Check if this ticket matches the ticketId and has been checked in
                        if (ticket.ticketId?._id === ticketId && ticket.checkedInAt) {
                            const quantity = Number(ticket.quantity) || 0;
                            return ticketTotal + quantity;
                        }
                        return ticketTotal;
                    }, 0);
                    return total + ticketCheckIns;
                }
            }
            return total;
        }, 0);
    }

    function calculateTicketWalkIns(ticketId: string) {
        return orders.reduce((total: number, order: any) => {
            if (order.status === 'completed' || order.status === 'confirmed') {
                if (Array.isArray(order.tickets)) {
                    const ticketWalkIns = order.tickets.reduce((ticketTotal: number, ticket: any) => {
                        // Check if this ticket matches the ticketId and payment method is cash (walk-in)
                        if (ticket.ticketId?._id === ticketId && order.paymentMethod === 'cash') {
                            return ticketTotal + (ticket.quantity || 0);
                        }
                        return ticketTotal;
                    }, 0);
                    return total + ticketWalkIns;
                }
            }
            return total;
        }, 0);
    }

    function calculateTicketNoShows(ticketId: string) {
        const soldCount = calculateTicketSoldAmount(ticketId);
        const checkInCount = calculateTicketCheckInAmount(ticketId);
        // No shows = sold tickets - checked in tickets (excluding walk-ins for now)
        return Math.max(0, soldCount - checkInCount);
    }

    function calculateTicketRevenue(ticketId: string) {
        return orders.reduce((total: number, order: any) => {
            if (order.status === 'completed' || order.status === 'confirmed') {
                if (Array.isArray(order.tickets)) {
                    const ticketInOrder = order.tickets.filter((ticket: any) => ticket.ticketId?._id === ticketId || ticket.ticketId === ticketId);
                    if (ticketInOrder.length > 0) {
                        // Allocate the order's payout to this ticket type by its share of the order's ticket value
                        const ticketValue = (ticket: any) => Math.max(0, (ticket.price || 0) - (ticket.promocodeDiscount || 0) - (ticket.memberPricingDiscount || 0)) * (ticket.quantity || 1);
                        const orderValue = order.tickets.reduce((acc: number, ticket: any) => acc + ticketValue(ticket), 0);
                        if (orderValue <= 0) return total;
                        const matchedValue = ticketInOrder.reduce((acc: number, ticket: any) => acc + ticketValue(ticket), 0);
                        return total + getOrderPayoutBreakdown(order).net * (matchedValue / orderValue);
                    }
                }
            }
            return total;
        }, 0);
    }

    // Combined promo code + member pricing discount for a ticket, across completed/confirmed orders
    function calculateTicketDiscount(ticketId: string) {
        return orders.reduce((total: number, order: any) => {
            if (order.status === 'completed' || order.status === 'confirmed') {
                if (Array.isArray(order.tickets)) {
                    return total + order.tickets.reduce((acc: number, ticket: any) => {
                        if (ticket.ticketId?._id === ticketId || ticket.ticketId === ticketId) {
                            return acc + (ticket.promocodeDiscount || 0) + (ticket.memberPricingDiscount || 0);
                        }
                        return acc;
                    }, 0);
                }
            }
            return total;
        }, 0);
    }

    function calculateAverageCheckInRate() {
        const totalSold = calculateTotalSoldAmount();
        if (totalSold === 0) return 0;

        const totalCheckIns = calculateTotalCheckIns();
        if (totalCheckIns === "-") return "-";
        if (totalCheckIns === 0) return 0;

        const rate = (totalCheckIns / totalSold) * 100;
        return Math.round(Number(rate) || 0);
    }

    // Helper function to check if event has started
    const hasEventStarted = () => {
        if (!eventData?.times || !Array.isArray(eventData.times) || eventData.times.length === 0) {
            return false;
        }

        const now = new Date();
        const firstEventTime = eventData.times[0];

        if (!firstEventTime.startDate) return false;

        // Create start datetime
        const startDate = new Date(firstEventTime.startDate);
        if (firstEventTime.startTime) {
            const [hours, minutes] = firstEventTime.startTime.split(':').map(Number);
            startDate.setHours(hours, minutes, 0, 0);
        }

        return now >= startDate;
    };

    function calculateTotalCheckIns() {
        if (!Array.isArray(ticketsData?.data)) return 0;

        // If event hasn't started, return "-" as string
        /* if (!hasEventStarted()) {
            return "-";
        } */

        const total = ticketsData.data.reduce((total: number, ticket: any) => {
            const checkInAmount = calculateTicketCheckInAmount(ticket._id);
            return total + (Number(checkInAmount) || 0);
        }, 0);

        return Number(total) || 0;
    }

    function calculateTotalWalkIns() {
        return Array.isArray(ticketsData?.data)
            ? ticketsData.data.reduce((total: number, ticket: any) => {
                return total + calculateTicketWalkIns(ticket._id);
            }, 0)
            : 0;
    }

    function calculateTotalNoShows() {
        if (!Array.isArray(ticketsData?.data)) return 0;

        // If event hasn't started, return "-" as string
        if (!hasEventStarted()) {
            return "-";
        }

        return ticketsData.data.reduce((total: number, ticket: any) => {
            return total + calculateTicketNoShows(ticket._id);
        }, 0);
    }

    // Fee rate calculations
    // Everything taken from a sale is one service fee: platform fee, tax and card processing (total minus net payout)
    function orderServiceFee(order: any) {
        const payout = getOrderPayoutBreakdown(order);
        return payout.platformFee + payout.tax + payout.stripeFee;
    }

    function calculateTotalFees() {
        return orders.reduce((total: number, order: any) => {
            if (order.status === 'completed' || order.status === 'confirmed') {
                return total + orderServiceFee(order);
            }
            return total;
        }, 0);
    }

    const refundTotals = refundSummary(orders);

    function calculateNetRevenue() {
        return calculateTotalRevenue();
    }

    function calculateFeeRate() {
        const totalOrderAmount = calculateTotalOrderAmount();
        if (totalOrderAmount === 0) return 0;
        return Math.round((calculateTotalFees() / totalOrderAmount) * 100);
    }

    function calculateTicketFees(ticketId: string) {
        return orders.reduce((total: number, order: any) => {
            if (order.status === 'completed' || order.status === 'confirmed') {
                if (Array.isArray(order.tickets)) {
                    // Allocate the order's service fee to this ticket type by its share of the order's ticket value, like revenue
                    const ticketValue = (ticket: any) => Math.max(0, (ticket.price || 0) - (ticket.promocodeDiscount || 0) - (ticket.memberPricingDiscount || 0)) * (ticket.quantity || 1);
                    const matched = order.tickets.filter((ticket: any) => ticket.ticketId?._id === ticketId || ticket.ticketId === ticketId);
                    const orderValue = order.tickets.reduce((acc: number, ticket: any) => acc + ticketValue(ticket), 0);
                    if (matched.length > 0 && orderValue > 0) {
                        return total + orderServiceFee(order) * (matched.reduce((acc: number, ticket: any) => acc + ticketValue(ticket), 0) / orderValue);
                    }
                }
            }
            return total;
        }, 0);
    }

    function getTicketAttendeeLines(order: any): string[] {
        if (!Array.isArray(order.tickets)) return [];
        return order.tickets
            .filter((t: any) => t.attendeeName || t.attendeeEmail || t.attendeePhone)
            .map((t: any) => {
                const details = [t.attendeeEmail, t.attendeePhone].filter(Boolean).join(' | ');
                const label = t.attendeeName || 'Attendee';
                return `${t.ticketId?.name || 'Ticket'}: ${label}${details ? ` (${details})` : ''}`;
            });
    }

    // Status badge helper
    function areAllTicketsCheckedIn(order: any): boolean {
        if (!Array.isArray(order.tickets) || order.tickets.length === 0) {
            return false;
        }
        return order.tickets.every((ticket: any) => ticket.checkedInAt);
    }

    function getOrderStatus(order: any): string {
        // If all tickets are checked in, show "checked_in" status
        if (areAllTicketsCheckedIn(order)) {
            return 'checked_in';
        }
        // Otherwise, return the original order status
        return order.status;
    }

    function getStatusBadge(status: string) {
        switch (status) {
            case 'checked_in':
                return <span className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-3 h-7 inline-flex items-center rounded-full text-xs font-semibold">Checked-in</span>;
            case 'pending':
                return <span className="bg-[#FFF3E6] dark:bg-[#FF9800]/15 text-[#FF9800] px-3 h-7 inline-flex items-center rounded-full text-xs font-semibold">Pending</span>;
            case 'completed':
                return <span className="bg-[#E6F7EC] dark:bg-[#233530] text-[#1E7F43] dark:text-nightTextSecondary px-3 h-7 inline-flex items-center rounded-full text-xs font-semibold">Confirmed</span>;
            case 'cancelled':
                return <span className="bg-[#FDEAEA] dark:bg-[#D93025]/15 text-[#D93025] dark:text-[#F87171] px-3 h-7 inline-flex items-center rounded-full text-xs font-semibold">Canceled</span>;
            case 'refunded':
                return <span className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 px-3 h-7 inline-flex items-center rounded-full text-xs font-semibold">Refunded</span>;
            case 'pending-refund':
                return <span className="bg-[#FFF3E6] dark:bg-[#FF9800]/15 text-[#FF9800] px-3 h-7 inline-flex items-center rounded-full text-xs font-semibold">Pending Refund</span>;
            case 'disputed':
                return <span className="bg-[#FFE6E6] dark:bg-[#CB0000]/15 text-[#CB0000] dark:text-[#F87171] px-3 h-7 inline-flex items-center rounded-full text-xs font-semibold">Disputed</span>;
            case 'failed':
            case 'payment_failed':
                return <span className="bg-[#FFF3E6] dark:bg-[#FF9800]/15 text-[#FF9800] px-3 h-7 inline-flex items-center rounded-full text-xs font-semibold">Payment Failed</span>;
            default:
                return <span className="bg-grey99 dark:bg-white/[0.06] text-grey40 dark:text-nightMuted px-3 h-7 inline-flex items-center rounded-full text-xs font-semibold">{status}</span>;
        }
    }

    // Helper function to check if order is eligible for refund
    function getEligibleOrdersForBulkEmail(): any[] {
        return orders

    }

    function isOrderEligibleForRefund(order: any): boolean {
        // Check if payment method is not cash
        if (order.paymentMethod === 'cash') {
            return false;
        }

        // Check if order status is completed
        if (order.status !== 'completed') {
            return false;
        }

        // Organization owners and admins can always refund, regardless of the event's refund policy
        if (isOrgAdmin) {
            return true;
        }

        // Check if event allows refunds
        if (!eventData?.refundable) {
            return false;
        }

        // Check if order is not already refunded
        if (order.status === 'refunded') {
            return false;
        }

        // Check if any tickets are checked in
        if (Array.isArray(order.tickets)) {
            const hasCheckedInTickets = order.tickets.some((ticket: any) => ticket.checkedInAt);
            if (hasCheckedInTickets) {
                return false;
            }
        }

        // Check if we're within the refund window
        if (eventData?.times && eventData.times.length > 0) {
            const eventStartTime = new Date(eventData.times[0].startDate);
            const now = new Date();

            // If refundDaysBeforeStart is 0, refunds are available until event starts
            if (eventData.refundDaysBeforeStart === 0) {
                if (now >= eventStartTime) {
                    return false;
                }
            } else {
                const refundDeadline = new Date(eventStartTime.getTime() - (eventData.refundDaysBeforeStart * 24 * 60 * 60 * 1000));
                if (now > refundDeadline) {
                    return false;
                }
            }
        }

        return true;
    }

    // Helper function to get refund policy information
    function getRefundPolicyInfo() {
        if (!eventData?.refundable) {
            return { message: 'Refunds are not allowed for this event', eligible: false };
        }

        if (eventData?.times && eventData.times.length > 0) {
            const eventStartTime = new Date(eventData.times[0].startDate);
            const now = new Date();
            const refundDeadline = new Date(eventStartTime.getTime() - (eventData.refundDaysBeforeStart * 24 * 60 * 60 * 1000));

            if (now > refundDeadline) {
                return {
                    message: `Refund deadline passed (${refundDeadline.toLocaleDateString()})`,
                    eligible: false
                };
            }

            return {
                message: `Refunds available until ${refundDeadline.toLocaleDateString()}`,
                eligible: true
            };
        }

        return {
            message: `Refunds available up to ${eventData.refundDaysBeforeStart} day${eventData.refundDaysBeforeStart !== 1 ? 's' : ''} before event`,
            eligible: true
        };
    }

    // Helper function to handle refund button click
    function handleRefundClick(order: any) {
        setSelectedOrderForRefund(order);
        setRefundModalOpen(true);
        setOpenActionMenuIdx(null); // Close the action menu
    }

    // Helper function to handle status change button click
    function handleStatusChangeClick(order: any) {
        setSelectedOrderForStatusChange(order);
        setNewStatus(order.status || '');
        setStatusChangeNotes('');
        setStatusChangeModalOpen(true);
        setOpenActionMenuIdx(null); // Close the action menu
    }

    // Helper function to handle status change submission
    function handleStatusChangeSubmit() {
        if (!selectedOrderForStatusChange || !newStatus) return;

        const statusData = {
            status: newStatus,
            notes: statusChangeNotes
        };

        updateOrderStatusMutation.mutate({
            orderId: selectedOrderForStatusChange._id,
            statusData
        });
    }

    // Info tab state
    const [info, setInfo] = useState({
        name: '',
        category: '',
        description: '',
        handle: '',
        banner: null as File | null,
        bannerPreview: '',
        socialImg: null as File | null,
        socialImgPreview: '',
        additionalImages: [] as File[],
        additionalImagePreviews: [] as string[],
        additionalSocialImages: [] as File[],
        additionalSocialImagePreviews: [] as string[],
        additionalBannerPhotos: [] as File[],
        additionalBannerPhotoPreviews: [] as { link: string; path?: string }[],
        youtubeVideoLink: '',
        times: [
            { startDate: null as any, startTime: null as any, endDate: null as any, endTime: null as any },
        ],
        address: '',
        venue: '',
        venueId: '', // Add venueId field for selected venue
        map: '',
        howto: '',
        city: '',
        country: '',
        state: '',
        zipcode: '',
        isPrivate: 0,
        showOnlyCity: 0,
        commentsDisabled: 0,
        ticketInfoMode: 0, // 0=disabled, 1=optional, 2=required
        liabilityWaiverEnabled: false,
        liabilityWaiverText: '',
    });


    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [toast, setToast] = useState('');
    const [handleValidation, setHandleValidation] = useState({
        isValidating: false,
        isAvailable: null as boolean | null,
        message: ''
    });
    // Tickets tab state
    const [tickets, setTickets] = useState([
        {
            _id: '', // Added _id for existing tickets
            name: '',
            qty: '',
            price: '',
            type: 'Paid',
            status: true,
            desc: '',
            start: null,
            startTime: null,
            end: null,
            endTime: null,
            promocodes: [] as any[], // Add promocodes array to ticket state
            memberPricingDiscountType: undefined as 'fixed' | 'percentage' | undefined,
            memberPricingDiscount: '',
            allowPromoCodeWithMemberPricing: false,
            tierPrices: [] as any[],
            tierIds: [] as string[],
        },
        {
            _id: '', // Added _id for existing tickets
            name: '',
            qty: '',
            price: '',
            type: 'Free',
            status: false,
            desc: '',
            start: null,
            startTime: null,
            end: null,
            endTime: null,
            promocodes: [] as any[], // Add promocodes array to ticket state
            memberPricingDiscountType: undefined as 'fixed' | 'percentage' | undefined,
            memberPricingDiscount: '',
            allowPromoCodeWithMemberPricing: false,
            tierPrices: [] as any[],
            tierIds: [] as string[],
        },
    ]);
    const [ticketLoading, setTicketLoading] = useState(false);
    const [ticketError, setTicketError] = useState('');
    const [refundPolicy, setRefundPolicy] = useState('1');
    const [refundable, setRefundable] = useState(true);
    const [refundDaysBeforeStart, setRefundDaysBeforeStart] = useState(1);
    const [feeMode, setFeeMode] = useState<'added' | 'included'>('included');
    const [taxMode, setTaxMode] = useState<'added' | 'included'>('included');
    const feeFormula = describeFeeFormula(eventData?.taxInfo?.resolvedFee, cur);
    const [currency, setCurrency] = useState<EventCurrency>('usd');
    const [openSettingsIdx, setOpenSettingsIdx] = useState<number | null>(null);
    const [mobileTicketMenuIdx, setMobileTicketMenuIdx] = useState<number | null>(null);
    const [ticketStatusFilter, setTicketStatusFilter] = useState<'active' | 'disabled'>('active');
    const [ticketLoadingIdx, setTicketLoadingIdx] = useState<number | null>(null);
    const [ticketLoadingIndices, setTicketLoadingIndices] = useState<Set<number>>(new Set());
    const [ticketErrorIdx, setTicketErrorIdx] = useState<{ [idx: number]: string }>({});


    // Add Google Maps state for address picker
    const [lat, setLat] = useState(34.0522);
    const [lng, setLng] = useState(-118.2437);
    const [autocomplete, setAutocomplete] = useState<any>(null);
    const [map, setMap] = useState<any>(null);
    const mapContainerStyle = { width: '100%', height: '220px', borderRadius: '16px' };
    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: 'AIzaSyBKbmD1FAPDpYvIFHjUJpeZ9XbohnH_QFI',
        libraries: libraries,
    });
    const [addressLoading, setAddressLoading] = useState(false);
    const [lastFetchedCoords, setLastFetchedCoords] = useState<{ lat: number; lng: number } | null>(null);
    const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);

    // Helper function to extract complete address object from Google address components
    function extractAddressFromComponents(addressComponents: any[]): { country: string; state: string; city: string; zipcode: string; fullAddress: string } {
        if (!addressComponents || !Array.isArray(addressComponents)) {
            return { country: '', state: '', city: '', zipcode: '', fullAddress: '' };
        }

        const address: { country: string; state: string; city: string; zipcode: string; fullAddress: string } = {
            country: '',
            state: '',
            city: '',
            zipcode: '',
            fullAddress: ''
        };

        // Extract country
        const country = addressComponents.find(component =>
            component.types.includes('country')
        );
        if (country) address.country = country.long_name;

        // Extract state/province
        const state = addressComponents.find(component =>
            component.types.includes('administrative_area_level_1')
        );
        if (state) address.state = state.long_name;

        // Extract city
        const city = addressComponents.find(component =>
            component.types.includes('locality')
        );
        if (city) address.city = city.long_name;

        // Extract postal code
        const zipcode = addressComponents.find(component =>
            component.types.includes('postal_code')
        );
        if (zipcode) address.zipcode = zipcode.long_name;

        return address;
    }

    function onLoadAutocomplete(ac: any) {
        setAutocomplete(ac);
    }
    function onPlaceChanged() {
        if (autocomplete) {
            const place = autocomplete.getPlace();
            if (place.geometry && place.geometry.location) {
                const newLat = place.geometry.location.lat();
                const newLng = place.geometry.location.lng();
                setLat(newLat);
                setLng(newLng);

                // Extract complete address from address components
                const addressData = extractAddressFromComponents(place.address_components);

                setInfo(w => ({
                    ...w,
                    address: place.formatted_address || '',
                    city: addressData.city,
                    state: addressData.state,
                    country: addressData.country,
                    zipcode: addressData.zipcode
                }));

                if (map) {
                    map.panTo({ lat: newLat, lng: newLng });
                }
            }
        }
    }
    function onMapLoad(mapInstance: any) {
        setMap(mapInstance);
    }
    async function fetchAddressFromCoords(lat: number, lng: number) {
        setAddressLoading(true);
        fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=AIzaSyBTl2hQB_YKIYPeWLD2qJ0G08yTo7SErP8`
        )
            .then(response => response.json())
            .then(data => {
                if (data.results && data.results[0]) {
                    const result = data.results[0];
                    // Extract complete address from address components
                    const addressData = extractAddressFromComponents(result.address_components);

                    setInfo(w => ({
                        ...w,
                        address: result.formatted_address,
                        city: addressData.city,
                        state: addressData.state,
                        country: addressData.country,
                        zipcode: addressData.zipcode
                    }));
                }
            })
            .catch(() => {
                // Optionally handle error
            })
            .finally(() => {
                setAddressLoading(false);
            });
    }
    function handleMapIdle() {
        if (map) {
            const center = map.getCenter();
            const newLat = center.lat();
            const newLng = center.lng();
            if (!lastFetchedCoords || Math.abs(newLat - lastFetchedCoords.lat) > 0.00001 || Math.abs(newLng - lastFetchedCoords.lng) > 0.00001) {
                setLastFetchedCoords({ lat: newLat, lng: newLng });
                if (debounceTimer) clearTimeout(debounceTimer);
                const timer = setTimeout(() => {
                    fetchAddressFromCoords(newLat, newLng);
                }, 400);
                setDebounceTimer(timer);
            }
            setLat(newLat);
            setLng(newLng);
        }
    }

    // Placeholder mutation for API integration
    const ticketMutation = useMutation({
        mutationFn: async (ticket: any) => {
            // Replace with your API call
            await new Promise(res => setTimeout(res, 1000));
            return ticket;
        },
        onError: (error, variables, context: any) => {
            setTicketErrorIdx(e => ({ ...e, [context?.idx]: 'Failed to save ticket.' }));
            setTicketLoadingIdx(null);
        },
        onSuccess: (data, variables, context: any) => {
            setTicketLoadingIdx(null);
            setTicketErrorIdx(e => ({ ...e, [context?.idx]: '' }));
        },
    });



    // Handlers
    function handleInfoChange(field: string, value: any) {
        setInfo((prev) => ({ ...prev, [field]: value }));

        // Trigger handle validation when handle field changes
        if (field === 'handle') {
            debouncedValidateHandle(value);
        }
    }

    const applyVenueSelection = (next: { name: string; venueId: string | null; venue: any }) => {
        const v = next.venue;
        if (!v) {
            setInfo(prev => ({ ...prev, venue: next.name, venueId: next.venueId ?? '' }));
            return;
        }
        setInfo(prev => ({
            ...prev,
            venue: v.name,
            venueId: v._id,
            ...(v.address?.fullAddress ? {
                address: v.address.fullAddress,
                city: v.address.city || '',
                state: v.address.state || '',
                country: v.address.country || '',
                zipcode: v.address.zipcode || '',
            } : {}),
        }));
        const vLat = parseFloat(v.lat ?? v.coordinates?.[1]);
        const vLng = parseFloat(v.lng ?? v.coordinates?.[0]);
        if (!isNaN(vLat) && !isNaN(vLng)) {
            setLat(vLat);
            setLng(vLng);
            map?.panTo({ lat: vLat, lng: vLng });
        }
    };
    function handleTimeChange(idx: number, field: string, value: any) {
        // If the field is a time field and value is a string, set to null
        const isTimeField = field === 'startTime' || field === 'endTime';
        setInfo((prev) => {
            const updatedTimes = prev.times.map((t, i) => {
                if (i !== idx) return t;
                const next: any = { ...t, [field]: isTimeField && typeof value === 'string' ? null : value };
                if (field === 'startDate' && value && next.endDate && next.endDate.compare(value) < 0) {
                    next.endDate = value;
                }
                return next;
            });
            return { ...prev, times: updatedTimes };
        });
    }
    function addTime() {
        setInfo((prev) => ({ ...prev, times: [...prev.times, { startDate: null, startTime: null, endDate: null, endTime: null }] }));
    }
    function removeTime(idx: number) {
        setInfo((prev) => ({ ...prev, times: prev.times.filter((_, i) => i !== idx) }));
    }
    // Add validation for Info tab
    function validateInfo() {
        const errors: string[] = [];
        if (!info.name) errors.push('Event name is required.');
        if (!info.category) errors.push('Category is required.');
        if (!info.description) errors.push('Description is required.');
        if (!info.banner && !info.bannerPreview) errors.push('Banner image is required.');
        if (!info.times[0].startDate || !info.times[0].endDate) errors.push('Event date/time is required.');
        // Validate each time range: end must be after start when on same day
        info.times.forEach((t, i) => {
            if (t.startDate && t.endDate) {
                const valid = isEndAfterStart(t.startDate, t.startTime, t.endDate, t.endTime);
                if (!valid) errors.push(`Time ${i + 1}: End time must be after start time.`);
            }
        });
        if (!info.address) errors.push('Address is required.');
        if (!info.venue) errors.push('Venue is required.');
        if (refundable && (refundDaysBeforeStart < 0 || refundDaysBeforeStart.toString() === '')) errors.push('Days before event start is required.');
        if (info.liabilityWaiverEnabled && !info.liabilityWaiverText.trim()) errors.push('Liability waiver text is required when enabled.');

        // Validate handle if provided
        if (info.handle) {
            if (info.handle.length < 3) {
                errors.push('Handle must be at least 3 characters long.');
            } else if (!/^[a-zA-Z0-9_-]+$/.test(info.handle)) {
                errors.push('Handle can only contain letters, numbers, hyphens, and underscores.');
            } else if (handleValidation.isAvailable === false) {
                errors.push('Handle is already taken. Please choose a different one.');
            } else if (handleValidation.isValidating) {
                errors.push('Please wait while we check handle availability.');
            }
        }

        return errors;
    }
    const infoMutation = useMutation({
        mutationFn: (infoPayload: any) => updateEvent(effectiveEventId, infoPayload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['organizerEvent', effectiveEventId] });
            queryClient.invalidateQueries({ queryKey: ['event-venues', effectiveEventId] });
        },
    });

    // Separate mutation for saving refund policy and fee settings without full validation
    const refundPolicyMutation = useMutation({
        mutationFn: (payload: any) => updateEvent(effectiveEventId, payload),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['organizerEvent', effectiveEventId] }),
    });
    async function handleSave() {

        const errors = validateInfo();
        if (errors.length > 0) {
            setError(errors.join(' '));
            setLoading(false);
            return;
        }
        setLoading(true); setError('');
        try {
            // Create complete address object
            const addressObject = {
                fullAddress: info.address,
                country: info.country,
                state: info.state,
                city: info.city,
                zipcode: info.zipcode
            };

            // Map info fields to backend contract
            const infoPayload: any = {
                title: info.name,
                description: info.description,
                category: info.category,
                address: JSON.stringify(addressObject),
                venue: info.venue,
                venueId: info.venueId ? info.venueId : null,
                howto: info.howto,
                lat: lat.toString(),
                lng: lng.toString(),
                times: JSON.stringify(info.times.map((t: any) => ({
                    startDate: t.startDate && t.startTime
                        ? `${formatDateObjToString(t.startDate)} ${formatTimeObjToString(t.startTime)}`.trim()
                        : '',
                    endDate: t.endDate && t.endTime
                        ? `${formatDateObjToString(t.endDate)} ${formatTimeObjToString(t.endTime)}`.trim()
                        : '',
                }))),
                refundable: refundable,
                refundDaysBeforeStart: +refundDaysBeforeStart,
                feeMode,
                taxMode,
                currency,
                isPrivate: info.isPrivate,
                showOnlyCity: info.showOnlyCity,
                commentsDisabled: info.commentsDisabled,
                ticketInfoMode: info.ticketInfoMode,
                youtubeVideoLink: info.youtubeVideoLink,
                liabilityWaiverEnabled: info.liabilityWaiverEnabled,
                liabilityWaiverText: info.liabilityWaiverEnabled ? info.liabilityWaiverText : null,
            };
            if (info.handle != '') infoPayload.handle = info.handle;
            await infoMutation.mutateAsync(infoPayload);
            addToast({
                title: 'Success!',
                description: 'Event info saved successfully',
                color: 'success',
            });
            setLoading(false);
        } catch (e: any) {
            setError(e.message || 'Failed to save.');
            setLoading(false);
        }
    }

    async function handleSaveRefundPolicy() {
        setLoading(true); setError('');
        try {
            const payload: any = {
                refundable,
                refundDaysBeforeStart,
                feeMode,
                taxMode,
                currency,
            };
            // Also include fee settings (previous behavior)
            await refundPolicyMutation.mutateAsync(payload);
            setLoading(false);
        } catch (e: any) {
            setError(e.message || 'Failed to save refund policy.');
            setLoading(false);
        }
    }
    const [bannerUploading, setBannerUploading] = useState(false);
    const [bannerUploadError, setBannerUploadError] = useState('');
    const [socialImgUploading, setSocialImgUploading] = useState(false);
    const [socialImgUploadError, setSocialImgUploadError] = useState('');
    const [bannerCacheBust, setBannerCacheBust] = useState(() => Date.now());
    const [socialImgCacheBust, setSocialImgCacheBust] = useState(() => Date.now());
    async function handleBannerUpload(e: React.ChangeEvent<HTMLInputElement>) {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (file) {
            if (!(await hasAspectRatio(file))) {
                setBannerUploadError(BANNER_RATIO_ERROR);
                addToast({ title: 'Invalid image', description: BANNER_RATIO_ERROR, color: 'danger' });
                return;
            }
            setBannerUploading(true);
            setBannerUploadError('');
            setInfo((prev) => ({ ...prev, banner: file, bannerPreview: URL.createObjectURL(file) }));
            try {
                const formData = new FormData();
                formData.append('files', file);
                await uploadBanner(effectiveEventId, formData);
                setBannerCacheBust(Date.now());
                setInfo(prev => ({ ...prev, bannerPreview: '' }));
                queryClient.invalidateQueries({ queryKey: ['organizerEvent', effectiveEventId] });
            } catch (err: any) {
                setBannerUploadError(err?.message || 'Failed to upload banner.');
            } finally {
                setBannerUploading(false);
            }
        }
    }
    async function handleSocialImgUpload(e: React.ChangeEvent<HTMLInputElement>) {
        const file = e.target.files?.[0];
        if (file) {
            setSocialImgUploading(true);
            setSocialImgUploadError('');
            setInfo((prev) => ({ ...prev, socialImg: file, socialImgPreview: URL.createObjectURL(file) }));
            try {
                const formData = new FormData();
                formData.append('files', file);
                await uploadSocialImage(effectiveEventId, formData);
                setSocialImgCacheBust(Date.now());
                setInfo(prev => ({ ...prev, socialImgPreview: '' }));
                queryClient.invalidateQueries({ queryKey: ['organizerEvent', effectiveEventId] });
            } catch (err: any) {
                setSocialImgUploadError(err?.message || 'Failed to upload image.');
            } finally {
                setSocialImgUploading(false);
            }
        }
    }

    function handleAdditionalImagesUpload(e: React.ChangeEvent<HTMLInputElement>) {
        const files = Array.from(e.target.files || []);
        if (files.length > 0) {
            setInfo((prev) => ({
                ...prev,
                additionalImages: [...prev.additionalImages, ...files],
                additionalImagePreviews: [
                    ...prev.additionalImagePreviews,
                    ...files.map((file) => URL.createObjectURL(file)),
                ],
            }));
        }
    }
    function handleRemoveAdditionalImage(idx: number) {
        setInfo((prev) => ({
            ...prev,
            additionalImages: prev.additionalImages.filter((_, i) => i !== idx),
            additionalImagePreviews: prev.additionalImagePreviews.filter((_, i) => i !== idx),
        }));
    }

    function handleAdditionalSocialImagesUpload(e: React.ChangeEvent<HTMLInputElement>) {
        const files = Array.from(e.target.files || []);
        if (files.length > 0) {
            setInfo((prev) => ({
                ...prev,
                additionalSocialImages: [...prev.additionalSocialImages, ...files],
                additionalSocialImagePreviews: [
                    ...prev.additionalSocialImagePreviews,
                    ...files.map((file) => URL.createObjectURL(file)),
                ],
            }));
        }
    }
    function handleRemoveAdditionalSocialImage(idx: number) {
        setInfo((prev) => ({
            ...prev,
            additionalSocialImages: prev.additionalSocialImages.filter((_, i) => i !== idx),
            additionalSocialImagePreviews: prev.additionalSocialImagePreviews.filter((_, i) => i !== idx),
        }));
    }

    const queryClient = useQueryClient();
    const [additionalBannerUploading, setAdditionalBannerUploading] = useState(false);
    async function handleAdditionalBannerPhotosUpload(e: React.ChangeEvent<HTMLInputElement>) {
        let files = Array.from(e.target.files || []);
        e.target.value = '';
        const ratioChecks = await Promise.all(files.map(f => hasAspectRatio(f)));
        if (ratioChecks.some(ok => !ok)) {
            addToast({ title: 'Invalid image', description: BANNER_RATIO_ERROR, color: 'danger' });
            files = files.filter((_, i) => ratioChecks[i]);
        }
        if (files.length > 0) {
            setAdditionalBannerUploading(true);
            try {
                const toAdd: { link: string; path?: string }[] = [];
                for (const file of files) {
                    const formData = new FormData();
                    formData.append('files', file);
                    const res = await uploadAdditionalBanners(effectiveEventId, formData);
                    const list = Array.isArray((res as any)?.data?.files) ? (res as any).data.files : [];
                    const last = list[list.length - 1];
                    toAdd.push({ link: URL.createObjectURL(file), path: last?.path });
                }
                setInfo((prev) => ({
                    ...prev,
                    additionalBannerPhotos: [...prev.additionalBannerPhotos, ...files],
                    additionalBannerPhotoPreviews: [...prev.additionalBannerPhotoPreviews, ...toAdd],
                }));
                queryClient.invalidateQueries({ queryKey: ['organizerEvent', effectiveEventId] });
            } catch (err: any) {
                console.error('Failed to upload additional banner photos:', err);
            } finally {
                setAdditionalBannerUploading(false);
            }
        }
    }

    async function handleRemoveAdditionalBannerPhoto(idx: number) {
        if (!effectiveEventId) return;
        const item = info.additionalBannerPhotoPreviews[idx];
        if (!item) return;
        try {
            await deleteEventAttachments(effectiveEventId, 'additionalBanners', item.path, item.path ? undefined : idx);
            setInfo((prev) => {
                const start = prev.additionalBannerPhotoPreviews.length - prev.additionalBannerPhotos.length;
                const newPreviews = prev.additionalBannerPhotoPreviews.filter((_, i) => i !== idx);
                const newPhotos = idx >= start
                    ? prev.additionalBannerPhotos.filter((_, i) => i !== idx - start)
                    : prev.additionalBannerPhotos;
                return { ...prev, additionalBannerPhotoPreviews: newPreviews, additionalBannerPhotos: newPhotos };
            });
            queryClient.invalidateQueries({ queryKey: ['organizerEvent', effectiveEventId] });
        } catch (err: any) {
            addToast({ title: 'Could not remove photo', description: err?.message || 'Please try again.', color: 'danger' });
        }
    }

    const refreshTicketData = () => Promise.all([
        queryClient.invalidateQueries({ queryKey: ['organizerEvent', effectiveEventId] }),
        queryClient.invalidateQueries({ queryKey: ['tierSummary'] }),
        queryClient.invalidateQueries({ queryKey: ['tierSummaryDrawer'] }),
    ]);

    // Ticket mutations
    const createTicketMutation = useMutation({
        mutationFn: (ticket: any) => createTicket(effectiveEventId, ticket),
        onSuccess: () => refreshTicketData(),
    });
    const updateTicketMutation = useMutation({
        mutationFn: ({ id, ticket }: { id: string, ticket: any }) => updateTicket(id, ticket),
        onSuccess: () => refreshTicketData(),
    });
    const deleteTicketMutation = useMutation({
        mutationFn: (id: string) => deleteTicket(id),
        onSuccess: () => refreshTicketData(),
    });

    function tierPayload(tk: any) {
        if (!tierMode) return {};
        const tierIds: string[] = tk.tierIds || [];
        const overrides = nonEmptyOverrides(tk.tierPrices || [], tierIds);
        const seats = tierIds.reduce((sum, name) => sum + (tierList.find(t => t.name === name)?.seatCount ?? 0), 0);
        return { tierIds, tierPrices: overrides, price: minEffectivePrice(tierList, tierIds, overrides), quantity: seats };
    }

    function tierDraftError(tk: any): string {
        if (!tierMode) return '';
        const v = validateTierSelection(tierList, tk.tierIds || [], tk.tierPrices || []);
        return v.valid ? '' : (v.formError || 'Set a price for each selected tier');
    }

    // Add ticket (local only until user saves)
    function addTicket() {
        const type = 'Free';
        // Get current date and time for ticket start
        const now = today(getLocalTimeZone());
        const currentTime = parseTime(new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' }));

        // Get event's start and end times from the first time slot
        const eventTimes = eventData?.times || [];
        const firstTimeSlot = eventTimes[0];

        let eventStartDate = now;
        let eventStartTime = currentTime;
        let eventEndDate = now;
        let eventEndTime = currentTime;

        if (firstTimeSlot) {
            // Parse event start date and time
            if (firstTimeSlot.startDate) {
                // Handle date strings that might include time (e.g., "2025-10-18 11:11")
                const dateOnly = firstTimeSlot.startDate.split(' ')[0];
                eventStartDate = parseDate(dateOnly);
            }
            if (firstTimeSlot.startTime) {
                eventStartTime = parseTime(firstTimeSlot.startTime);
            }

            // Parse event end date and time
            if (firstTimeSlot.endDate) {
                // Handle date strings that might include time (e.g., "2025-10-18 11:11")
                const dateOnly = firstTimeSlot.endDate.split(' ')[0];
                eventEndDate = parseDate(dateOnly);
            }
            if (firstTimeSlot.endTime) {
                eventEndTime = parseTime(firstTimeSlot.endTime);
            }
        }

        setTickets(prev => ([
            ...prev,
            {
                _id: '',
                name: '',
                qty: '1',
                price: '',
                type,
                status: true,
                desc: '',
                start: tierMode ? null : now, // Start date: today
                startTime: tierMode ? null : currentTime, // Start time: current time
                end: tierMode ? null : eventStartDate, // End date: event's start date
                endTime: tierMode ? null : eventStartTime, // End time: event's start time
                promocodes: [], // Initialize promocodes array
                memberPricingDiscountType: undefined,
                memberPricingDiscount: '',
                allowPromoCodeWithMemberPricing: false,
                tierPrices: [] as any[],
                tierIds: [] as string[],
            }
        ]));
        setOpenSettingsIdx(tickets.length);
    }

    // Update ticket (only updates local state, doesn't call API)
    function handleTicketChange(idx: number, field: string, value: any) {
        if (['start', 'startTime', 'end', 'endTime'].includes(field)) {
            setTicketErrorIdx(e => (e[idx] ? { ...e, [idx]: '' } : e));
        }
        setTickets(tks => {
            const updated = tks.map((tk, i) => {
                if (i !== idx) return tk;
                const next: any = { ...tk, [field]: value };
                if (field === 'start' && value && next.end && typeof next.end.compare === 'function' && next.end.compare(value) < 0) {
                    next.end = value;
                }
                if (field === 'price') {
                    next.type = String(value).trim() === '' ? 'Free' : 'Paid';
                }
                return next;
            });
            return updated;
        });
    }

    // Immediately persists a ticket's promocodes/member pricing to the DB, without requiring
    // the ticket's own "Save" button — used right after Save is clicked inside the Promocode
    // or Member Pricing modal. Only applies to already-created tickets (real _id); a brand-new,
    // not-yet-saved ticket still saves everything together via its own Save button.
    function persistTicketDiscounts(ticket: any) {
        if (!ticket?._id) return;
        updateTicketMutation.mutate(
            {
                id: ticket._id,
                ticket: {
                    promocodes: JSON.stringify(ticket.promocodes || []),
                    memberPricingDiscountType: ticket.memberPricingDiscountType,
                    memberPricingDiscount: ticket.memberPricingDiscount,
                    allowPromoCodeWithMemberPricing: ticket.allowPromoCodeWithMemberPricing === true,
                },
            },
            {
                onSuccess: () => {
                    setToast('Saved');
                    setTimeout(() => setToast(''), 2000);
                },
                onError: () => {
                    setToast('Failed to save. Please try again.');
                    setTimeout(() => setToast(''), 3000);
                },
            }
        );
    }

    // Save or create individual ticket (calls API)
    // Promocode handlers
    const handleAddPromocode = (ticketId: string) => {
        if (!ticketId) return; // Safety check
        setEditingPromocode(null);
        setPromocodeModalTicketId(ticketId);
        onPromocodeModalOpen();
    };

    const handleEditPromocode = (ticketId: string, promocodeId: string) => {
        if (!ticketId) return; // Safety check
        setEditingPromocode(promocodeId);
        setPromocodeModalTicketId(ticketId);
        onPromocodeModalOpen();
    };

    const handleRemovePromocode = (ticketId: string, promocodeId: string) => {
        if (!ticketId) return; // Safety check
        setTickets(prevTickets => prevTickets.map((ticket) => {
            if (ticket._id === ticketId) {
                return {
                    ...ticket,
                    promocodes: (ticket.promocodes || []).filter((p: any) => p._id !== promocodeId)
                };
            }
            return ticket;
        }));
    };

    const handleSavePromocode = (promocodeData: any) => {
        if (promocodeModalTicketId === null) return;

        let updatedTicketForPersist: any = null;

        setTickets(prevTickets => prevTickets.map((ticket) => {
            if (ticket._id === promocodeModalTicketId) {
                const promocodes = ticket.promocodes || [];
                const updatedPromocodeData = {
                    ...promocodeData,
                    ticketId: ticket._id,
                    ticketName: ticket.name
                };

                const newPromocodes = editingPromocode
                    ? promocodes.map((p: any) => p._id === editingPromocode ? updatedPromocodeData : p)
                    : [...promocodes, updatedPromocodeData];

                const updatedTicket = { ...ticket, promocodes: newPromocodes };
                updatedTicketForPersist = updatedTicket;
                return updatedTicket;
            }
            return ticket;
        }));

        onPromocodeModalOpenChange();
        setEditingPromocode(null);
        setPromocodeModalTicketId(null);

        if (updatedTicketForPersist) {
            persistTicketDiscounts(updatedTicketForPersist);
        }
    };


    function handleSaveTicket(idx: number) {

        // Find all unsaved tickets (tickets without _id)
        const unsavedTickets = tickets.filter(tk => !tk._id);

        if (unsavedTickets.length === 0) {
            // If no unsaved tickets, just save the current one
            saveSingleTicket(idx);
            return;
        }

        // Get all indices of unsaved tickets for loading state tracking
        const unsavedIndices = unsavedTickets.map(tk => tickets.findIndex(t => t === tk));

        // Set loading state for all unsaved tickets
        setTicketLoadingIndices(new Set(unsavedIndices));

        // Save all unsaved tickets
        let completedSaves = 0;
        let hasErrors = false;
        const totalUnsaved = unsavedTickets.length;

        unsavedTickets.forEach((tk) => {

            const originalIdx = tickets.findIndex(t => t === tk);

            // Validate each unsaved ticket
            if (!tierMode && (!tk.start || !tk.startTime || !tk.end || !tk.endTime)) {
                setTicketErrorIdx(e => ({ ...e, [originalIdx]: 'Please set sale start and end date and time.' }));
                hasErrors = true;
                completedSaves++;
                // Clear loading for this specific ticket
                setTicketLoadingIndices(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(originalIdx);
                    return newSet;
                });
                return;
            }

            // Validate ticket time range if dates are present
            if (tk.start && tk.end) {
                const valid = isEndAfterStart(tk.start, tk.startTime, tk.end, tk.endTime);
                if (!valid) {
                    setTicketErrorIdx(e => ({ ...e, [originalIdx]: 'Sale end must be after sale start.' }));
                    hasErrors = true;
                    completedSaves++;
                    // Clear loading for this specific ticket
                    setTicketLoadingIndices(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(originalIdx);
                        return newSet;
                    });
                    return;
                }
            }

            // Prepare payload for this ticket
            let saleStart = '';
            if (tk.start && tk.startTime) {
                const startDate = (tk.start as any).toString();
                const startTime = (tk.startTime as any).toString();
                const startTimeWithoutSeconds = startTime.split(':').slice(0, 2).join(':');
                saleStart = `${startDate} ${startTimeWithoutSeconds}`;
            } else if (tk.start) {
                saleStart = `${(tk.start as any).toString()} 00:00`;
            }

            let saleEnd = '';
            if (tk.end && tk.endTime) {
                const endDate = (tk.end as any).toString();
                const endTime = (tk.endTime as any).toString();
                const endTimeWithoutSeconds = endTime.split(':').slice(0, 2).join(':');
                saleEnd = `${endDate} ${endTimeWithoutSeconds}`;
            } else if (tk.end) {
                saleEnd = `${(tk.end as any).toString()} 23:59`;
            }

            const payload: any = {
                name: tk.name,
                quantity: Number(tk.qty),
                //remained: Number(tk.qty),
                price: Number(tk.price),
                type: tk.type,
                status: tk.status,
                description: tk.desc,
                promocodes: tk.promocodes || [], // Include promocodes in the payload
                memberPricingDiscountType: tk.memberPricingDiscountType,
                memberPricingDiscount: tk.memberPricingDiscount,
                allowPromoCodeWithMemberPricing: tk.allowPromoCodeWithMemberPricing === true,
                ...tierPayload(tk),
            };
            console.log('payload.promocodes', payload.promocodes);
            if (saleStart || tierMode) payload.saleStart = saleStart;
            if (saleEnd || tierMode) payload.saleEnd = saleEnd;

            // Clear error for this ticket
            setTicketErrorIdx(e => ({ ...e, [originalIdx]: '' }));

            // Create the ticket
            createTicketMutation.mutate(payload, {
                onSuccess: () => {
                    completedSaves++;
                    setTicketErrorIdx(e => ({ ...e, [originalIdx]: '' }));

                    // Clear loading for this specific ticket
                    setTicketLoadingIndices(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(originalIdx);
                        return newSet;
                    });

                    if (completedSaves === totalUnsaved) {
                        setToast(`All ${totalUnsaved} tickets saved successfully`);
                        setTimeout(() => setToast(''), 3000);
                        refetchTickets();
                        setOpenSettingsIdx(null);
                    }
                },
                onError: () => {
                    completedSaves++;
                    hasErrors = true;
                    setTicketErrorIdx(e => ({ ...e, [originalIdx]: 'Failed to create ticket.' }));

                    // Clear loading for this specific ticket
                    setTicketLoadingIndices(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(originalIdx);
                        return newSet;
                    });

                    if (completedSaves === totalUnsaved) {
                        if (hasErrors) {
                            setToast('Some tickets failed to save. Please check errors.');
                            setTimeout(() => setToast(''), 3000);
                        }
                    }
                }
            });
        });
    }

    // Helper function to save a single ticket (for existing tickets)
    function saveSingleTicket(idx: number) {
        const tk = tickets[idx];
        console.log(tk);

        const tierErr = tierDraftError(tk);
        if (tierErr) {
            setTicketErrorIdx(e => ({ ...e, [idx]: tierErr }));
            return;
        }
        setTicketLoadingIdx(idx);
        setTicketErrorIdx(e => ({ ...e, [idx]: '' }));

        // Require both sale start and end date/time before saving
        if (!tierMode && (!tk.start || !tk.startTime || !tk.end || !tk.endTime)) {
            setTicketLoadingIdx(null);
            setOpenSettingsIdx(idx);
            setTicketErrorIdx(e => ({ ...e, [idx]: 'Please set sale start and end date and time.' }));
            return;
        }

        // Validate ticket time range if dates are present
        if (tk.start && tk.end && tk.startTime && tk.endTime) {
            const valid = isEndAfterStart(tk.start, tk.startTime, tk.end, tk.endTime);
            if (!valid) {
                setTicketLoadingIdx(null);
                setTicketErrorIdx(e => ({ ...e, [idx]: 'Sale end must be after sale start.' }));
                return;
            }
        }

        // Prepare payload (convert fields as needed)
        let saleStart = '';
        if (tk.start && tk.startTime) {
            const startDate = (tk.start as any).toString();
            const startTime = (tk.startTime as any).toString();
            // here remove seconds from startTime
            const startTimeWithoutSeconds = startTime.split(':').slice(0, 2).join(':');
            saleStart = `${startDate} ${startTimeWithoutSeconds}`;
        } else if (tk.start) {
            saleStart = `${(tk.start as any).toString()} 00:00`;
        }

        let saleEnd = '';
        if (tk.end && tk.endTime) {
            const endDate = (tk.end as any).toString();
            const endTime = (tk.endTime as any).toString();
            // here remove seconds from endTime
            const endTimeWithoutSeconds = endTime.split(':').slice(0, 2).join(':');
            saleEnd = `${endDate} ${endTimeWithoutSeconds}`;
        } else if (tk.end) {
            saleEnd = `${(tk.end as any).toString()} 23:59`;
        }

        const payload: any = {
            name: tk.name,
            quantity: Number(tk.qty),
            //remained: Number(tk.qty),
            price: Number(tk.price),
            type: tk.type,
            status: tk.status,
            description: tk.desc,
            promocodes: JSON.stringify(tk.promocodes || []), // Include promocodes in the payload
            memberPricingDiscountType: tk.memberPricingDiscountType,
            memberPricingDiscount: tk.memberPricingDiscount,
            allowPromoCodeWithMemberPricing: tk.allowPromoCodeWithMemberPricing === true,
            ...tierPayload(tk),
        };
        console.log('payload.promocodes', payload.promocodes);
        if (saleStart || tierMode) payload.saleStart = saleStart;
        if (saleEnd || tierMode) payload.saleEnd = saleEnd;

        if (!tk._id) {
            // Create new ticket
            createTicketMutation.mutate(payload, {
                onSuccess: () => {
                    setTicketLoadingIdx(null);
                    setTicketErrorIdx(e => ({ ...e, [idx]: '' }));
                    setToast('Ticket created');
                    setTimeout(() => setToast(''), 2000);
                    // Close settings; data will refetch
                    refetchTickets()
                    setOpenSettingsIdx(null);
                },
                onError: () => {
                    setTicketLoadingIdx(null);
                    setTicketErrorIdx(e => ({ ...e, [idx]: 'Failed to create ticket.' }));
                }
            });
        } else {
            // Update existing ticket
            updateTicketMutation.mutate(
                { id: tk._id, ticket: payload },
                {
                    onSuccess: () => {
                        setTicketLoadingIdx(null);
                        setTicketErrorIdx(e => ({ ...e, [idx]: '' }));
                        setToast('Ticket saved');
                        refetchTickets()
                        setTimeout(() => setToast(''), 2000);
                        setOpenSettingsIdx(null);
                    },
                    onError: () => {
                        setTicketLoadingIdx(null);
                        setTicketErrorIdx(e => ({ ...e, [idx]: 'Failed to save ticket.' }));
                    }
                }
            );
        }
    }

    // Show ticket deletion confirmation
    function removeTicket(idx: number) {
        if (deleteTicketMutation.isPending || isDeletingTicket) return;
        setTicketToDelete(idx);
        setTicketDeleteConfirmModalOpen(true);
    }

    // Confirm ticket deletion (calls API)
    function confirmRemoveTicket() {
        if (ticketToDelete === null || deleteTicketMutation.isPending || isDeletingTicket) return;

        setIsDeletingTicket(true);
        const ticketToDeleteIdx = ticketToDelete;
        const tk = tickets[ticketToDeleteIdx];

        const removeFromList = () => setTickets(tks => (tks.length > 1 ? tks.filter((_, i) => i !== ticketToDeleteIdx) : tks));

        setTicketDeleteConfirmModalOpen(false);
        setTicketToDelete(null);

        if (!tk._id) {
            removeFromList();
            setIsDeletingTicket(false);
            return;
        }

        deleteTicketMutation.mutate(tk._id, {
            onSuccess: () => {
                setIsDeletingTicket(false);
                removeFromList();
                refetchTickets();
            },
            onError: (err: any) => {
                setIsDeletingTicket(false);
                const message = err?.response?.data?.message || 'Failed to delete ticket.';
                addToast({ title: 'Could not delete ticket', description: Array.isArray(message) ? message.join(', ') : message, color: 'danger' });
            }
        });
    }
    function duplicateTicket(idx: number) {
        setTickets(tks => [
            ...tks.slice(0, idx + 1),
            { ...tks[idx], name: tks[idx].name + ' Copy', _id: '' },
            ...tks.slice(idx + 1),
        ]);
        setOpenSettingsIdx(idx + 1);
    }

    // Close the add/edit ticket modal, discarding the ticket if it was never saved
    function closeTicketModal() {
        const idx = openSettingsIdx;
        if (idx !== null && !tickets[idx]?._id) {
            setTickets(tks => tks.filter((_, i) => i !== idx));
        }
        setOpenSettingsIdx(null);
        if (idx !== null) {
            setTicketErrorIdx(e => ({ ...e, [idx]: '' }));
        }
    }
    async function handleSaveTickets() {
        setTicketLoading(true); setTicketError('');
        try {
            // Placeholder for backend save
            await new Promise(res => setTimeout(res, 1000));
            setTicketLoading(false);
        } catch (e) {
            setTicketError(e.message || 'Failed to save tickets.');
            setTicketLoading(false);
        }
    }

    // Walk-in order mutation
    const walkInOrderMutation = useMutation({
        mutationFn: createWalkinOrder,
        onSuccess: () => {
            // Refetch orders to show the new walk-in order
            queryClient.invalidateQueries({ queryKey: ['eventOrders', effectiveEventId] });
            queryClient.invalidateQueries({ queryKey: ['orders', effectiveEventId] });

            queryClient.invalidateQueries({ queryKey: ['seat-assignments'] });
            queryClient.invalidateQueries({ queryKey: ['tierSummary'] });
            queryClient.invalidateQueries({ queryKey: ['tierSummaryDrawer'] });
            setWalkInModalOpen(false);
            setWalkInSeats([]);
            setWalkInSeatLabels({});
            setWalkInAssignments([]);
            setWalkInForm({
                customerName: '',
                customerEmail: '',
                customerPhone: '',
                selectedTickets: [],
                totalAmount: 0
            });
        },
        onError: (error: any) => {
            console.error(error.message || 'Failed to create walk-in order:', error);
        }
    });

    // Order status update mutation
    const updateOrderStatusMutation = useMutation({
        mutationFn: ({ orderId, statusData }: { orderId: string; statusData: any }) => updateOrderStatus(orderId, statusData),
        onSuccess: () => {
            // Refetch orders to show the updated status
            queryClient.invalidateQueries({ queryKey: ['eventOrders', effectiveEventId] });
            queryClient.invalidateQueries({ queryKey: ['orders', effectiveEventId] });

            setStatusChangeModalOpen(false);
            setSelectedOrderForStatusChange(null);
            setNewStatus('');
            setStatusChangeNotes('');
        },
        onError: (error: any) => {
            console.error(error.message || 'Failed to update order status:', error);
        }
    });

    // Delete event mutation
    const deleteEventMutation = useMutation({
        mutationFn: (eventId: string) => deleteEvent(eventId),
        onSuccess: () => {
            // Redirect to events list after successful deletion
            navigate('/profile');
        },
        onError: (error: any) => {
            console.error(error.message || 'Failed to delete event:', error);
            setToast('Failed to delete event. Please try again.');
            setTimeout(() => setToast(''), 3000);
        }
    });

    // Connect event to organizer mutation
    const connectOrganizerMutation = useMutation({
        mutationFn: (organizerId: string) => connectEventToOrganizer(organizerId, effectiveEventId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['organizerEvent', effectiveEventId] });
            setToast('Event successfully connected to organizer!');
            setTimeout(() => setToast(''), 3000);
        },
        onError: (error: any) => {
            console.error(error.message || 'Failed to connect event to organizer:', error);
            setToast('Failed to connect event to organizer. Please try again.');
            setTimeout(() => setToast(''), 3000);
        }
    });

    // Walk-in order helper functions
    function addTicketToWalkIn(ticket: any) {
        const existingTicket = walkInForm.selectedTickets.find(t => t.ticketId === ticket._id);
        if (existingTicket) {
            setWalkInForm(prev => ({
                ...prev,
                selectedTickets: prev.selectedTickets.map(t =>
                    t.ticketId === ticket._id
                        ? { ...t, quantity: t.quantity + 1 }
                        : t
                ),
                totalAmount: Number(prev.totalAmount) + Number(ticket.price)
            }));
        } else {
            // Check if event has multiple dates
            const eventTimes = eventData?.times || [];
            let selectedDate = undefined;

            if (eventTimes.length === 1) {
                // Auto-select the only date
                selectedDate = eventTimes[0].startDate + ' ' + eventTimes[0].startTime;
            } else if (eventTimes.length > 1) {
                // For multiple dates, we'll let user select in the UI
                selectedDate = undefined;
            }

            setWalkInForm(prev => ({
                ...prev,
                selectedTickets: [...prev.selectedTickets, {
                    ticketId: ticket._id,
                    quantity: 1,
                    price: Number(ticket.price),
                    name: ticket.name,
                    selectedDate: selectedDate
                }],
                totalAmount: Number(prev.totalAmount) + Number(ticket.price)
            }));
        }
    }

    function removeTicketFromWalkIn(ticketId: string) {
        const ticket = walkInForm.selectedTickets.find(t => t.ticketId === ticketId);
        if (ticket) {
            setWalkInForm(prev => ({
                ...prev,
                selectedTickets: prev.selectedTickets.filter(t => t.ticketId !== ticketId),
                totalAmount: Number(prev.totalAmount) - (Number(ticket.price) * ticket.quantity)
            }));
        }
    }

    function updateWalkInTicketQuantity(ticketId: string, quantity: number) {
        const ticket = walkInForm.selectedTickets.find(t => t.ticketId === ticketId);
        if (ticket) {
            const quantityDiff = quantity - ticket.quantity;
            setWalkInForm(prev => ({
                ...prev,
                selectedTickets: prev.selectedTickets.map(t =>
                    t.ticketId === ticketId
                        ? { ...t, quantity }
                        : t
                ),
                totalAmount: Number(prev.totalAmount) + (Number(ticket.price) * quantityDiff)
            }));
        }
    }

    function updateWalkInTicketDate(ticketId: string, selectedDate: string) {
        setWalkInForm(prev => ({
            ...prev,
            selectedTickets: prev.selectedTickets.map(t =>
                t.ticketId === ticketId
                    ? { ...t, selectedDate }
                    : t
            )
        }));
    }

    // Helper function to get all months that contain event dates
    function getAllEventMonths() {
        const allDays: Date[] = [];
        eventData?.times?.forEach((range: any) => {
            const start = new Date(range.startDate);
            const end = new Date(range.endDate);
            for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
                allDays.push(new Date(d));
            }
        });

        // Get unique months from all event days
        const uniqueMonths = new Set<string>();
        allDays.forEach(day => {
            uniqueMonths.add(`${day.getFullYear()}-${day.getMonth()}`);
        });

        // Convert back to Date objects and sort
        return Array.from(uniqueMonths)
            .map(monthStr => {
                const [year, month] = monthStr.split('-').map(Number);
                return new Date(year, month, 1);
            })
            .sort((a, b) => a.getTime() - b.getTime());
    }

    // Get all available dates for the event
    function getAvailableDates() {
        const allDays: Date[] = [];
        eventData?.times?.forEach((range: any) => {
            const start = new Date(range.startDate);
            const end = new Date(range.endDate);
            for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
                allDays.push(new Date(d));
            }
        });

        allDays.sort((a, b) => a.getTime() - b.getTime());
        const now = new Date();
        return allDays.filter(d => d >= now);
    }

    // Calculate total amount for walk-in form
    function calculateWalkInTotal() {
        return walkInForm.selectedTickets.reduce((total, ticket) => {
            return total + (ticket.seatTotal ?? Number(ticket.price) * ticket.quantity);
        }, 0);
    }

    // Email validation function
    function validateEmail(email: string): boolean {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    }

    // Handle validation with debouncing
    const validateHandle = React.useCallback(
        async (handle: string) => {
            if (!handle || handle.length < 3) {
                setHandleValidation({
                    isValidating: false,
                    isAvailable: null,
                    message: ''
                });
                return;
            }

            // Check if handle contains only valid characters (alphanumeric, hyphens, underscores)
            if (!/^[a-zA-Z0-9_-]+$/.test(handle)) {
                setHandleValidation({
                    isValidating: false,
                    isAvailable: false,
                    message: 'Handle can only contain letters, numbers, hyphens, and underscores'
                });
                return;
            }

            setHandleValidation({
                isValidating: true,
                isAvailable: null,
                message: 'Checking availability...'
            });

            try {
                const response = await checkHandleAvailability(handle, effectiveEventId);
                const isAvailable = response.data.available;

                setHandleValidation({
                    isValidating: false,
                    isAvailable,
                    message: isAvailable ? 'Handle is available!' : 'Handle is already taken'
                });
            } catch (error) {
                setHandleValidation({
                    isValidating: false,
                    isAvailable: false,
                    message: 'Error checking availability'
                });
            }
        },
        [effectiveEventId]
    );

    // Debounced handle validation
    const debouncedValidateHandle = React.useCallback(
        (() => {
            let timeoutId: NodeJS.Timeout;
            return (handle: string) => {
                clearTimeout(timeoutId);
                timeoutId = setTimeout(() => validateHandle(handle), 500);
            };
        })(),
        [validateHandle]
    );

    // Validate walk-in form
    function validateWalkInForm() {
        const errors = {
            customerName: '',
            customerEmail: '',
            customerPhone: ''
        };

        // Validate customer name
        if (!walkInForm.customerName.trim()) {
            errors.customerName = 'Customer name is required';
        } else if (walkInForm.customerName.trim().length < 2) {
            errors.customerName = 'Customer name must be at least 2 characters';
        }

        // Validate customer email
        if (!walkInForm.customerEmail.trim()) {
            errors.customerEmail = 'Email is required';
        } else if (!validateEmail(walkInForm.customerEmail.trim())) {
            errors.customerEmail = 'Please enter a valid email address';
        }

        // Validate customer phone (optional but if provided, must be valid)
        if (walkInForm.customerPhone.trim() && !/^[\+]?[1-9][\d]{0,15}$/.test(walkInForm.customerPhone.trim().replace(/[\s\-\(\)]/g, ''))) {
            errors.customerPhone = 'Please enter a valid phone number';
        }

        setWalkInErrors(errors);
        return !Object.values(errors).some(error => error !== '');
    }

    async function handleCreateWalkInOrder() {
        if (!validateWalkInForm() || walkInForm.selectedTickets.length === 0) {
            return;
        }

        const orderData = {
            eventId: effectiveEventId,
            tickets: walkInForm.selectedTickets.map(ticket => ({
                ticketId: ticket.ticketId,
                quantity: ticket.quantity,
                selectedDate: ticket.selectedDate,
                ...(ticket.seatUuids?.length ? { seatUuids: ticket.seatUuids } : {})
            })),
            customerName: walkInForm.customerName.trim(),
            customerEmail: walkInForm.customerEmail.trim(),
            customerPhone: walkInForm.customerPhone.trim(),
            paymentMethod: 'cash',
            paymentNotes: 'Walk-in order'
        };



        walkInOrderMutation.mutate(orderData);
    }

    // Delete event handler
    function handleDeleteEvent() {
        setDeleteConfirmModalOpen(false);
        setMoreMenuOpen(false);
        deleteEventMutation.mutate(effectiveEventId);
    }

    // Close MoreIcon menu when clicking outside or pressing Escape
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            const target = event.target as Element;
            if (moreMenuOpen && !target.closest('.more-menu-container')) {
                setMoreMenuOpen(false);
            }
        }

        function handleKeyDown(event: KeyboardEvent) {
            if (event.key === 'Escape') {
                setMoreMenuOpen(false);
                setDeleteConfirmModalOpen(false);
            }
        }

        if (moreMenuOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('keydown', handleKeyDown);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [moreMenuOpen]);

    // Close sidebar settings menu when clicking outside or pressing Escape
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            const target = event.target as Element;
            if (sidebarMenuOpen && !target.closest('.sidebar-menu-container') && !target.closest('.mobile-settings-menu-container')) {
                setSidebarMenuOpen(false);
            }
        }

        function handleKeyDown(event: KeyboardEvent) {
            if (event.key === 'Escape') {
                setSidebarMenuOpen(false);
            }
        }

        if (sidebarMenuOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('keydown', handleKeyDown);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [sidebarMenuOpen]);

    // Close mobile ticket row menu when clicking outside or pressing Escape
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            const target = event.target as Element;
            if (mobileTicketMenuIdx !== null && !target.closest('.mobile-ticket-menu-container')) {
                setMobileTicketMenuIdx(null);
            }
        }

        function handleKeyDown(event: KeyboardEvent) {
            if (event.key === 'Escape') {
                setMobileTicketMenuIdx(null);
            }
        }

        if (mobileTicketMenuIdx !== null) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('keydown', handleKeyDown);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [mobileTicketMenuIdx]);

    // Close mobile tab dropdown when clicking outside or pressing Escape
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            const target = event.target as Element;
            if (mobileTabMenuOpen && !target.closest('.mobile-tab-menu-container')) {
                setMobileTabMenuOpen(false);
            }
        }

        function handleKeyDown(event: KeyboardEvent) {
            if (event.key === 'Escape') {
                setMobileTabMenuOpen(false);
            }
        }

        if (mobileTabMenuOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('keydown', handleKeyDown);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [mobileTabMenuOpen]);



    // Tickets are now included in event data
    const ticketsData = eventData?.tickets ? {
        data: eventData.tickets.map((tk: any, index: number) => ({
            ...tk,
            _id: tk._id || `${effectiveEventId}-${index}`,
        }))
    } : null;
    useEffect(() => {
        if (!walkInSeatMode) return;
        const eventTimes = eventData?.times || [];
        const firstDate = getAvailableDates()[0];
        const selectedDate = eventTimes.length === 1
            ? eventTimes[0].startDate + ' ' + eventTimes[0].startTime
            : (eventTimes.length > 1 && firstDate ? moment(firstDate).format('YYYY-MM-DD HH:mm') : undefined);
        const rawTickets: any[] = Array.isArray(ticketsData?.data) ? ticketsData.data : [];
        const byTicket = new Map<string, { seatUuids: string[]; total: number }>();
        walkInSeats.forEach(seatUuid => {
            const assignment = walkInAssignments.find(a => a.seatUuid === seatUuid);
            if (!assignment) return;
            const base = Number(rawTickets.find(t => t._id === assignment.ticketId)?.price) || 0;
            const price = typeof assignment.price === 'number' ? assignment.price : base;
            const entry = byTicket.get(assignment.ticketId) ?? { seatUuids: [], total: 0 };
            entry.seatUuids.push(seatUuid);
            entry.total += price;
            byTicket.set(assignment.ticketId, entry);
        });
        const selectedTickets = [...byTicket.entries()].map(([ticketId, entry]) => ({
            ticketId,
            quantity: entry.seatUuids.length,
            price: entry.total / entry.seatUuids.length,
            name: rawTickets.find(t => t._id === ticketId)?.name ?? 'Ticket',
            selectedDate,
            seatUuids: entry.seatUuids,
            seatTotal: entry.total,
        }));
        setWalkInForm(prev => ({ ...prev, selectedTickets, totalAmount: selectedTickets.reduce((sum, t) => sum + t.seatTotal, 0) }));
    }, [walkInSeats, walkInAssignments, walkInSeatMode, ticketsData]); // eslint-disable-line react-hooks/exhaustive-deps

    const isTicketsLoading = isEventLoading;
    const isTicketsError = isEventError;
    const refetchTickets = () => {
        // Refetch event data to get updated tickets
        refetchEvent();
    };

    // Prefill tickets from event data
    useEffect(() => {
        // Prefill tickets from event.tickets array
        if (eventData?.tickets && Array.isArray(eventData.tickets)) {
            setTickets(eventData.tickets.map((tk: any, index: number) => ({
                _id: tk._id || `${effectiveEventId}-${index}`,
                name: tk.name || '',
                qty: tk.quantity?.toString() || '',
                price: tk.price?.toString() || '',
                type: tk.type || 'Paid',
                status: tk.status ?? true,
                desc: tk.description || '',
                start: tk.saleStart ? parseDate(tk.saleStart.split(' ')[0]) : null,
                startTime: tk.saleStart && tk.saleStart.split(' ')[1] ? (() => {
                    const timeStr = tk.saleStart.split(' ')[1];
                    const timeParts = timeStr.split(':');
                    if (timeParts.length >= 2) {
                        const [h, m] = timeParts.slice(0, 2).map(Number);
                        return (!isNaN(h) && !isNaN(m)) ? new Time(h, m) : null;
                    }
                    return null;
                })() : null,
                end: tk.saleEnd ? parseDate(tk.saleEnd.split(' ')[0]) : null,
                endTime: tk.saleEnd && tk.saleEnd.split(' ')[1] ? (() => {
                    const timeStr = tk.saleEnd.split(' ')[1];
                    const timeParts = timeStr.split(':');
                    if (timeParts.length >= 2) {
                        const [h, m] = timeParts.slice(0, 2).map(Number);
                        return (!isNaN(h) && !isNaN(m)) ? new Time(h, m) : null;
                    }
                    return null;
                })() : null,
                promocodes: tk.promocodes || [], // Include promocodes from ticket
                memberPricingDiscountType: tk.memberPricingDiscountType,
                memberPricingDiscount: tk.memberPricingDiscount,
                allowPromoCodeWithMemberPricing: tk.allowPromoCodeWithMemberPricing === true,
                tierPrices: (tk.tierPrices || []).map((tp: any) => ({ tier: tp.tier, price: String(tp.price) })),
                tierIds: Array.from(new Set<string>([...(tk.tierIds || []), ...(tk.tierPrices || []).map((tp: any) => tp.tier)])),
            })));
        }
    }, [eventData?.tickets, effectiveEventId]);

    useEffect(() => {
        if (!eventData || !eventData.title) return;
        console.log('EventComponent: Loading event data', {
            eventData,
            address: eventData.address,
            lat: eventData.lat,
            lng: eventData.lng
        });
        setInfo(prev => ({
            ...prev,
            name: eventData.title || '',
            category: (eventData.category?.toLowerCase() || ''),
            description: eventData.description || '',
            handle: eventData.handle || '',
            banner: null,
            bannerPreview: eventData.attachments?.banner?.link || '',
            socialImg: null,
            socialImgPreview: eventData.attachments?.images?.[0]?.link || '',
            additionalImages: [],
            additionalImagePreviews: (eventData.attachments?.additionalImages || []).map((img: any) => img.link),
            additionalSocialImages: [],
            additionalSocialImagePreviews: (eventData.attachments?.additionalSocialImages || []).map((img: any) => img.link),
            additionalBannerPhotos: [],
            additionalBannerPhotoPreviews: (eventData.attachments?.additionalBanners || []).map((img: any) => ({ link: img.link, path: img.path })),
            times: Array.isArray(eventData.times) && eventData.times.length > 0
                ? eventData.times.map((t: any) => {
                    let startDate = null, startTime = null, endDate = null, endTime = null;
                    if (t.startDate) {
                        const [date, time] = t.startDate.split(' ');
                        if (date) startDate = parseDate(date);
                        if (time) {
                            const timeParts = time.split(':');
                            if (timeParts.length >= 2) {
                                const [h, m] = timeParts.slice(0, 2).map(Number);
                                if (!isNaN(h) && !isNaN(m)) startTime = new Time(h, m);
                            }
                        }
                    }
                    if (t.startTime && !startTime) {
                        const timeParts = t.startTime.split(':');
                        if (timeParts.length >= 2) {
                            const [h, m] = timeParts.slice(0, 2).map(Number);
                            if (!isNaN(h) && !isNaN(m)) startTime = new Time(h, m);
                        }
                    }
                    if (t.endDate) {
                        const [date, time] = t.endDate.split(' ');
                        if (date) endDate = parseDate(date);
                        if (time) {
                            const timeParts = time.split(':');
                            if (timeParts.length >= 2) {
                                const [h, m] = timeParts.slice(0, 2).map(Number);
                                if (!isNaN(h) && !isNaN(m)) endTime = new Time(h, m);
                            }
                        }
                    }
                    if (t.endTime && !endTime) {
                        const timeParts = t.endTime.split(':');
                        if (timeParts.length >= 2) {
                            const [h, m] = timeParts.slice(0, 2).map(Number);
                            if (!isNaN(h) && !isNaN(m)) endTime = new Time(h, m);
                        }
                    }
                    return { startDate, startTime, endDate, endTime };
                })
                : [{ startDate: null, startTime: null, endDate: null, endTime: null }],
            address: (() => {
                // Handle both old string format and new object format
                if (typeof eventData.address === 'string') {
                    console.log('EventComponent: String address found:', eventData.address);
                    return eventData.address;
                } else if (eventData.address && typeof eventData.address === 'object') {
                    console.log('EventComponent: Object address found:', eventData.address);
                    return eventData.address.fullAddress || '';
                }
                console.log('EventComponent: No address found, returning empty string');
                return '';
            })(),
            venue: eventData.venue || '',
            venueId: eventData.venueId || '',
            map: eventData.map || '',
            howto: eventData.howto || '',
            city: (() => {
                if (eventData.address && typeof eventData.address === 'object') {
                    return eventData.address.city || '';
                }
                return '';
            })(),
            country: (() => {
                if (eventData.address && typeof eventData.address === 'object') {
                    return eventData.address.country || '';
                }
                return '';
            })(),
            state: (() => {
                if (eventData.address && typeof eventData.address === 'object') {
                    return eventData.address.state || '';
                }
                return '';
            })(),
            zipcode: (() => {
                if (eventData.address && typeof eventData.address === 'object') {
                    return eventData.address.zipcode || '';
                }
                return '';
            })(),
            isPrivate: eventData.isPrivate !== undefined ? (typeof eventData.isPrivate === 'boolean' ? (eventData.isPrivate ? 1 : 0) : eventData.isPrivate) : 0,
            showOnlyCity: eventData.showOnlyCity !== undefined ? (typeof eventData.showOnlyCity === 'boolean' ? (eventData.showOnlyCity ? 1 : 0) : eventData.showOnlyCity) : 0,
            commentsDisabled: eventData.commentsDisabled !== undefined ? (typeof eventData.commentsDisabled === 'boolean' ? (eventData.commentsDisabled ? 1 : 0) : eventData.commentsDisabled) : 0,
            ticketInfoMode: eventData.ticketInfoMode !== undefined ? Number(eventData.ticketInfoMode) : 0,
            youtubeVideoLink: eventData.youtubeVideoLink || '',
            liabilityWaiverEnabled: !!eventData.liabilityWaiverEnabled,
            liabilityWaiverText: eventData.liabilityWaiverText ? String(eventData.liabilityWaiverText) : '',
        }));
        console.log('EventComponent: Final info state set with address:', info.address);

        // Initialize map coordinates from event data
        if (eventData.lat && eventData.lng) {
            console.log('EventComponent: Setting map coordinates from event data:', { lat: eventData.lat, lng: eventData.lng });
            setLat(parseFloat(eventData.lat));
            setLng(parseFloat(eventData.lng));
        }
    }, [eventData, venue]);

    // Initialize refund settings from event data
    useEffect(() => {
        if (eventData) {
            setRefundable(eventData.refundable !== undefined ? eventData.refundable : true);
            setRefundDaysBeforeStart(eventData.refundDaysBeforeStart || 1);
            setFeeMode(eventData.feeMode === 'added' ? 'added' : 'included');
            setTaxMode(eventData.taxMode === 'added' ? 'added' : 'included');
            setCurrency(resolveCurrency(eventData.currency));
        }
    }, [eventData]);

    // Get role-based access information - always call this hook before any early returns
    const { data: userRolesData } = useUserRoles();
    const isOrgAdmin = Boolean(eventData?.organizerId && (
        (userRolesData?.ownedOrganizerIds ?? []).includes(eventData.organizerId)
        || (userRolesData?.roles ?? []).some(r => r.organizerId === eventData.organizerId && r.role === 'admin')
        || user?.permissions?.includes('ADMIN')
    ));
    const { canFullAccess, canCheckin, isUsher } = useEventAccess(
        eventData?.organizerId,
        eventData?.userId
    );

    // Redirect ushers to Orders tab if they're on a restricted tab
    useEffect(() => {
        if (isUsher && currentActiveTab !== 'Orders') {
            const currentEventId = eid || eventId;
            if (currentEventId) {
                navigate(`/organizations/events/${currentEventId}/Orders`);
            }
        }
    }, [isUsher, currentActiveTab, eid, eventId, navigate]);

    // Payout account status, used by the setup checklist on the Dashboard tab
    const { data: payoutAccount, isFetching: isPayoutAccountLoading } = useQuery({
        queryKey: ['payoutAccount'],
        queryFn: getPayoutAccount,
        retry: false,
        enabled: !!user && !isUsher,
    });

    // If loading or error
    if (isEventLoading) return <div className="text-center py-12 text-sm font-semibold text-grey40 dark:text-nightMuted">Loading event...</div>;
    if (isEventError || !event) return <div className="text-center py-12 text-sm font-semibold text-grey40 dark:text-nightMuted">Failed to load event.</div>;
    if (isOrdersLoading) return <div className="text-center py-12 text-sm font-semibold text-grey40 dark:text-nightMuted">Loading orders...</div>;
    if (isOrdersError) return <div className="text-center py-12 text-sm font-semibold text-grey40 dark:text-nightMuted">Failed to load orders.</div>;

    const eventBanner = eventData.attachments?.banner?.link || '/banner.png';
    const eventTitle = eventData.title || 'Untitled Event';
    const eventTimes = Array.isArray(eventData.times) ? eventData.times : [];
    const eventStartDate = eventTimes[0]?.startDate || '';
    const eventTotalSold = eventData.totalSold ?? 0;
    const eventTotalCapacity = eventData.totalCapacity ?? 0;
    const eventTotalRevenue = eventData.totalRevenue ?? 0;
    const eventTickets = Array.isArray(ticketsData?.data) ? ticketsData.data : [];
    const eventVenue = eventData.venue || '';
    const eventAddress = eventData.address || '';
    const eventMap = eventData.map || '';
    const eventDetails = eventData.howto || '';
    const eventCategory = eventData.category || '';

    // Check if current user can edit this event
    const canEdit = canUserEditEvent(user, eventData) || canFullAccess;
    const eventDescription = eventData.description || '';
    const eventOrganizer = eventData.info || { orgName: '', avatar: '', attachments: {} };
    const eventOrganizerAvatar = eventOrganizer.attachments?.avatar?.link || '';

    const _rawBannerUrl = info.bannerPreview || eventData.attachments?.banner?.link || '';
    const bannerToShow = _rawBannerUrl.startsWith('blob:') ? _rawBannerUrl : _rawBannerUrl ? `${_rawBannerUrl}?t=${bannerCacheBust}` : '';
    const _rawSocialUrl = info.socialImgPreview || eventData.attachments?.images?.[0]?.link || '';
    const socialImgToShow = _rawSocialUrl.startsWith('blob:') ? _rawSocialUrl : _rawSocialUrl ? `${_rawSocialUrl}?t=${socialImgCacheBust}` : '';

    // Sidebar navigation items
    const visibleTabs: ('Dashboard' | 'Orders' | 'Info' | 'Tickets' | 'FAQs' | 'Comments')[] = isUsher
        ? ['Orders']
        : eventData?.eventType === 'created' ? ['Dashboard', 'Tickets', 'Orders', 'Comments', 'FAQs'] : ['Info'];
    const tabIcons: Record<string, React.ReactNode> = {
        Dashboard: <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8 2V8H14M14 8C14 8.78793 13.8448 9.56815 13.5433 10.2961C13.2417 11.0241 12.7998 11.6855 12.2426 12.2426C11.6855 12.7998 11.0241 13.2417 10.2961 13.5433C9.56815 13.8448 8.78793 14 8 14C7.21207 14 6.43185 13.8448 5.7039 13.5433C4.97595 13.2417 4.31451 12.7998 3.75736 12.2426C3.20021 11.6855 2.75825 11.0241 2.45672 10.2961C2.15519 9.56815 2 8.78793 2 8C2 7.21207 2.15519 6.43185 2.45672 5.7039C2.75825 4.97595 3.20021 4.31451 3.75736 3.75736C4.31451 3.20021 4.97595 2.75825 5.7039 2.45672C6.43185 2.15519 7.21207 2 8 2C8.78793 2 9.56815 2.15519 10.2961 2.45672C11.0241 2.75825 11.6855 3.20021 12.2426 3.75736C12.7998 4.31451 13.2417 4.97595 13.5433 5.7039C13.8448 6.43185 14 7.21207 14 8Z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        ,
        Orders: <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6.00065 4.66667H10.0007M6.00065 7.33333H10.0007M8.66732 10H10.0007M3.33398 14V3.33333C3.33398 2.97971 3.47446 2.64057 3.72451 2.39052C3.97456 2.14048 4.3137 2 4.66732 2H11.334C11.6876 2 12.0267 2.14048 12.2768 2.39052C12.5268 2.64057 12.6673 2.97971 12.6673 3.33333V14L10.6673 12.6667L9.33398 14L8.00065 12.6667L6.66732 14L5.33398 12.6667L3.33398 14Z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        ,
        Info: <InfoIcon className="w-4 h-4" />,
        Tickets: <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 3.33331V4.66665M10 7.33331V8.66665M10 11.3333V12.6666M3.33333 3.33331H12.6667C13.0203 3.33331 13.3594 3.47379 13.6095 3.72384C13.8595 3.97389 14 4.31302 14 4.66665V6.66665C13.6464 6.66665 13.3072 6.80712 13.0572 7.05717C12.8071 7.30722 12.6667 7.64636 12.6667 7.99998C12.6667 8.3536 12.8071 8.69274 13.0572 8.94279C13.3072 9.19284 13.6464 9.33331 14 9.33331V11.3333C14 11.6869 13.8595 12.0261 13.6095 12.2761C13.3594 12.5262 13.0203 12.6666 12.6667 12.6666H3.33333C2.97971 12.6666 2.64057 12.5262 2.39052 12.2761C2.14048 12.0261 2 11.6869 2 11.3333V9.33331C2.35362 9.33331 2.69276 9.19284 2.94281 8.94279C3.19286 8.69274 3.33333 8.3536 3.33333 7.99998C3.33333 7.64636 3.19286 7.30722 2.94281 7.05717C2.69276 6.80712 2.35362 6.66665 2 6.66665V4.66665C2 4.31302 2.14048 3.97389 2.39052 3.72384C2.64057 3.47379 2.97971 3.33331 3.33333 3.33331Z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        ,
        Comments: <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2 13.3333V3.33333C2 2.97971 2.14048 2.64057 2.39052 2.39052C2.64057 2.14048 2.97971 2 3.33333 2H12.6667C13.0203 2 13.3594 2.14048 13.6095 2.39052C13.8595 2.64057 14 2.97971 14 3.33333V10C14 10.3536 13.8595 10.6928 13.6095 10.9428C13.3594 11.1929 13.0203 11.3333 12.6667 11.3333H4.66667L2 13.3333Z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        ,
        FAQs: <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8 10.6667V10.6733M8 8.66667C8.29983 8.66757 8.59122 8.56739 8.8271 8.38229C9.06299 8.1972 9.2296 7.938 9.30003 7.64656C9.37047 7.35511 9.34061 7.04843 9.21529 6.77605C9.08996 6.50366 8.87649 6.28146 8.60933 6.14534C8.34412 6.00948 8.04074 5.96736 7.74854 6.02582C7.45634 6.08428 7.19252 6.23989 7 6.46734M2 3.33333C2 2.97971 2.14048 2.64057 2.39052 2.39052C2.64057 2.14048 2.97971 2 3.33333 2H12.6667C13.0203 2 13.3594 2.14048 13.6095 2.39052C13.8595 2.64057 14 2.97971 14 3.33333V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V3.33333Z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        ,
    };
    const tabLabels: Record<string, string> = {
        Dashboard: 'Overview',
        Orders: 'Orders',
        Info: 'Info',
        Tickets: 'Tickets Listing',
        FAQs: 'FAQ',
        Comments: 'Comments',
    };

    // Status badge for the event summary card
    const eventEndDate = eventTimes[0]?.endDate || eventStartDate;
    const eventStatusLabel = moment().isBefore(moment(eventStartDate))
        ? 'Upcoming'
        : moment().isAfter(moment(eventEndDate))
            ? 'Past'
            : 'Live now';

    // Setup checklist, shown on the Dashboard tab for events missing something
    const hasCoverImage = !!eventData.attachments?.banner?.link;
    const hasTickets = eventTickets.length > 0;
    const needsPayoutSetup = !isUsher && !isPayoutAccountLoading && (!payoutAccount || payoutAccount.status !== 'active');
    const publicEventPath = eventData?.eventType === 'created' ? `/event/${eventData?._id}` : `/promoted-event/${eventData?._id}`;
    const publicEventUrl = typeof window !== 'undefined' ? `${window.location.origin}${publicEventPath}` : publicEventPath;

    return (
        <>
            {toast && (
                <div className="fixed top-4 right-4 z-50 rounded-full bg-grey10 px-5 py-2.5 text-sm font-semibold text-white shadow-xl dark:bg-white dark:text-nightBg">
                    {toast}
                </div>
            )}

            {/* Delete Confirmation Modal */}
            <DeleteConfirmModal
                isOpen={deleteConfirmModalOpen}
                onClose={() => setDeleteConfirmModalOpen(false)}
                onConfirm={handleDeleteEvent}
                title="Delete Event"
                message="This will permanently remove the event and all associated data including tickets and orders. This action cannot be undone."
                itemName={eventTitle}
                loading={deleteEventMutation.isPending}
            />

            {/* Mobile-only top bar: Back + event name + settings */}
            <div className="lg:hidden fixed top-0 left-0 right-0 z-[9999] bg-white/90 dark:bg-nightBg/90 h-14 flex items-center justify-between px-4 border-b border-black/[0.06] dark:border-white/10">
                <button
                    type="button"
                    onClick={() => navigate('/organizations/events')}
                    className="flex h-9 w-9 -ml-1 items-center justify-center rounded-full text-grey10 transition-colors hover:bg-grey99 dark:text-white dark:hover:bg-white/10 flex-shrink-0"
                    aria-label="Back"
                >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                        <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
                <div className="min-w-0 flex-1 px-3 text-center text-grey10 dark:text-white text-base font-bold tracking-[-0.02em] truncate">
                    {currentActiveTab !== 'Info' && eventTitle}
                </div>
                {currentActiveTab === 'Info' && (
                    <button
                        type="button"
                        onClick={() => handleSave()}
                        disabled={loading}
                        className="flex-shrink-0 inline-flex h-9 items-center px-4 rounded-full bg-primary text-white font-semibold text-sm transition-colors hover:bg-primary30 disabled:opacity-60 dark:bg-nightAccent dark:text-nightBg"
                    >
                        {loading ? 'Saving...' : 'Save'}
                    </button>
                )}
                <div className={`relative mobile-settings-menu-container flex-shrink-0 ${currentActiveTab === 'Info' ? 'hidden' : ''}`}>
                    <button
                        type="button"
                        onClick={() => setSidebarMenuOpen(v => !v)}
                        className="flex h-9 w-9 -mr-1 items-center justify-center rounded-full text-grey10 transition-colors hover:bg-grey99 dark:text-white dark:hover:bg-white/10"
                        aria-label="Event settings"
                    >
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                            <path
                                d="M12 22.63c-.67 0-1.35-.15-1.88-.46L4.62 19c-2.24-1.51-2.38-1.74-2.38-4.11V9.11c0-2.37.13-2.6 2.33-4.09l5.54-3.2c1.05-.61 2.7-.61 3.75 0L19.38 5c2.24 1.51 2.38 1.74 2.38 4.11v5.77c0 2.37-.13 2.6-2.33 4.09l-5.54 3.2c-.54.31-1.22.46-1.89.46m0-19.76c-.42 0-.83.08-1.12.25L5.38 6.3C3.75 7.4 3.75 7.4 3.75 9.11v5.77c0 1.71 0 1.71 1.67 2.84l5.46 3.15c.59.34 1.66.34 2.25 0l5.5-3.18c1.62-1.1 1.62-1.1 1.62-2.81V9.11c0-1.71 0-1.71-1.67-2.84l-5.46-3.15c-.29-.17-.7-.25-1.12-.25"
                                fill="currentColor"
                            />
                            <path
                                d="M12 15.75c-2.07 0-3.75-1.68-3.75-3.75S9.93 8.25 12 8.25s3.75 1.68 3.75 3.75-1.68 3.75-3.75 3.75m0-6c-1.24 0-2.25 1.01-2.25 2.25s1.01 2.25 2.25 2.25 2.25-1.01 2.25-2.25S13.24 9.75 12 9.75"
                                fill="currentColor"
                            />
                        </svg>
                    </button>
                    {sidebarMenuOpen && (
                        <div className={`absolute right-0 top-full mt-2 z-50 min-w-[200px] flex flex-col ${ecMenu}`}>
                            <button
                                className={`${ecMenuItem} text-grey10 hover:bg-grey99 dark:text-nightTextSecondary dark:hover:bg-white/10`}
                                onClick={() => {
                                    setSidebarMenuOpen(false);
                                    handleTabChange('Info' as any);
                                }}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                    <path d="M7 6.99998H6C5.46957 6.99998 4.96086 7.2107 4.58579 7.58577C4.21071 7.96084 4 8.46955 4 8.99998V18C4 18.5304 4.21071 19.0391 4.58579 19.4142C4.96086 19.7893 5.46957 20 6 20H15C15.5304 20 16.0391 19.7893 16.4142 19.4142C16.7893 19.0391 17 18.5304 17 18V17M16 4.99998L19 7.99998M20.385 6.58499C20.7788 6.19114 21.0001 5.65697 21.0001 5.09998C21.0001 4.543 20.7788 4.00883 20.385 3.61498C19.9912 3.22114 19.457 2.99988 18.9 2.99988C18.343 2.99988 17.8088 3.22114 17.415 3.61498L9 12V15H12L20.385 6.58499Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                Edit Event
                            </button>
                            <RoleBasedAccess
                                eventOrganizerId={eventData?.organizerId}
                                eventUserId={eventData?.userId}
                                requiredAccess="edit"
                                fallback={null}
                            >
                                <button
                                    className={`${ecMenuItem} text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10`}
                                    onClick={() => {
                                        setSidebarMenuOpen(false);
                                        setDeleteConfirmModalOpen(true);
                                    }}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                        <path d="M4 7H20M10 11V17M14 11V17M5 7L6 19C6 19.5304 6.21071 20.0391 6.58579 20.4142C6.96086 20.7893 7.46957 21 8 21H16C16.5304 21 17.0391 20.7893 17.4142 20.4142C17.7893 20.0391 18 19.5304 18 19L19 7M9 7V4C9 3.73478 9.10536 3.48043 9.29289 3.29289C9.48043 3.10536 9.73478 3 10 3H14C14.2652 3 14.5196 3.10536 14.7071 3.29289C14.8946 3.48043 15 3.73478 15 4V7" stroke="#FF5C6C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    Delete Event
                                </button>
                            </RoleBasedAccess>
                        </div>
                    )}
                </div>
            </div>

            <div className="flex flex-col lg:flex-row gap-6 lg:gap-10 w-full items-start lg:items-start pt-14 lg:pt-0 bg-white dark:bg-nightBg lg:bg-transparent lg:dark:bg-transparent min-h-screen lg:min-h-0">
                {/* Sidebar */}
                <aside className="sidebar-menu-container hidden lg:flex lg:flex-col lg:flex-shrink-0 lg:sticky lg:top-[80px] lg:self-start lg:w-64 lg:h-[calc(100vh-80px)] lg:z-30 bg-white dark:bg-nightBg border-r border-black/[0.06] dark:border-white/10 relative pt-4 pl-4 pr-4">
                    <div className="relative flex-shrink-0">
                        <img src={bannerToShow || '/banner.png'} alt="event" className="w-full h-[140px] object-cover rounded-[22px] ring-1 ring-black/[0.06] dark:ring-white/10" />
                        <div className="absolute top-3 right-3">
                            <button
                                type="button"
                                onClick={() => setSidebarMenuOpen(v => !v)}
                                className="w-10 h-10 rounded-full bg-black/45 text-white flex items-center justify-center hover:bg-black/60 transition-colors"
                                aria-label="Event settings"
                            >
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                                    <path
                                        d="M12 22.63c-.67 0-1.35-.15-1.88-.46L4.62 19c-2.24-1.51-2.38-1.74-2.38-4.11V9.11c0-2.37.13-2.6 2.33-4.09l5.54-3.2c1.05-.61 2.7-.61 3.75 0L19.38 5c2.24 1.51 2.38 1.74 2.38 4.11v5.77c0 2.37-.13 2.6-2.33 4.09l-5.54 3.2c-.54.31-1.22.46-1.89.46m0-19.76c-.42 0-.83.08-1.12.25L5.38 6.3C3.75 7.4 3.75 7.4 3.75 9.11v5.77c0 1.71 0 1.71 1.67 2.84l5.46 3.15c.59.34 1.66.34 2.25 0l5.5-3.18c1.62-1.1 1.62-1.1 1.62-2.81V9.11c0-1.71 0-1.71-1.67-2.84l-5.46-3.15c-.29-.17-.7-.25-1.12-.25"
                                        fill="currentColor"
                                    />
                                    <path
                                        d="M12 15.75c-2.07 0-3.75-1.68-3.75-3.75S9.93 8.25 12 8.25s3.75 1.68 3.75 3.75-1.68 3.75-3.75 3.75m0-6c-1.24 0-2.25 1.01-2.25 2.25s1.01 2.25 2.25 2.25 2.25-1.01 2.25-2.25S13.24 9.75 12 9.75"
                                        fill="currentColor"
                                    />
                                </svg>
                            </button>
                            {sidebarMenuOpen && (
                                <div className={`absolute right-0 top-full mt-2 z-50 min-w-[200px] flex flex-col ${ecMenu}`}>
                                    <button
                                        className={`${ecMenuItem} text-grey10 hover:bg-grey99 dark:text-nightTextSecondary dark:hover:bg-white/10`}
                                        onClick={() => {
                                            setSidebarMenuOpen(false);
                                            handleTabChange('Info' as any);
                                        }}
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                            <path d="M7 6.99998H6C5.46957 6.99998 4.96086 7.2107 4.58579 7.58577C4.21071 7.96084 4 8.46955 4 8.99998V18C4 18.5304 4.21071 19.0391 4.58579 19.4142C4.96086 19.7893 5.46957 20 6 20H15C15.5304 20 16.0391 19.7893 16.4142 19.4142C16.7893 19.0391 17 18.5304 17 18V17M16 4.99998L19 7.99998M20.385 6.58499C20.7788 6.19114 21.0001 5.65697 21.0001 5.09998C21.0001 4.543 20.7788 4.00883 20.385 3.61498C19.9912 3.22114 19.457 2.99988 18.9 2.99988C18.343 2.99988 17.8088 3.22114 17.415 3.61498L9 12V15H12L20.385 6.58499Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                        Edit Event
                                    </button>
                                    <RoleBasedAccess
                                        eventOrganizerId={eventData?.organizerId}
                                        eventUserId={eventData?.userId}
                                        requiredAccess="edit"
                                        fallback={null}
                                    >
                                        <button
                                            className={`${ecMenuItem} text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10`}
                                            onClick={() => {
                                                setSidebarMenuOpen(false);
                                                setDeleteConfirmModalOpen(true);
                                            }}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                                <path d="M4 7H20M10 11V17M14 11V17M5 7L6 19C6 19.5304 6.21071 20.0391 6.58579 20.4142C6.96086 20.7893 7.46957 21 8 21H16C16.5304 21 17.0391 20.7893 17.4142 20.4142C17.7893 20.0391 18 19.5304 18 19L19 7M9 7V4C9 3.73478 9.10536 3.48043 9.29289 3.29289C9.48043 3.10536 9.73478 3 10 3H14C14.2652 3 14.5196 3.10536 14.7071 3.29289C14.8946 3.48043 15 3.73478 15 4V7" stroke="#FF5C6C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                            Delete Event
                                        </button>
                                    </RoleBasedAccess>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="px-1 pt-4 pb-5 flex-shrink-0">
                        <span className={`${ecPill} mb-2 ${eventStatusLabel === 'Live now' ? 'bg-green-50 text-green-700 dark:bg-green-500/10 dark:text-green-400' : eventStatusLabel === 'Past' ? 'bg-grey99 text-grey40 dark:bg-white/[0.06] dark:text-nightMuted' : 'bg-primary/10 text-primary dark:bg-nightAccent/15 dark:text-nightAccent'}`}>
                            {eventStatusLabel}
                        </span>
                        <div className="text-xl font-bold leading-[1.1] tracking-[-0.03em] text-grey10 dark:text-white">{eventTitle}</div>
                    </div>
                    <nav className="flex flex-col gap-1 flex-1 overflow-y-auto pb-4">
                        {visibleTabs.map(tab => {
                            const active = currentActiveTab === tab;
                            return (
                                <button
                                    key={tab}
                                    onClick={() => handleTabChange(tab as any)}
                                    className={`flex items-center gap-3 px-4 rounded-full h-11 text-sm font-semibold transition-colors text-left ${active ? 'bg-grey10 text-white shadow-sm dark:bg-white dark:text-nightBg' : 'text-grey40 hover:bg-grey99 hover:text-grey10 dark:text-nightMuted dark:hover:bg-white/[0.06] dark:hover:text-white'}`}
                                >
                                    {tabIcons[tab]}
                                    {tabLabels[tab] || tab}
                                    {tab === 'Comments' && eventData?.commentsDisabled === 1 && (
                                        <span className={`ml-auto inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-bold ${active ? 'bg-white/20 text-white dark:bg-nightBg/15 dark:text-nightBg' : 'bg-grey95 text-grey40 dark:bg-white/10 dark:text-nightMuted'}`}>
                                            Disabled
                                        </span>
                                    )}
                                    {tab === 'Comments' && pendingCommentCount > 0 && (
                                        <span className={`ml-auto inline-flex min-w-[22px] items-center justify-center rounded-full px-1.5 py-0.5 text-[11px] font-bold ${active ? 'bg-white/20 text-white dark:bg-nightBg/15 dark:text-nightBg' : 'bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300'}`} aria-label={`${pendingCommentCount} pending comments`}>
                                            {pendingCommentCount}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </nav>
                    {/* <div className="flex-shrink-0 px-4 py-4 border-t border-[#F4F5F6] dark:border-nightBorder">
                        <span className={`inline-block mb-2 px-2 py-0.5 rounded text-[11px] font-semibold ${eventStatusLabel === 'Live now' ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400' : eventStatusLabel === 'Past' ? 'bg-gray-100 dark:bg-nightElevated text-gray-500 dark:text-nightMuted' : 'bg-[#E6F6F4] dark:bg-nightAccent/10 text-primary40 dark:text-nightAccent'}`}>
                            {eventStatusLabel}
                        </span>
                        <a
                            href={publicEventPath}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block text-xs text-primary40 hover:underline truncate"
                        >
                            {publicEventUrl}
                        </a>
                    </div> */}
                </aside>

                {/* Main content */}
                <div className={`w-full min-w-0 flex-1 px-4 lg:pl-0 lg:pr-4 ${currentActiveTab === 'Tickets' ? '' : ''}`}>
                    {/* Authorization Notice for Authorized Users */}
                    {/* {canEdit && user?.email !== eventData.userId && (
                    <div className="bg-blue-50 border-b border-blue-200 px-4 md:px-8 py-3">
                        <div className="flex items-center gap-3">
                            <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div className="text-sm text-blue-800">
                                <span className="font-medium">You are authorized to manage this event.</span>
                                <span className="ml-2 text-blue-700">You can edit event details, manage tickets, and perform check-ins.</span>
                            </div>
                        </div>
                    </div>
                )} */}

                    {/* Tab dropdown (mobile only — desktop uses the sidebar nav). Sticky right under
                        the fixed app header (h-16) so it stays visible while scrolling, on every tab. */}
                    <div className={`${currentActiveTab === 'Info' ? 'hidden ' : ''}mobile-tab-menu-container sticky top-14 z-30 bg-white dark:bg-nightBg lg:static lg:top-auto lg:z-auto lg:bg-transparent lg:hidden mb-3 py-2`}>
                        <button
                            type="button"
                            onClick={() => setMobileTabMenuOpen(v => !v)}
                            className="w-full flex items-center justify-between gap-2 px-5 h-12 rounded-full bg-grey99 ring-1 ring-black/[0.04] dark:bg-white/[0.06] dark:ring-white/[0.06] text-grey10 dark:text-white font-semibold text-base"
                        >
                            <span className="flex items-center gap-2">
                                {tabIcons[currentActiveTab]}
                                {tabLabels[currentActiveTab] || currentActiveTab}
                            </span>
                            <ChevronDown className={`w-5 h-5 text-grey40 dark:text-nightMuted transition-transform ${mobileTabMenuOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {mobileTabMenuOpen && (
                            <div className={`absolute left-0 right-0 top-full mt-1 z-40 ${ecMenu}`}>
                                {visibleTabs.map(tab => {
                                    const active = currentActiveTab === tab;
                                    return (
                                        <button
                                            key={tab}
                                            className={`${ecMenuItem} ${active ? 'bg-grey10 text-white dark:bg-white dark:text-nightBg' : 'text-grey10 hover:bg-grey99 dark:text-nightTextSecondary dark:hover:bg-white/10'}`}
                                            onClick={() => {
                                                handleTabChange(tab as any);
                                                setMobileTabMenuOpen(false);
                                            }}
                                        >
                                            {tabIcons[tab]}
                                            {tabLabels[tab] || tab}
                                            {tab === 'Comments' && eventData?.commentsDisabled === 1 && (
                                                <span className="ml-2 inline-flex items-center rounded-full bg-grey95 px-2 py-0.5 text-[11px] font-bold text-grey40 dark:bg-white/10 dark:text-nightMuted">
                                                    Disabled
                                                </span>
                                            )}
                                            {tab === 'Comments' && pendingCommentCount > 0 && (
                                                <span className="ml-2 inline-flex min-w-[22px] items-center justify-center rounded-full bg-amber-100 px-1.5 py-0.5 text-[11px] font-bold text-amber-800 dark:bg-amber-500/20 dark:text-amber-300" aria-label={`${pendingCommentCount} pending comments`}>
                                                    {pendingCommentCount}
                                                </span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                    {currentActiveTab === 'Orders' && (
                        <div className="mt-0">
                            <div className="mb-6">
                                <div className={ecH2}>Orders</div>
                            </div>

                        </div>
                    )}
                    <RoleBasedAccess
                        eventOrganizerId={eventData?.organizerId}
                        eventUserId={eventData?.userId}
                        requiredAccess="checkin"
                        fallback={
                            <div className='rounded-[28px] bg-grey99 dark:bg-white/[0.04] mt-4'>
                                <div className="px-4 md:px-8 pt-6 pb-8">
                                    <div className="text-center py-12">
                                        <div className="text-base text-grey40 dark:text-nightMuted">You don't have permission to access this event.</div>
                                    </div>
                                </div>
                            </div>
                        }
                    >
                        <div className="mt-0">
                            {currentActiveTab === 'FAQs' && (
                                <FAQsTab eventId={effectiveEventId} />
                            )}
                            {currentActiveTab === 'Comments' && (
                                <CommentsTab eventId={effectiveEventId} commentsDisabled={eventData?.commentsDisabled === 1} canEdit={canEdit} onEditSettings={openCommentSettings} />
                            )}
                            {currentActiveTab === 'Dashboard' && (
                                <div className="w-full pt-0 pb-8">
                                    {pendingCommentCount > 0 && (
                                        <button
                                            type="button"
                                            onClick={() => handleTabChange('Comments')}
                                            className="mb-4 flex w-full items-center justify-between rounded-2xl bg-amber-50 px-5 py-3 text-left text-sm font-semibold text-amber-800 ring-1 ring-amber-200 transition-colors hover:bg-amber-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20"
                                        >
                                            <span>{pendingCommentCount === 1 ? '1 comment needs your review' : `${pendingCommentCount} comments need your review`}</span>
                                            <span aria-hidden="true">Review</span>
                                        </button>
                                    )}
                                    {/* Setup checklist */}
                                    {needsPayoutSetup && (
                                        <div className="mb-6 p-5 rounded-[28px] bg-amber-50 ring-1 ring-amber-200/70 dark:bg-amber-500/10 dark:ring-amber-500/20 flex items-start gap-4">
                                            <span className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-500/15 text-amber-600 dark:text-amber-400 flex items-center justify-center flex-shrink-0">
                                                <AlertTriangle className="w-4 h-4" />
                                            </span>
                                            <div className="flex-1">
                                                <div className="text-base font-bold tracking-[-0.01em] text-amber-900 dark:text-amber-300">Complete your account to receive payouts</div>
                                                <div className="text-sm text-amber-700 dark:text-amber-400/90 mt-1">Stripe, our payment provider, requires your organization's details to process payments and send payouts.</div>
                                                <button
                                                    type="button"
                                                    onClick={() => navigate('/organizations/payments')}
                                                    className="mt-4 inline-flex h-10 items-center px-5 rounded-full bg-amber-900 text-amber-50 text-sm font-semibold hover:bg-amber-950 dark:bg-amber-400 dark:text-nightBg dark:hover:bg-amber-300 transition-colors"
                                                >
                                                    Add information
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                    {(!hasTickets || !hasCoverImage) && (
                                        <div className="mb-6">
                                            <h3 className="text-xl font-bold tracking-[-0.02em] text-grey10 dark:text-white mb-4">Complete your event setup</h3>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                {!hasTickets && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleTabChange('Tickets' as any)}
                                                        className="flex items-center gap-3 p-4 rounded-[22px] bg-grey99 ring-1 ring-black/[0.04] hover:ring-primary/40 hover:bg-primary99 dark:bg-white/[0.04] dark:ring-white/[0.06] dark:hover:bg-nightAccent/10 dark:hover:ring-nightAccent/40 transition-colors text-left"
                                                    >
                                                        <span className="w-10 h-10 rounded-full bg-primary/10 dark:bg-nightAccent/15 text-primary dark:text-nightAccent flex items-center justify-center flex-shrink-0">
                                                            <Ticket className="w-4 h-4" />
                                                        </span>
                                                        <span className="text-sm font-semibold text-grey10 dark:text-white">Add tickets</span>
                                                    </button>
                                                )}
                                                {!hasCoverImage && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleTabChange('Info' as any)}
                                                        className="flex items-center gap-3 p-4 rounded-[22px] bg-grey99 ring-1 ring-black/[0.04] hover:ring-primary/40 hover:bg-primary99 dark:bg-white/[0.04] dark:ring-white/[0.06] dark:hover:bg-nightAccent/10 dark:hover:ring-nightAccent/40 transition-colors text-left"
                                                    >
                                                        <span className="w-10 h-10 rounded-full bg-primary/10 dark:bg-nightAccent/15 text-primary dark:text-nightAccent flex items-center justify-center flex-shrink-0">
                                                            <ImagePlus className="w-4 h-4" />
                                                        </span>
                                                        <span className="text-sm font-semibold text-grey10 dark:text-white">Add a cover image</span>
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    {/* Mobile status row */}
                                    <div className="lg:hidden flex items-center justify-between h-12 px-5 mb-5 rounded-full bg-grey99 ring-1 ring-black/[0.04] dark:bg-white/[0.04] dark:ring-white/[0.06]">
                                        <span className="text-xs font-bold uppercase tracking-[0.14em] text-grey40 dark:text-nightMuted">Status</span>
                                        <span className={`${ecPill} ${eventStatusLabel === 'Past' ? 'bg-grey95 text-grey40 dark:bg-white/[0.06] dark:text-nightMuted' : 'bg-green-50 text-green-700 dark:bg-green-500/10 dark:text-green-400'}`}>{eventStatusLabel === 'Past' ? 'ENDED' : 'ON SALE'}</span>
                                    </div>

                                    {/* Section heading */}
                                    <div className="mb-6">
                                        <div className={`hidden lg:block ${ecH2} mb-8`}>Overview</div>

                                        <div className="flex flex-col lg:flex-row lg:items-center gap-1 lg:gap-6 mb-8">
                                            <div className={`hidden lg:block ${ecStatTile} flex-shrink-0 lg:min-w-[220px]`}>
                                                <div className={`${ecStatLabel} mb-3`}>Tickets Sold</div>
                                                <div className={ecStatValue}>
                                                    {calculateTotalSoldAmount()}
                                                </div>
                                            </div>
                                            <div className="min-w-0">
                                                <div className="text-2xl font-bold tracking-[-0.03em] text-grey10 dark:text-white truncate">{eventTitle}</div>
                                                <div className="text-sm text-grey40 dark:text-nightMuted mt-1.5">
                                                    {eventData?.times?.[0]?.startDate && (
                                                        <>
                                                            <span className="font-semibold text-grey10 dark:text-nightTextSecondary">{moment(eventData.times[0].startDate).format('MMM D, YYYY')}</span>
                                                            {' from '}
                                                            <span className="font-semibold text-grey10 dark:text-nightTextSecondary">{moment(eventData.times[0].startDate).format('h:mm A')}{eventData.times[0].endDate ? ` - ${moment(eventData.times[0].endDate).format('h:mm A')}` : ''}</span>
                                                        </>
                                                    )}
                                                </div>
                                                <a
                                                    href={publicEventUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="mt-3 inline-flex max-w-full items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1.5 text-sm font-semibold text-primary dark:bg-nightAccent/15 dark:text-nightAccent hover:bg-primary/15 dark:hover:bg-nightAccent/20 truncate"
                                                >
                                                    {publicEventUrl.replace(/^https?:\/\//, '')}
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="flex-shrink-0">
                                                        <path d="M7 17L17 7M17 7H8M17 7V16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                </a>
                                            </div>
                                        </div>

                                        <div className="text-2xl lg:text-[28px] font-bold tracking-[-0.03em] text-grey10 dark:text-white">Your Sales</div>
                                    </div>

                                    {/* Statistics Cards */}
                                    <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 ${isUsher ? 'mb-10' : 'mb-3 lg:mb-4'}`}>
                                        {/* Net Revenue */}
                                        {!isUsher && (
                                            <div className={ecStatTile}>
                                                <div className={`${ecStatLabel} mb-4`}>Net Revenue</div>
                                                <div className={ecStatValue}>
                                                    {cur}{calculateNetRevenue().toFixed(0)}
                                                </div>
                                            </div>
                                        )}

                                        {/* Total Check-ins */}
                                        <div className={ecStatTile}>
                                            <div className="flex items-start justify-between gap-2 mb-4">
                                                <div className={ecStatLabel}>Total Check-ins</div>
                                                {eventTotalCapacity > 0 && (
                                                    <span className={`${ecPill} bg-white text-grey20 ring-1 ring-black/[0.06] dark:bg-white/[0.06] dark:text-nightTextSecondary dark:ring-white/10 whitespace-nowrap`}>
                                                        {Math.round((calculateTotalCheckIns() / eventTotalCapacity) * 100)}%
                                                    </span>
                                                )}
                                            </div>
                                            <div className='flex items-end justify-between gap-2'>
                                                <div className={ecStatValue}>
                                                    {calculateTotalCheckIns()}/{eventTotalCapacity}
                                                </div>
                                                <div className="text-xs md:text-sm text-grey40 dark:text-nightMuted whitespace-nowrap"><span className='font-semibold text-grey10 dark:text-nightTextSecondary'>{calculateTotalWalkIns()}</span> Walk-Ins</div>
                                            </div>
                                        </div>

                                        {/* Service Fee */}
                                        {!isUsher && (
                                            <div className={ecStatTile}>
                                                <div className="flex items-start justify-between gap-2 mb-4">
                                                    <div className={ecStatLabel}>{event?.data?.resolvedFee?.feeLabel || 'Service Fee'}</div>
                                                    <span className={`${ecPill} bg-red-50 text-[#C62828] dark:bg-red-500/10 dark:text-[#FF5C6C] whitespace-nowrap`}>
                                                        {calculateFeeRate()}%
                                                    </span>
                                                </div>
                                                <div className="text-[clamp(28px,3vw,40px)] font-bold leading-none tracking-[-0.04em] text-[#C62828] dark:text-[#FF5C6C]">
                                                    {cur}{calculateTotalFees().toFixed(0)}
                                                </div>
                                            </div>
                                        )}

                                        {/* Refunds */}
                                        {!isUsher && (
                                            <div className={ecStatTile}>
                                                <div className="flex items-start justify-between gap-2 mb-4">
                                                    <div className={ecStatLabel}>Refunds</div>
                                                    <span className={`${ecPill} bg-white text-grey20 ring-1 ring-black/[0.06] dark:bg-white/[0.06] dark:text-nightTextSecondary dark:ring-white/10 whitespace-nowrap`}>
                                                        {refundTotals.count} orders
                                                    </span>
                                                </div>
                                                <div className={`text-[clamp(28px,3vw,40px)] font-bold leading-none tracking-[-0.04em] ${refundTotals.count > 0 ? 'text-[#C62828] dark:text-[#FF5C6C]' : 'text-grey10 dark:text-white'}`}>
                                                    {cur}{refundTotals.amount.toFixed(0)}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {!isUsher && (
                                        <EventSalesDashboard orders={orders} capacity={eventTotalCapacity} tiers={tierList} cur={cur} endDate={eventData?.times?.[0]?.endDate ?? null} />
                                    )}

                                    {/* Sales Breakdown Section */}
                                    {!tierMode && (
                                    <div className="mb-4 lg:rounded-[28px] lg:bg-white lg:ring-1 lg:ring-black/[0.06] lg:dark:bg-white/[0.04] lg:dark:ring-white/10 pt-0 lg:pt-6 pb-0 lg:pb-3 lg:overflow-hidden">
                                        <h3 className="text-xl font-bold tracking-[-0.02em] text-grey10 dark:text-white mb-4 lg:px-6">Sales Breakdown</h3>

                                        {/* Desktop Table View (hidden on mobile) */}
                                        <div className="hidden lg:block overflow-x-auto">
                                            <table className="min-w-full text-sm">
                                                <thead>
                                                    <tr className="bg-grey99 dark:bg-white/[0.04] text-grey50 dark:text-nightMuted text-xs uppercase tracking-[0.12em]">
                                                        <th className="py-3 px-6 text-left font-bold">Ticket</th>
                                                        <th className="py-3 px-4 text-left font-bold">Sold</th>
                                                        {!isUsher && <th className="py-3 px-4 text-left font-bold">Revenue</th>}
                                                        {!isUsher && <th className="py-3 px-4 text-left font-bold">Service Fee</th>}
                                                        <th className="py-3 px-4 text-left font-bold">Check_ins</th>
                                                        <th className="py-3 px-4 text-left font-bold">Walk_ins</th>
                                                        <th className="py-3 px-6 text-left font-bold">Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-black/[0.06] dark:divide-white/10">
                                                    {Array.isArray(ticketsData?.data) && ticketsData.data.length > 0 ? (
                                                        ticketsData.data.map((ticket: any) => {
                                                            const soldCount = calculateTicketSoldAmount(ticket._id);
                                                            const totalQuantity = ticket.quantity || 0;
                                                            const ticketRevenue = calculateTicketRevenue(ticket._id);
                                                            const checkInCount = calculateTicketCheckInAmount(ticket._id);
                                                            const walkIns = calculateTicketWalkIns(ticket._id);
                                                            const ticketFees = calculateTicketFees(ticket._id);
                                                            const isSoldOut = totalQuantity > 0 && soldCount >= totalQuantity;

                                                            return (
                                                                <tr key={ticket._id}>
                                                                    <td className="py-4 px-6 text-grey10 dark:text-white font-semibold">{ticket.name} <span className='font-normal text-grey50 dark:text-nightMuted'>. {cur}{ticket.price || 0}</span></td>
                                                                    <td className="py-4 px-4 text-grey10 dark:text-nightTextSecondary font-medium">
                                                                        {soldCount}<span className="text-grey60 dark:text-nightMuted font-normal">/{totalQuantity}</span>
                                                                    </td>
                                                                    {!isUsher && (
                                                                        <td className="py-4 px-4 text-grey10 dark:text-nightTextSecondary font-medium">
                                                                            {cur}{ticketRevenue.toFixed(0)}
                                                                        </td>
                                                                    )}
                                                                    {!isUsher && (
                                                                        <td className="py-4 px-4 text-[#C62828] dark:text-red-400 font-medium">
                                                                            {cur}{ticketFees.toFixed(0)}
                                                                        </td>
                                                                    )}
                                                                    <td className="py-4 px-4 text-grey10 dark:text-nightTextSecondary font-medium">
                                                                        {checkInCount}<span className="text-grey60 dark:text-nightMuted font-normal">/{soldCount}</span>
                                                                    </td>
                                                                    <td className="py-4 px-4 text-grey10 dark:text-nightTextSecondary font-medium">{walkIns}</td>
                                                                    <td className="py-4 px-6">
                                                                        <span className={`${ecPill} ${isSoldOut ? 'bg-purple-100 text-purple-700 dark:bg-purple-500/15 dark:text-purple-300' : 'bg-primary/10 text-primary dark:bg-nightAccent/15 dark:text-nightAccent'}`}>
                                                                            {isSoldOut ? 'Sold Out' : 'On Sale'}
                                                                        </span>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })
                                                    ) : (
                                                        <tr>
                                                            <td colSpan={isUsher ? 5 : 7} className="py-10 px-4 text-center text-grey40 dark:text-nightMuted">
                                                                No tickets available
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>

                                        {/* Mobile Card View (hidden on desktop) */}
                                        <div className="lg:hidden">
                                            <div className="flex flex-col gap-3">
                                                {Array.isArray(ticketsData?.data) && ticketsData.data.length > 0 ? (
                                                    ticketsData.data.map((ticket: any) => {
                                                        const soldCount = calculateTicketSoldAmount(ticket._id);
                                                        const totalQuantity = ticket.quantity || 0;
                                                        const ticketRevenue = calculateTicketRevenue(ticket._id);
                                                        const checkInCount = calculateTicketCheckInAmount(ticket._id);
                                                        const walkIns = calculateTicketWalkIns(ticket._id);
                                                        const ticketFees = calculateTicketFees(ticket._id);
                                                        const isSoldOut = totalQuantity > 0 && soldCount >= totalQuantity;

                                                        return (
                                                            <div key={ticket._id} className={`${ecStatTile} p-5`}>
                                                                <div className="text-base font-bold tracking-[-0.01em] text-grey10 dark:text-white mb-4">
                                                                    {ticket.name} <span className="text-grey50 dark:text-nightMuted font-normal">. {cur}{ticket.price || 0}</span>
                                                                </div>
                                                                <div className="space-y-2">
                                                                    <div className="flex justify-between items-center">
                                                                        <span className="text-xs font-bold uppercase tracking-[0.12em] text-grey40 dark:text-nightMuted">Sold</span>
                                                                        <span className="text-sm font-bold text-grey10 dark:text-white">
                                                                            {soldCount}/{totalQuantity}
                                                                        </span>
                                                                    </div>
                                                                    {!isUsher && (
                                                                        <div className="flex justify-between items-center">
                                                                            <span className="text-xs font-bold uppercase tracking-[0.12em] text-grey40 dark:text-nightMuted">Revenue</span>
                                                                            <span className="text-sm font-bold text-grey10 dark:text-white">
                                                                                {cur}{ticketRevenue.toFixed(0)}
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                    {!isUsher && (
                                                                        <div className="flex justify-between items-center">
                                                                            <span className="text-xs font-bold uppercase tracking-[0.12em] text-grey40 dark:text-nightMuted">Service Fee</span>
                                                                            <span className="text-sm font-bold text-[#FF5C6C]">
                                                                                {cur}{ticketFees.toFixed(0)}
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                    <div className="flex justify-between items-center">
                                                                        <span className="text-xs font-bold uppercase tracking-[0.12em] text-grey40 dark:text-nightMuted">Check_ins</span>
                                                                        <span className="text-sm font-bold text-grey10 dark:text-white">
                                                                            {checkInCount}/{soldCount}
                                                                        </span>
                                                                    </div>
                                                                    <div className="flex justify-between items-center">
                                                                        <span className="text-xs font-bold uppercase tracking-[0.12em] text-grey40 dark:text-nightMuted">Walk_ins</span>
                                                                        <span className="text-sm font-bold text-grey10 dark:text-white">
                                                                            {walkIns}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                                <span className={`${ecPill} mt-4 ${isSoldOut ? 'bg-purple-100 text-purple-700 dark:bg-purple-500/15 dark:text-purple-300' : 'bg-primary/10 text-primary dark:bg-nightAccent/15 dark:text-nightAccent'}`}>
                                                                    {isSoldOut ? 'Sold Out' : 'On Sale'}
                                                                </span>
                                                            </div>
                                                        );
                                                    })
                                                ) : (
                                                    <div className="rounded-[28px] bg-grey99 dark:bg-white/[0.04] text-center text-grey40 dark:text-nightMuted py-12">
                                                        No tickets available
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    )}
                                </div>
                            )}
                            {currentActiveTab === 'Orders' && (
                                <>
                                <div className="p-0 lg:p-0 bg-transparent lg:bg-white lg:dark:bg-white/[0.04] lg:rounded-[28px] lg:ring-1 lg:ring-black/[0.06] lg:dark:ring-white/10">
                                    {/* Mobile-only toolbar: search + scan QR + more actions */}
                                    <div className="lg:hidden flex items-center gap-2 pb-3">
                                        <div className="relative flex-1 min-w-0">
                                            <span className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none text-grey50 dark:text-nightMuted">
                                                <Search width={16} height={16} />
                                            </span>
                                            <input
                                                type="text"
                                                placeholder="Name, email, order ID"
                                                className="w-full pl-11 pr-4 h-12 rounded-2xl border-0 bg-grey99 ring-1 ring-black/[0.04] dark:bg-white/[0.06] dark:ring-white/10 text-grey10 dark:text-white placeholder:text-grey60 dark:placeholder:text-nightMuted text-base focus:outline-none focus:ring-2 focus:ring-primary/50 dark:focus:ring-nightAccent/60"
                                                value={search}
                                                onChange={e => setSearch(e.target.value)}
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setGlobalQRScannerOpen(true)}
                                            className={`${ecIconBtn} h-12 w-12`}
                                            aria-label="Scan QR"
                                        >
                                            <Scan width={18} height={18} />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setMobileOrdersActionsOpen(true)}
                                            className={`${ecIconBtn} h-12 w-12`}
                                            aria-label="More actions"
                                        >
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                                                <circle cx="12" cy="5" r="2" />
                                                <circle cx="12" cy="12" r="2" />
                                                <circle cx="12" cy="19" r="2" />
                                            </svg>
                                        </button>
                                    </div>
                                    {/* Mobile-only filter/sort row */}
                                    <div className="lg:hidden flex flex-wrap items-center gap-2 pb-4">
                                        <div className="relative order-ticket-filter">
                                            <button
                                                type="button"
                                                onClick={() => { setTicketFilterOpen(v => !v); setOrderSortOpen(false); }}
                                                className="flex h-10 items-center gap-2 rounded-full bg-grey99 px-4 text-sm font-semibold text-grey20 transition-colors hover:bg-grey95 dark:bg-white/[0.06] dark:text-nightTextSecondary dark:hover:bg-white/10 whitespace-nowrap"
                                            >
                                                <svg width="16" height="16" fill="none" viewBox="0 0 16 16">
                                                    <path d="M1.5 6.5V4.667A1.167 1.167 0 012.667 3.5h10.666A1.167 1.167 0 0114.5 4.667V6.5a1.167 1.167 0 000 2.333v1.834a1.167 1.167 0 01-1.167 1.166H2.667A1.167 1.167 0 011.5 10.667V8.833a1.167 1.167 0 000-2.333z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
                                                    <path d="M6.5 3.5v9" stroke="currentColor" strokeWidth="1.3" strokeDasharray="1.5 1.5" />
                                                </svg>
                                                {ticketFilter === 'all' ? 'Filter by Ticket' : ((eventData?.tickets || []).find((t: any) => String(t._id) === String(ticketFilter))?.name || 'Filter by Ticket')}
                                            </button>
                                            {ticketFilterOpen && (
                                                <div className={`absolute left-0 top-12 z-20 min-w-[200px] max-h-64 overflow-y-auto ${ecMenu}`}>
                                                    <button
                                                        onClick={() => { setTicketFilter('all'); setTicketFilterOpen(false); }}
                                                        className={`w-full text-left rounded-xl px-3 py-2 text-sm hover:bg-grey99 dark:hover:bg-white/10 whitespace-nowrap ${ticketFilter === 'all' ? 'text-primary dark:text-nightAccent font-semibold' : 'text-grey20 dark:text-nightTextSecondary'}`}
                                                    >
                                                        All tickets
                                                    </button>
                                                    {(eventData?.tickets || []).map((t: any) => (
                                                        <button
                                                            key={t._id}
                                                            onClick={() => { setTicketFilter(t._id); setTicketFilterOpen(false); }}
                                                            className={`w-full text-left rounded-xl px-3 py-2 text-sm hover:bg-grey99 dark:hover:bg-white/10 whitespace-nowrap ${String(ticketFilter) === String(t._id) ? 'text-primary dark:text-nightAccent font-semibold' : 'text-grey20 dark:text-nightTextSecondary'}`}
                                                        >
                                                            {t.name}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <div className="relative order-sort-filter">
                                            <button
                                                type="button"
                                                onClick={() => { setOrderSortOpen(v => !v); setTicketFilterOpen(false); }}
                                                className="flex h-10 items-center gap-2 rounded-full bg-grey99 px-4 text-sm font-semibold text-grey20 transition-colors hover:bg-grey95 dark:bg-white/[0.06] dark:text-nightTextSecondary dark:hover:bg-white/10 whitespace-nowrap"
                                            >
                                                <svg width="16" height="16" fill="none" viewBox="0 0 16 16">
                                                    <path d="M4 2v9m0 0L1.5 8.5M4 11l2.5-2.5M12 14V5m0 0l2.5 2.5M12 5L9.5 7.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                                {orderSort === 'newest' ? 'New to Old' : 'Old to New'}
                                            </button>
                                            {orderSortOpen && (
                                                <div className={`absolute left-0 top-12 z-20 min-w-[160px] ${ecMenu}`}>
                                                    <button
                                                        onClick={() => { setOrderSort('newest'); setOrderSortOpen(false); }}
                                                        className={`w-full text-left rounded-xl px-3 py-2 text-sm hover:bg-grey99 dark:hover:bg-white/10 whitespace-nowrap ${orderSort === 'newest' ? 'text-primary dark:text-nightAccent font-semibold' : 'text-grey20 dark:text-nightTextSecondary'}`}
                                                    >
                                                        New to old
                                                    </button>
                                                    <button
                                                        onClick={() => { setOrderSort('oldest'); setOrderSortOpen(false); }}
                                                        className={`w-full text-left rounded-xl px-3 py-2 text-sm hover:bg-grey99 dark:hover:bg-white/10 whitespace-nowrap ${orderSort === 'oldest' ? 'text-primary dark:text-nightAccent font-semibold' : 'text-grey20 dark:text-nightTextSecondary'}`}
                                                    >
                                                        Old to new
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    {/* Mobile-only Actions bottom sheet */}
                                    {mobileOrdersActionsOpen && (
                                        <div className="lg:hidden fixed inset-0 z-[100000]">
                                            <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOrdersActionsOpen(false)} />
                                            <div className="absolute left-0 right-0 bottom-0 bg-white dark:bg-nightSurface rounded-t-[28px] ring-1 ring-black/[0.06] dark:ring-white/10 px-5 pt-6 pb-8">
                                                <div className="flex items-center justify-between mb-4">
                                                    <h3 className="text-2xl font-bold tracking-[-0.03em] text-grey10 dark:text-white">Actions</h3>
                                                    <button
                                                        type="button"
                                                        onClick={() => setMobileOrdersActionsOpen(false)}
                                                        className={ecIconBtn}
                                                        aria-label="Close"
                                                    >
                                                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                                            <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                                        </svg>
                                                    </button>
                                                </div>
                                                <div className="flex flex-col">
                                                    <RoleBasedAccess
                                                        eventOrganizerId={eventData?.organizerId}
                                                        eventUserId={eventData?.userId}
                                                        requiredAccess="full"
                                                        fallback={null}
                                                    >
                                                        <button
                                                            type="button"
                                                            className="w-full flex items-center gap-3 rounded-2xl px-3 text-grey10 dark:text-nightTextSecondary py-3 text-base font-semibold text-left transition-colors hover:bg-grey99 dark:hover:bg-white/[0.06]"
                                                            onClick={() => {
                                                                setMobileOrdersActionsOpen(false);
                                                                setWalkInModalOpen(true);
                                                                setWalkInErrors({ customerName: '', customerEmail: '', customerPhone: '' });
                                                            }}
                                                        >
                                                            <Plus width={18} height={18} />
                                                            Add Walk-In
                                                        </button>
                                                        <button
                                                            type="button"
                                                            disabled={(eventData?.tickets || []).length === 0}
                                                            className="w-full flex items-center gap-3 rounded-2xl px-3 text-grey10 dark:text-nightTextSecondary py-3 text-base font-semibold text-left transition-colors hover:bg-grey99 dark:hover:bg-white/[0.06] disabled:opacity-40"
                                                            onClick={() => {
                                                                setMobileOrdersActionsOpen(false);
                                                                setBulkFreeTicketsModalOpen(true);
                                                            }}
                                                        >
                                                            <Plus width={18} height={18} />
                                                            Add Guest
                                                        </button>
                                                        <button
                                                            type="button"
                                                            disabled={getEligibleOrdersForBulkEmail().length === 0}
                                                            className="w-full flex items-center gap-3 rounded-2xl px-3 text-grey10 dark:text-nightTextSecondary py-3 text-base font-semibold text-left transition-colors hover:bg-grey99 dark:hover:bg-white/[0.06] disabled:opacity-40"
                                                            onClick={() => {
                                                                setMobileOrdersActionsOpen(false);
                                                                setBulkEmailModalOpen(true);
                                                            }}
                                                        >
                                                            <Mail width={18} height={18} />
                                                            Send Bulk Email{getEligibleOrdersForBulkEmail().length > 0 ? ` (${getEligibleOrdersForBulkEmail().length})` : ''}
                                                        </button>
                                                    </RoleBasedAccess>
                                                    <button
                                                        type="button"
                                                        disabled={filteredOrders.length === 0 || createContactListMutation.isPending}
                                                        className="w-full flex items-center gap-3 rounded-2xl px-3 text-grey10 dark:text-nightTextSecondary py-3 text-base font-semibold text-left transition-colors hover:bg-grey99 dark:hover:bg-white/[0.06] disabled:opacity-40"
                                                        onClick={() => {
                                                            setMobileOrdersActionsOpen(false);
                                                            handleCreateContactList();
                                                        }}
                                                    >
                                                        <Users width={18} height={18} />
                                                        Create Contact List
                                                    </button>
                                                    <button
                                                        type="button"
                                                        disabled={filteredOrders.length === 0}
                                                        className="w-full flex items-center gap-3 rounded-2xl px-3 text-grey10 dark:text-nightTextSecondary py-3 text-base font-semibold text-left transition-colors hover:bg-grey99 dark:hover:bg-white/[0.06] disabled:opacity-40"
                                                        onClick={() => {
                                                            setMobileOrdersActionsOpen(false);
                                                            handleExportOrdersToCSV();
                                                        }}
                                                    >
                                                        <FileDown width={18} height={18} />
                                                        Export CSV
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="w-full flex items-center gap-3 rounded-2xl px-3 text-grey10 dark:text-nightTextSecondary py-3 text-base font-semibold text-left transition-colors hover:bg-grey99 dark:hover:bg-white/[0.06]"
                                                        onClick={() => {
                                                            setMobileOrdersActionsOpen(false);
                                                            setToast('Order import is not available yet');
                                                            setTimeout(() => setToast(''), 3000);
                                                        }}
                                                    >
                                                        <FileUp width={18} height={18} />
                                                        Import CSV
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    <div className="hidden lg:flex lg:items-center justify-between gap-3 px-6 pt-6 pb-5 border-b border-black/[0.06] dark:border-white/10">
                                        <RoleBasedAccess
                                            eventOrganizerId={eventData?.organizerId}
                                            eventUserId={eventData?.userId}
                                            requiredAccess="full"
                                            fallback={null}
                                        >
                                            <button
                                                onClick={() => setBulkEmailModalOpen(true)}
                                                disabled={getEligibleOrdersForBulkEmail().length === 0}
                                                className="inline-flex h-10 items-center justify-center gap-2 px-5 rounded-full bg-grey10 text-white dark:bg-white dark:text-nightBg text-sm font-semibold hover:bg-black dark:hover:bg-grey90 transition disabled:opacity-50 disabled:cursor-not-allowed w-fit"
                                            >
                                                <Mail width={16} height={16} />
                                                Send Email{getEligibleOrdersForBulkEmail().length > 0 ? ` (${getEligibleOrdersForBulkEmail().length})` : ''}
                                            </button>
                                        </RoleBasedAccess>
                                        <div className="flex flex-wrap items-center gap-2">
                                            <button
                                                onClick={() => setGlobalQRScannerOpen(true)}
                                                className="inline-flex h-10 items-center gap-2 px-4 rounded-full ring-1 ring-grey90 dark:ring-white/15 text-grey10 dark:text-white bg-transparent text-sm font-semibold hover:bg-grey99 dark:hover:bg-white/[0.06] transition"
                                            >
                                                <Scan width={16} height={16} />
                                                Scan QR
                                            </button>
                                            <RoleBasedAccess
                                                eventOrganizerId={eventData?.organizerId}
                                                eventUserId={eventData?.userId}
                                                requiredAccess="full"
                                                fallback={null}
                                            >
                                                <button
                                                    onClick={() => {
                                                        setWalkInModalOpen(true);
                                                        setWalkInErrors({ customerName: '', customerEmail: '', customerPhone: '' });
                                                    }}
                                                    className="inline-flex h-10 items-center gap-2 px-4 rounded-full ring-1 ring-grey90 dark:ring-white/15 text-grey10 dark:text-white bg-transparent text-sm font-semibold hover:bg-grey99 dark:hover:bg-white/[0.06] transition"
                                                >
                                                    <Plus color="currentColor" width={16} height={16} />
                                                    Add Walk-In
                                                </button>
                                                <button
                                                    onClick={() => setBulkFreeTicketsModalOpen(true)}
                                                    disabled={(eventData?.tickets || []).length === 0}
                                                    className="inline-flex h-10 items-center gap-2 px-4 rounded-full ring-1 ring-grey90 dark:ring-white/15 text-grey10 dark:text-white bg-transparent text-sm font-semibold hover:bg-grey99 dark:hover:bg-white/[0.06] transition disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    <Plus color="currentColor" width={16} height={16} />
                                                    Add Guest
                                                </button>
                                            </RoleBasedAccess>
                                            <button
                                                onClick={handleCreateContactList}
                                                disabled={filteredOrders.length === 0 || createContactListMutation.isPending}
                                                className="inline-flex h-10 items-center gap-2 px-4 rounded-full ring-1 ring-grey90 dark:ring-white/15 text-grey10 dark:text-white bg-transparent text-sm font-semibold hover:bg-grey99 dark:hover:bg-white/[0.06] transition disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {createContactListMutation.isPending ? (
                                                    <>
                                                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                        Creating...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Users width={16} height={16} />
                                                        Create Contact List
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                    <div className="bg-transparent">
                                        {/* Search + filter/sort/export bar (desktop only — mobile has its own toolbar above) */}
                                        <div className="hidden lg:flex lg:items-center justify-between gap-3 px-6 pt-5 pb-5 border-b border-black/[0.06] dark:border-white/10">
                                            <div className="relative w-full lg:w-[320px] shrink-0">
                                                <span className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none text-grey50 dark:text-nightMuted">
                                                    <Search width={18} height={18} />
                                                </span>
                                                <input
                                                    type="text"
                                                    placeholder="Search name, email, order ID"
                                                    className="w-full pl-11 pr-4 h-11 rounded-2xl border-0 bg-grey99 ring-1 ring-black/[0.04] text-grey10 placeholder:text-grey60 dark:bg-white/[0.06] dark:ring-white/10 dark:text-white dark:placeholder:text-nightMuted text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 dark:focus:ring-nightAccent/60"
                                                    value={search}
                                                    onChange={e => setSearch(e.target.value)}
                                                />
                                            </div>
                                            <div className="flex flex-wrap items-center gap-2">
                                                {/* Filter by ticket */}
                                                <div className="relative order-ticket-filter">
                                                    <button
                                                        type="button"
                                                        onClick={() => { setTicketFilterOpen(v => !v); setOrderSortOpen(false); }}
                                                        className="flex h-10 items-center gap-2 rounded-full bg-grey99 px-4 text-sm font-semibold text-grey20 transition-colors hover:bg-grey95 dark:bg-white/[0.06] dark:text-nightTextSecondary dark:hover:bg-white/10 whitespace-nowrap"
                                                    >
                                                        <svg width="16" height="16" fill="none" viewBox="0 0 16 16">
                                                            <path d="M1.5 6.5V4.667A1.167 1.167 0 012.667 3.5h10.666A1.167 1.167 0 0114.5 4.667V6.5a1.167 1.167 0 000 2.333v1.834a1.167 1.167 0 01-1.167 1.166H2.667A1.167 1.167 0 011.5 10.667V8.833a1.167 1.167 0 000-2.333z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
                                                            <path d="M6.5 3.5v9" stroke="currentColor" strokeWidth="1.3" strokeDasharray="1.5 1.5" />
                                                        </svg>
                                                        {ticketFilter === 'all' ? 'Filter by Ticket' : ((eventData?.tickets || []).find((t: any) => String(t._id) === String(ticketFilter))?.name || 'Filter by Ticket')}
                                                    </button>
                                                    {ticketFilterOpen && (
                                                        <div className={`absolute right-0 lg:left-0 top-12 z-20 min-w-[200px] max-h-64 overflow-y-auto ${ecMenu}`}>
                                                            <button
                                                                onClick={() => { setTicketFilter('all'); setTicketFilterOpen(false); }}
                                                                className={`w-full text-left rounded-xl px-3 py-2 text-sm hover:bg-grey99 dark:hover:bg-white/10 whitespace-nowrap ${ticketFilter === 'all' ? 'text-primary dark:text-nightAccent font-semibold' : 'text-grey20 dark:text-nightTextSecondary'}`}
                                                            >
                                                                All tickets
                                                            </button>
                                                            {(eventData?.tickets || []).map((t: any) => (
                                                                <button
                                                                    key={t._id}
                                                                    onClick={() => { setTicketFilter(t._id); setTicketFilterOpen(false); }}
                                                                    className={`w-full text-left rounded-xl px-3 py-2 text-sm hover:bg-grey99 dark:hover:bg-white/10 whitespace-nowrap ${String(ticketFilter) === String(t._id) ? 'text-primary dark:text-nightAccent font-semibold' : 'text-grey20 dark:text-nightTextSecondary'}`}
                                                                >
                                                                    {t.name}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                                {/* Sort by date */}
                                                <div className="relative order-sort-filter">
                                                    <button
                                                        type="button"
                                                        onClick={() => { setOrderSortOpen(v => !v); setTicketFilterOpen(false); }}
                                                        className="flex h-10 items-center gap-2 rounded-full bg-grey99 px-4 text-sm font-semibold text-grey20 transition-colors hover:bg-grey95 dark:bg-white/[0.06] dark:text-nightTextSecondary dark:hover:bg-white/10 whitespace-nowrap"
                                                    >
                                                        <svg width="16" height="16" fill="none" viewBox="0 0 16 16">
                                                            <path d="M4 2v9m0 0L1.5 8.5M4 11l2.5-2.5M12 14V5m0 0l2.5 2.5M12 5L9.5 7.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                        {orderSort === 'newest' ? 'New to Old' : 'Old to New'}
                                                    </button>
                                                    {orderSortOpen && (
                                                        <div className={`absolute right-0 top-12 z-20 min-w-[160px] ${ecMenu}`}>
                                                            <button
                                                                onClick={() => { setOrderSort('newest'); setOrderSortOpen(false); }}
                                                                className={`w-full text-left rounded-xl px-3 py-2 text-sm hover:bg-grey99 dark:hover:bg-white/10 whitespace-nowrap ${orderSort === 'newest' ? 'text-primary dark:text-nightAccent font-semibold' : 'text-grey20 dark:text-nightTextSecondary'}`}
                                                            >
                                                                New to old
                                                            </button>
                                                            <button
                                                                onClick={() => { setOrderSort('oldest'); setOrderSortOpen(false); }}
                                                                className={`w-full text-left rounded-xl px-3 py-2 text-sm hover:bg-grey99 dark:hover:bg-white/10 whitespace-nowrap ${orderSort === 'oldest' ? 'text-primary dark:text-nightAccent font-semibold' : 'text-grey20 dark:text-nightTextSecondary'}`}
                                                            >
                                                                Old to new
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                                <button
                                                    onClick={handleExportOrdersToCSV}
                                                    disabled={filteredOrders.length === 0}
                                                    className="flex h-10 items-center gap-2 rounded-full bg-grey99 px-4 text-sm font-semibold text-grey20 transition-colors hover:bg-grey95 dark:bg-white/[0.06] dark:text-nightTextSecondary dark:hover:bg-white/10 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    <FileDown width={16} height={16} /> Export CSV
                                                </button>
                                                <button
                                                    onClick={() => { setToast('Order import is not available yet'); setTimeout(() => setToast(''), 3000); }}
                                                    className="flex h-10 items-center gap-2 rounded-full bg-grey99 px-4 text-sm font-semibold text-grey20 transition-colors hover:bg-grey95 dark:bg-white/[0.06] dark:text-nightTextSecondary dark:hover:bg-white/10 whitespace-nowrap"
                                                >
                                                    <FileUp width={16} height={16} /> Import CSV
                                                </button>
                                                <button
                                                    onClick={handleExportOrdersToPDF}
                                                    disabled={filteredOrders.length === 0}
                                                    className="flex h-10 items-center gap-2 rounded-full bg-grey99 px-4 text-sm font-semibold text-grey20 transition-colors hover:bg-grey95 dark:bg-white/[0.06] dark:text-nightTextSecondary dark:hover:bg-white/10 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    <FileDown width={16} height={16} /> Export PDF
                                                </button>
                                            </div>
                                        </div>

                                        {/* Desktop Table View (hidden on mobile) - div-based "semi table" so the body can bleed full-width while the header keeps its own padding */}
                                        <div className="hidden lg:block text-sm">
                                            <div className="grid px-2 grid-cols-[110px_1.4fr_1.2fr_1.6fr_0.7fr_120px_100px] bg-grey99 dark:bg-white/[0.04] text-grey50 dark:text-nightMuted font-bold text-xs uppercase tracking-[0.12em]">
                                                <div className="py-3 px-4 text-left">Order ID</div>
                                                <div className="py-3 px-4 text-left">Buyer</div>
                                                <div className="py-3 px-4 text-left">Date</div>
                                                <div className="py-3 px-4 text-left">Tickets</div>
                                                <div className="py-3 px-4 text-left">Price</div>
                                                <div className="py-3 px-4 text-left">Status</div>
                                                <div className="py-3 px-4 text-left">Actions</div>
                                            </div>
                                            <div>
                                                {paginatedOrders.map((order, idx) => {
                                                    const isExpanded = expandedOrderIdx === idx;
                                                    const isMenuOpen = openActionMenuIdx === idx;
                                                    const buyerEmail = order.customerEmail || order.guestEmail || order.email;
                                                    const ticketSummary = Array.isArray(order.tickets)
                                                        ? (() => {
                                                            const ticketCounts: { [key: string]: number } = {};
                                                            order.tickets.forEach((t: any) => {
                                                                const ticketName = t.ticketId?.name || 'Ticket';
                                                                ticketCounts[ticketName] = (ticketCounts[ticketName] || 0) + 1;
                                                            });
                                                            return Object.entries(ticketCounts)
                                                                .map(([name, count]) => `x${count} ${name}`)
                                                                .join(', ');
                                                        })()
                                                        : '';
                                                    return (
                                                        <React.Fragment key={order._id}>
                                                            <div className="grid pl-2 pr-2 grid-cols-[110px_1.4fr_1.2fr_1.6fr_0.7fr_120px_100px] items-center border-t border-black/[0.06] dark:border-white/10 transition-colors hover:bg-grey99/60 dark:hover:bg-white/[0.02]">
                                                                <div className="py-4 px-4 font-mono text-xs font-semibold text-grey20 dark:text-nightTextSecondary">#{order.hashedUniqueId || order.orderId}</div>
                                                                <div className="py-4 px-4 min-w-0">
                                                                    <div className="font-semibold text-grey10 dark:text-white truncate">{order.customerName || order.guestName || order.attendeeName || 'N/A'}</div>
                                                                    {buyerEmail && <div className="text-xs text-grey50 dark:text-nightMuted truncate">{buyerEmail}</div>}
                                                                </div>
                                                                <div className="py-4 px-4 text-grey20 dark:text-nightTextSecondary">{new Date(order.createdAt).toLocaleString()}</div>
                                                                <div className="py-4 px-4 text-grey20 dark:text-nightTextSecondary min-w-0">
                                                                    <div className="truncate">{ticketSummary}</div>
                                                                    {getTicketAttendeeLines(order).map((line, i) => (
                                                                        <div key={i} className="text-xs text-grey50 dark:text-nightMuted truncate" title={line}>{line}</div>
                                                                    ))}
                                                                </div>
                                                                <div className="py-4 px-4 font-semibold text-grey10 dark:text-white">
                                                                    {order.totalAmount
                                                                        ? `${cur}${getOrderPayoutBreakdown(order).net.toFixed(2)}`
                                                                        : `${cur}0`
                                                                    }
                                                                </div>
                                                                <div className="py-4 px-4">{getStatusBadge(getOrderStatus(order))}</div>
                                                                <div className="py-4 px-4 relative flex items-center justify-end gap-1">
                                                                    <RoleBasedAccess
                                                                        eventOrganizerId={eventData?.organizerId}
                                                                        eventUserId={eventData?.userId}
                                                                        requiredAccess="full"
                                                                        fallback={null}
                                                                    >
                                                                        <button
                                                                            className="flex items-center justify-center w-9 h-9 rounded-full bg-grey99 hover:bg-grey95 dark:bg-white/[0.06] dark:hover:bg-white/10 focus:bg-grey95 dark:focus:bg-white/10 transition border-none outline-none"
                                                                            style={{ boxShadow: isMenuOpen ? '0 2px 8px rgba(0,0,0,0.08)' : undefined }}
                                                                            onClick={() => setOpenActionMenuIdx(isMenuOpen ? null : idx)}
                                                                            title="Actions"
                                                                        >
                                                                            <svg width="20" height="20" fill="none" viewBox="0 0 20 20">
                                                                                <circle cx="10" cy="10" r="1.5" fill={isOrderEligibleForRefund(order) ? "#009688" : "#666"} />
                                                                                <circle cx="10" cy="5" r="1.5" fill={isOrderEligibleForRefund(order) ? "#009688" : "#666"} />
                                                                                <circle cx="10" cy="15" r="1.5" fill={isOrderEligibleForRefund(order) ? "#009688" : "#666"} />
                                                                            </svg>
                                                                        </button>
                                                                    </RoleBasedAccess>
                                                                    {isMenuOpen && (
                                                                        <div className={`absolute right-0 top-12 z-50 min-w-[170px] flex flex-col animate-fade-in ${ecMenu}`}>
                                                                            <RoleBasedAccess
                                                                                eventOrganizerId={eventData?.organizerId}
                                                                                eventUserId={eventData?.userId}
                                                                                requiredAccess="full"
                                                                                fallback={null}
                                                                            >
                                                                                <button
                                                                                    className="w-full flex items-center gap-2 text-[#009688] dark:text-nightAccent hover:bg-primary99 dark:hover:bg-nightAccent/10 rounded-xl px-3 py-2 text-sm font-semibold transition whitespace-nowrap"
                                                                                    onClick={() => {
                                                                                        console.log('Event data:', event);
                                                                                        console.log('Event title:', event?.title);
                                                                                        console.log('Event data title:', event?.data?.title);
                                                                                        setSelectedOrderForEmail(order);
                                                                                        setEmailModalOpen(true);
                                                                                        setOpenActionMenuIdx(null);
                                                                                    }}
                                                                                >
                                                                                    <svg width="16" height="16" fill="none" viewBox="0 0 16 16">
                                                                                        <path d="M2 4a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V4z" stroke="#009688" strokeWidth="1.5" fill="none" />
                                                                                        <path d="M6 6h4M6 8h4M6 10h2" stroke="#009688" strokeWidth="1.5" strokeLinecap="round" />
                                                                                    </svg>
                                                                                    Send Email
                                                                                </button>
                                                                                <button
                                                                                    className="w-full flex items-center gap-2 text-[#009688] dark:text-nightAccent hover:bg-primary99 dark:hover:bg-nightAccent/10 rounded-xl px-3 py-2 text-sm font-semibold transition whitespace-nowrap"
                                                                                    onClick={() => {
                                                                                        resendOrderEmailMutation.mutate(order);
                                                                                        setOpenActionMenuIdx(null);
                                                                                    }}
                                                                                >
                                                                                    <svg width="16" height="16" fill="none" viewBox="0 0 16 16">
                                                                                        <path d="M13 8a5 5 0 10-1.2 3.2" stroke="#009688" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                                                        <path d="M13 4v4h-4" stroke="#009688" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                                                    </svg>
                                                                                    Resend Ticket
                                                                                </button>
                                                                                <button
                                                                                    className="w-full flex items-center gap-2 text-[#009688] dark:text-nightAccent hover:bg-primary99 dark:hover:bg-nightAccent/10 rounded-xl px-3 py-2 text-sm font-semibold transition whitespace-nowrap"
                                                                                    onClick={() => {
                                                                                        openInfoModal(order);
                                                                                        setOpenActionMenuIdx(null);
                                                                                    }}
                                                                                >
                                                                                    <svg width="16" height="16" fill="none" viewBox="0 0 16 16">
                                                                                        <path d="M11 2l3 3-8 8H3v-3l8-8z" stroke="#009688" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                                                    </svg>
                                                                                    Edit Order Info
                                                                                </button>
                                                                                <button
                                                                                    className="w-full flex items-center gap-2 text-[#009688] dark:text-nightAccent hover:bg-primary99 dark:hover:bg-nightAccent/10 rounded-xl px-3 py-2 text-sm font-semibold transition whitespace-nowrap"
                                                                                    onClick={() => handleStatusChangeClick(order)}
                                                                                >
                                                                                    <svg width="16" height="16" fill="none" viewBox="0 0 16 16">
                                                                                        <path d="M12 2L9 5M9 5L12 8M9 5H4C3.44772 5 3 5.44772 3 6V14C3 14.5523 3.44772 15 4 15H12C12.5523 15 13 14.5523 13 14V6C13 5.44772 12.5523 5 12 5H9Z" stroke="#009688" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                                                    </svg>
                                                                                    Change Status
                                                                                </button>
                                                                            </RoleBasedAccess>
                                                                            {isOrderEligibleForRefund(order) && (
                                                                                <button
                                                                                    className="w-full flex items-center gap-2 text-[#009688] dark:text-nightAccent hover:bg-primary99 dark:hover:bg-nightAccent/10 rounded-xl px-3 py-2 text-sm font-semibold transition whitespace-nowrap"
                                                                                    onClick={() => handleRefundClick(order)}
                                                                                >
                                                                                    <svg width="16" height="16" fill="none" viewBox="0 0 16 16"><path d="M8 3v10M3 8h10" stroke="#009688" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                                                                    Refund
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                    <button
                                                                        className={`flex items-center justify-center w-9 h-9 ml-1 rounded-full hover:bg-grey99 dark:hover:bg-white/10 transition ${isExpanded ? 'rotate-180' : ''}`}

                                                                        onClick={() => setExpandedOrderIdx(isExpanded ? null : idx)}
                                                                        aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
                                                                    >
                                                                        <svg width="16" height="16" fill="none" viewBox="0 0 16 16"><path d="M4 6l4 4 4-4" stroke="#009688" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                                                    </button>
                                                                </div>
                                                            </div>
                                                            {isExpanded && (
                                                                <div className="mx-4 mb-4 rounded-[22px] bg-grey99 px-5 dark:bg-white/[0.04]">
                                                                    <div className="py-5 space-y-4">
                                                                        {/* Contact Info */}
                                                                        <div className="flex flex-row flex-wrap gap-6 text-sm">
                                                                            <div className="flex items-center">
                                                                                <span className="text-grey40 dark:text-nightMuted font-semibold mr-2 min-w-[60px]">Email:</span>
                                                                                <a
                                                                                    href={`mailto:${order.customerEmail || order.guestEmail || order.email || ''}`}
                                                                                    className="transition break-all text-grey10 hover:text-primary dark:text-nightTextSecondary dark:hover:text-nightAccent"
                                                                                >
                                                                                    {order.customerEmail || order.guestEmail || order.email || 'N/A'}
                                                                                </a>
                                                                            </div>
                                                                            <div className="flex items-center">
                                                                                <span className="text-grey40 dark:text-nightMuted font-semibold mr-2 min-w-[60px]">Phone:</span>
                                                                                <a
                                                                                    href={`tel:${order.customerPhone || order.guestPhone || order.phone || ''}`}
                                                                                    className="transition text-grey10 hover:text-primary dark:text-nightTextSecondary dark:hover:text-nightAccent"
                                                                                >
                                                                                    {order.customerPhone || order.guestPhone || order.phone || ''}
                                                                                </a>
                                                                            </div>
                                                                        </div>

                                                                        {!isUsher && (() => {
                                                                            const payout = getOrderPayoutBreakdown(order);
                                                                            return (
                                                                                <div className="max-w-sm space-y-1 text-sm">
                                                                                    <div className="flex justify-between text-grey10 dark:text-nightTextSecondary"><span>Order Total</span><span>{cur}{payout.total.toFixed(2)}</span></div>
                                                                                    <div className="flex justify-between text-[#C62828] dark:text-red-400"><span>{order.feeRateLabel || 'Service Fee'}</span><span>-{cur}{(payout.platformFee + payout.tax + payout.stripeFee).toFixed(2)}</span></div>
                                                                                    <div className="flex justify-between font-bold text-grey10 dark:text-white border-t border-black/[0.06] dark:border-white/10 pt-2 mt-1"><span>Your Payout</span><span>{cur}{payout.net.toFixed(2)}</span></div>
                                                                                </div>
                                                                            );
                                                                        })()}

                                                                        {/* Individual Tickets */}
                                                                        <div>
                                                                            <h4 className="text-xs font-bold uppercase tracking-[0.14em] text-grey40 dark:text-nightMuted mb-3">Tickets</h4>
                                                                            <div className="space-y-2">
                                                                                {Array.isArray(order.tickets) && order.tickets.map((ticket: any, ticketIdx: number) => (
                                                                                    <div key={ticketIdx} className="flex items-center justify-between gap-3 p-4 bg-white ring-1 ring-black/[0.04] dark:bg-white/[0.04] dark:ring-white/[0.06] rounded-2xl">
                                                                                        <div className="flex-1 min-w-0">
                                                                                            <div className="text-sm font-semibold text-grey10 dark:text-white">
                                                                                                {ticket.ticketId?.name || 'Ticket'}
                                                                                            </div>
                                                                                                {(ticket.attendeeName || ticket.attendeeEmail || ticket.attendeePhone) && (
                                                                                                <div className="text-xs text-grey40 dark:text-nightMuted mt-0.5 break-words">
                                                                                                {[ticket.attendeeName, ticket.attendeeEmail, ticket.attendeePhone].filter(Boolean).join(' · ')}
                                                                                            </div>
                                                            )}
                                                                                            {ticket.seatData && (
                                                                                <div className="text-xs text-grey40 dark:text-nightMuted mt-0.5">
                                                                                    {[ticket.seatData.zone && `Section ${ticket.seatData.zone}`, ticket.seatData.row && `Row ${ticket.seatData.row}`, ticket.seatData.seat && `Seat ${ticket.seatData.seat}`, ticket.seatData.tier && `Tier ${ticket.seatData.tier}`].filter(Boolean).join(' · ')}
                                                                                </div>
                                                                            )}
                                                                                            {/* <div className="text-xs text-[#666]">
                                                                                        {cur}{ticket.price}
                                                                                    </div> */}
                                                                                            <div className="text-xs text-grey50 dark:text-nightMuted mt-1 break-all">
                                                                                                <span className="font-mono text-grey20 dark:text-nightTextSecondary">
                                                                                                    {ticket.qrCode || ticket.ticketId?.qrCode || 'No QR code available'}
                                                                                                </span>
                                                                                            </div>
                                                                                        </div>
                                                                                        {ticket.checkedInAt ? (
                                                                                            <div className="flex flex-shrink-0 items-center gap-2 px-3 h-8 bg-green-50 dark:bg-green-500/10 text-green-700 dark:text-green-400 rounded-full text-xs font-semibold">
                                                                                                <svg width="12" height="12" fill="none" viewBox="0 0 16 16">
                                                                                                    <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z" fill="currentColor" />
                                                                                                </svg>
                                                                                                Checked-in
                                                                                            </div>
                                                                                        ) : order.status === 'refunded' ? (
                                                                                            <div className="flex flex-shrink-0 items-center gap-2 px-3 h-8 bg-grey95 dark:bg-white/[0.06] text-grey40 dark:text-nightMuted rounded-full text-xs font-semibold">
                                                                                                <svg width="12" height="12" fill="none" viewBox="0 0 16 16">
                                                                                                    <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                                                                </svg>
                                                                                                Refunded
                                                                                            </div>
                                                                                        ) : (

                                                                                            <button
                                                                                                onClick={() => {
                                                                                                    setSelectedTicketForCheckIn(ticket);
                                                                                                    setSelectedOrderForTicketCheckIn(order);
                                                                                                    setTicketCheckInModalOpen(true);
                                                                                                }}
                                                                                                className="flex flex-shrink-0 items-center gap-2 px-4 h-8 bg-primary text-white rounded-full text-xs font-semibold hover:bg-primary30 transition dark:bg-nightAccent dark:text-nightBg dark:hover:bg-primary70"
                                                                                            >
                                                                                                <svg width="12" height="12" fill="none" viewBox="0 0 16 16">
                                                                                                    <path d="M12 2L9 5M9 5L12 8M9 5H4C3.44772 5 3 5.44772 3 6V14C3 14.5523 3.44772 15 4 15H12C12.5523 15 13 14.5523 13 14V6C13 5.44772 12.5523 5 12 5H9Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                                                                </svg>
                                                                                                Check-in
                                                                                            </button>

                                                                                        )}
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        {/* Mobile Card View (hidden on desktop) */}
                                        <div className="lg:hidden space-y-3">
                                            {paginatedOrders.length === 0 ? (
                                                <div className="rounded-[28px] bg-grey99 dark:bg-white/[0.04] px-6 py-14 text-center">
                                                    <div className="text-xl font-bold tracking-[-0.02em] text-grey10 dark:text-white mb-2">No orders found</div>
                                                    {search && (
                                                        <div className="text-sm text-grey40 dark:text-nightMuted">
                                                            Try adjusting your search criteria
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                paginatedOrders.map((order, idx) => {
                                                    const isMenuOpen = openActionMenuIdx === idx;
                                                    const isExpanded = mobileExpandedOrderIdx === idx;
                                                    const ticketSummary = Array.isArray(order.tickets)
                                                        ? (() => {
                                                            const ticketCounts: { [key: string]: number } = {};
                                                            order.tickets.forEach((t: any) => {
                                                                const ticketName = t.ticketId?.name || 'Ticket';
                                                                ticketCounts[ticketName] = (ticketCounts[ticketName] || 0) + 1;
                                                            });
                                                            return Object.entries(ticketCounts)
                                                                .map(([name, count]) => `x${count} ${name}`)
                                                                .join(', ');
                                                        })()
                                                        : '';
                                                    const status = getOrderStatus(order);
                                                    const statusStyle: Record<string, string> = {
                                                        checked_in: 'bg-[#E6F7EC] text-[#1E7F43] dark:bg-[#1F3B2A] dark:text-[#4ADE80]',
                                                        pending: 'bg-[#FFF3E6] text-[#FF9800] dark:bg-[#3B2E1F] dark:text-[#FFB74D]',
                                                        completed: 'bg-[#E6F6F4] text-primary40 dark:bg-[#1F3B38] dark:text-[#3DD9C7]',
                                                        cancelled: 'bg-[#FDEAEA] text-[#D93025] dark:bg-[#3B1F1F] dark:text-[#F87171]',
                                                        refunded: 'bg-[#FDEAEA] text-[#D93025] dark:bg-[#3B1F1F] dark:text-[#F87171]',
                                                        'pending-refund': 'bg-[#FFF3E6] text-[#FF9800] dark:bg-[#3B2E1F] dark:text-[#FFB74D]',
                                                        disputed: 'bg-[#FDEAEA] text-[#D93025] dark:bg-[#3B1F1F] dark:text-[#F87171]',
                                                        failed: 'bg-[#FFF3E6] text-[#FF9800] dark:bg-[#3B2E1F] dark:text-[#FFB74D]',
                                                        payment_failed: 'bg-[#FFF3E6] text-[#FF9800] dark:bg-[#3B2E1F] dark:text-[#FFB74D]',
                                                    };
                                                    const statusLabel: Record<string, string> = {
                                                        checked_in: 'Checked-in',
                                                        pending: 'Pending',
                                                        completed: 'Confirmed',
                                                        cancelled: 'Canceled',
                                                        refunded: 'Refunded',
                                                        'pending-refund': 'Pending Refund',
                                                        disputed: 'Disputed',
                                                        failed: 'Payment Failed',
                                                        payment_failed: 'Payment Failed',
                                                    };

                                                    return (
                                                        <div key={order._id} className="rounded-[24px] bg-grey99 ring-1 ring-black/[0.04] dark:bg-white/[0.04] dark:ring-white/[0.06] p-5">
                                                            {/* Order Header */}
                                                            <div className="flex items-start justify-between gap-2 mb-2">
                                                                <div className="flex-1 min-w-0">
                                                                    <h3 className="text-base font-bold tracking-[-0.01em] text-grey10 dark:text-white truncate">
                                                                        {order.customerName || order.guestName || order.attendeeName || 'N/A'}
                                                                    </h3>
                                                                    <div className="text-xs text-grey50 dark:text-nightMuted truncate">
                                                                        {order.customerEmail || order.guestEmail || order.email || 'N/A'}
                                                                    </div>
                                                                </div>
                                                                <div className="relative flex-shrink-0 mobile-order-menu-container">
                                                                    <RoleBasedAccess
                                                                        eventOrganizerId={eventData?.organizerId}
                                                                        eventUserId={eventData?.userId}
                                                                        requiredAccess="full"
                                                                        fallback={null}
                                                                    >
                                                                        <button
                                                                            className="flex items-center justify-center w-9 h-9 rounded-full bg-white ring-1 ring-black/[0.06] dark:bg-white/[0.06] dark:ring-white/10 transition border-none outline-none"
                                                                            onClick={() => setOpenActionMenuIdx(isMenuOpen ? null : idx)}
                                                                            title="Actions"
                                                                        >
                                                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" className="text-grey10 dark:text-nightTextSecondary">
                                                                                <circle cx="12" cy="5" r="2" />
                                                                                <circle cx="12" cy="12" r="2" />
                                                                                <circle cx="12" cy="19" r="2" />
                                                                            </svg>
                                                                        </button>
                                                                    </RoleBasedAccess>
                                                                    {isMenuOpen && (
                                                                        <div className={`absolute right-0 top-11 z-50 min-w-[170px] flex flex-col ${ecMenu}`}>
                                                                            <RoleBasedAccess
                                                                                eventOrganizerId={eventData?.organizerId}
                                                                                eventUserId={eventData?.userId}
                                                                                requiredAccess="full"
                                                                                fallback={null}
                                                                            >
                                                                                <button
                                                                                    className="w-full flex items-center gap-2 text-[#009688] dark:text-nightAccent hover:bg-primary99 dark:hover:bg-nightAccent/10 rounded-xl px-3 py-2 text-sm font-semibold transition whitespace-nowrap"
                                                                                    onClick={() => {
                                                                                        setSelectedOrderForEmail(order);
                                                                                        setEmailModalOpen(true);
                                                                                        setOpenActionMenuIdx(null);
                                                                                    }}
                                                                                >
                                                                                    <svg width="16" height="16" fill="none" viewBox="0 0 16 16">
                                                                                        <path d="M2 4a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V4z" stroke="#009688" strokeWidth="1.5" fill="none" />
                                                                                        <path d="M6 6h4M6 8h4M6 10h2" stroke="#009688" strokeWidth="1.5" strokeLinecap="round" />
                                                                                    </svg>
                                                                                    Send Email
                                                                                </button>
                                                                                <button
                                                                                    className="w-full flex items-center gap-2 text-[#009688] dark:text-nightAccent hover:bg-primary99 dark:hover:bg-nightAccent/10 rounded-xl px-3 py-2 text-sm font-semibold transition whitespace-nowrap"
                                                                                    onClick={() => {
                                                                                        resendOrderEmailMutation.mutate(order);
                                                                                        setOpenActionMenuIdx(null);
                                                                                    }}
                                                                                >
                                                                                    <svg width="16" height="16" fill="none" viewBox="0 0 16 16">
                                                                                        <path d="M13 8a5 5 0 10-1.2 3.2" stroke="#009688" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                                                        <path d="M13 4v4h-4" stroke="#009688" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                                                    </svg>
                                                                                    Resend Ticket
                                                                                </button>
                                                                                <button
                                                                                    className="w-full flex items-center gap-2 text-[#009688] dark:text-nightAccent hover:bg-primary99 dark:hover:bg-nightAccent/10 rounded-xl px-3 py-2 text-sm font-semibold transition whitespace-nowrap"
                                                                                    onClick={() => {
                                                                                        openInfoModal(order);
                                                                                        setOpenActionMenuIdx(null);
                                                                                    }}
                                                                                >
                                                                                    <svg width="16" height="16" fill="none" viewBox="0 0 16 16">
                                                                                        <path d="M11 2l3 3-8 8H3v-3l8-8z" stroke="#009688" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                                                    </svg>
                                                                                    Edit Order Info
                                                                                </button>
                                                                                <button
                                                                                    className="w-full flex items-center gap-2 text-[#009688] dark:text-nightAccent hover:bg-primary99 dark:hover:bg-nightAccent/10 rounded-xl px-3 py-2 text-sm font-semibold transition whitespace-nowrap"
                                                                                    onClick={() => handleStatusChangeClick(order)}
                                                                                >
                                                                                    <svg width="16" height="16" fill="none" viewBox="0 0 16 16">
                                                                                        <path d="M12 2L9 5M9 5L12 8M9 5H4C3.44772 5 3 5.44772 3 6V14C3 14.5523 3.44772 15 4 15H12C12.5523 15 13 14.5523 13 14V6C13 5.44772 12.5523 5 12 5H9Z" stroke="#009688" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                                                    </svg>
                                                                                    Change Status
                                                                                </button>
                                                                            </RoleBasedAccess>
                                                                            {isOrderEligibleForRefund(order) && (
                                                                                <button
                                                                                    className="w-full flex items-center gap-2 text-[#009688] dark:text-nightAccent hover:bg-primary99 dark:hover:bg-nightAccent/10 rounded-xl px-3 py-2 text-sm font-semibold transition whitespace-nowrap"
                                                                                    onClick={() => handleRefundClick(order)}
                                                                                >
                                                                                    <svg width="16" height="16" fill="none" viewBox="0 0 16 16">
                                                                                        <path d="M8 3v10M3 8h10" stroke="#009688" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                                                    </svg>
                                                                                    Refund
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            <div className="text-xs text-grey50 dark:text-nightMuted mb-3">
                                                                {new Date(order.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - {new Date(order.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                            </div>

                                                            {/* Ticket summary (tap to expand) */}
                                                            <button
                                                                type="button"
                                                                className="flex items-center gap-2 w-full mb-3"
                                                                onClick={() => setMobileExpandedOrderIdx(isExpanded ? null : idx)}
                                                            >
                                                                <span className="text-sm text-grey20 dark:text-nightTextSecondary truncate">{ticketSummary}</span>
                                                                <ChevronDown className={`w-4 h-4 text-grey50 dark:text-nightMuted flex-shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                                                {order.totalAmount && (
                                                                    <span className="text-base text-grey10 dark:text-white font-bold ml-auto flex-shrink-0">
                                                                        {cur}{getOrderPayoutBreakdown(order).net.toFixed(2)}
                                                                    </span>
                                                                )}
                                                            </button>
                                                            {getTicketAttendeeLines(order).map((line, i) => (
                                                                <div key={i} className="text-xs text-grey50 dark:text-nightMuted -mt-2 mb-2 break-words">{line}</div>
                                                            ))}

                                                            <div className="flex items-center justify-between">
                                                                <span className="font-mono text-xs font-semibold text-grey40 dark:text-nightMuted">#{order.hashedUniqueId || order.orderId}</span>
                                                                <span className={`inline-flex items-center h-7 px-3 rounded-full text-xs font-semibold ${statusStyle[status] || 'bg-black/5 text-gray-600 dark:bg-white/10 dark:text-gray-300'}`}>
                                                                    {statusLabel[status] || status}
                                                                </span>
                                                            </div>

                                                            {isExpanded && (
                                                                <div className="mt-4 pt-4 border-t border-black/[0.06] dark:border-white/10">
                                                                    <div className="space-y-2 text-xs text-grey40 dark:text-nightMuted mb-4">
                                                                        <div className="flex justify-between">
                                                                            <span>Email:</span>
                                                                            <a href={`mailto:${order.customerEmail || order.guestEmail || order.email || ''}`} className="text-grey10 dark:text-nightTextSecondary font-semibold break-all text-xs">
                                                                                {order.customerEmail || order.guestEmail || order.email || 'N/A'}
                                                                            </a>
                                                                        </div>
                                                                        <div className="flex justify-between">
                                                                            <span>Phone:</span>
                                                                            <a href={`tel:${order.customerPhone || order.guestPhone || order.phone || ''}`} className="text-grey10 dark:text-nightTextSecondary font-semibold text-xs">
                                                                                {order.customerPhone || order.guestPhone || order.phone || '+1 461 87234'}
                                                                            </a>
                                                                        </div>
                                                                    </div>
                                                                    {!isUsher && (() => {
                                                                        const payout = getOrderPayoutBreakdown(order);
                                                                        return (
                                                                            <div className="space-y-2 text-xs text-grey40 dark:text-nightMuted mb-4">
                                                                                <div className="flex justify-between"><span>Order Total:</span><span className="text-grey10 dark:text-nightTextSecondary font-semibold">{cur}{payout.total.toFixed(2)}</span></div>
                                                                                <div className="flex justify-between"><span>{order.feeRateLabel || 'Service Fee'}:</span><span className="text-[#FF5C6C] font-medium">-{cur}{(payout.platformFee + payout.tax + payout.stripeFee).toFixed(2)}</span></div>
                                                                                <div className="flex justify-between border-t border-black/[0.06] dark:border-white/10 pt-2"><span className="text-grey10 dark:text-white font-bold">Your Payout:</span><span className="text-grey10 dark:text-white font-bold">{cur}{payout.net.toFixed(2)}</span></div>
                                                                            </div>
                                                                        );
                                                                    })()}
                                                                    <h4 className="text-xs font-bold uppercase tracking-[0.14em] text-grey40 dark:text-nightMuted mb-3">Tickets</h4>
                                                                    <div className="space-y-2">
                                                                        {Array.isArray(order.tickets) && order.tickets.map((ticket: any, ticketIdx: number) => (
                                                                            <div key={ticketIdx} className="flex items-center justify-between gap-3 p-3 bg-white ring-1 ring-black/[0.04] dark:bg-white/[0.04] dark:ring-white/[0.06] rounded-2xl">
                                                                                <div className="flex-1 min-w-0">
                                                                                    <div className="text-sm font-semibold text-grey10 dark:text-white">
                                                                                        {ticket.ticketId?.name || 'Ticket'}
                                                                                    </div>
                                                                                    {(ticket.attendeeName || ticket.attendeeEmail || ticket.attendeePhone) && (
                                                                                        <div className="text-xs text-grey40 dark:text-nightMuted mt-0.5 break-words">
                                                                                        {[ticket.attendeeName, ticket.attendeeEmail, ticket.attendeePhone].filter(Boolean).join(' · ')}
                                                                                        </div>
                                                                                    )}
                                                                                    {ticket.seatData && (
                                                                                <div className="text-xs text-grey40 dark:text-nightMuted mt-0.5">
                                                                                    {[ticket.seatData.zone && `Section ${ticket.seatData.zone}`, ticket.seatData.row && `Row ${ticket.seatData.row}`, ticket.seatData.seat && `Seat ${ticket.seatData.seat}`, ticket.seatData.tier && `Tier ${ticket.seatData.tier}`].filter(Boolean).join(' · ')}
                                                                                </div>
                                                                            )}
                                                                                    <div className="text-xs text-grey50 dark:text-nightMuted mt-1 break-all">
                                                                                        <span className="font-mono text-grey20 dark:text-nightTextSecondary">
                                                                                            {ticket.qrCode || ticket.ticketId?.qrCode || 'No QR code available'}
                                                                                        </span>
                                                                                    </div>
                                                                                </div>
                                                                                {ticket.checkedInAt ? (
                                                                                    <div className="flex flex-shrink-0 items-center gap-2 px-3 h-8 bg-green-50 text-green-700 dark:bg-green-500/10 dark:text-green-400 rounded-full text-xs font-semibold">
                                                                                        <svg width="12" height="12" fill="none" viewBox="0 0 16 16">
                                                                                            <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z" fill="currentColor" />
                                                                                        </svg>
                                                                                        Checked-in
                                                                                    </div>
                                                                                ) : order.status === 'refunded' ? (
                                                                                    <div className="flex flex-shrink-0 items-center gap-2 px-3 h-8 bg-grey95 text-grey40 dark:bg-white/[0.06] dark:text-nightMuted rounded-full text-xs font-semibold">
                                                                                        <svg width="12" height="12" fill="none" viewBox="0 0 16 16">
                                                                                            <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                                                        </svg>
                                                                                        Refunded
                                                                                    </div>
                                                                                ) : (
                                                                                    <button
                                                                                        onClick={() => {
                                                                                            setSelectedTicketForCheckIn(ticket);
                                                                                            setSelectedOrderForTicketCheckIn(order);
                                                                                            setTicketCheckInModalOpen(true);
                                                                                        }}
                                                                                        className="flex flex-shrink-0 items-center gap-2 px-4 h-8 bg-primary text-white rounded-full text-xs font-semibold hover:bg-primary30 transition dark:bg-nightAccent dark:text-nightBg dark:hover:bg-primary70"
                                                                                    >
                                                                                        <svg width="12" height="12" fill="none" viewBox="0 0 16 16">
                                                                                            <path d="M12 2L9 5M9 5L12 8M9 5H4C3.44772 5 3 5.44772 3 6V14C3 14.5523 3.44772 15 4 15H12C12.5523 15 13 14.5523 13 14V6C13 5.44772 12.5523 5 12 5H9Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                                                        </svg>
                                                                                        Check-in
                                                                                    </button>
                                                                                )}
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })
                                            )}
                                        </div>

                                        {/* Mobile Pagination */}
                                        <div className="mt-6 pb-4 lg:hidden">
                                            <div>
                                                {/* Rows per page selector and info */}
                                                <div className="flex flex-col gap-3 mb-4">
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-sm font-medium text-grey40 dark:text-nightMuted">Show:</span>
                                                            <Select classNames={{ trigger: 'h-10 px-3 text-sm' }} aria-label="Select" selectedKeys={[String(rowsPerPage ?? '')]} onChange={e => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                                                                <SelectItem key={String(10)}>10</SelectItem>
                                                                <SelectItem key={String(20)}>20</SelectItem>
                                                                <SelectItem key={String(50)}>50</SelectItem>
                                                            </Select>
                                                            <span className="text-sm font-medium text-grey40 dark:text-nightMuted">entries</span>
                                                        </div>
                                                    </div>
                                                    <div className="text-sm text-grey40 dark:text-nightMuted text-center">
                                                        Showing {(currentPage - 1) * rowsPerPage + 1} to {Math.min(currentPage * rowsPerPage, totalOrders)} of {totalOrders} results
                                                    </div>
                                                </div>

                                                {/* Navigation buttons */}
                                                <div className="flex items-center justify-between mb-4">
                                                    <button
                                                        className={`px-4 h-10 rounded-full text-sm font-semibold transition-colors min-w-[80px] ${currentPage === 1
                                                            ? 'text-grey70 dark:text-nightMuted bg-grey99 dark:bg-white/[0.04] cursor-not-allowed'
                                                            : 'text-grey10 dark:text-white ring-1 ring-grey90 dark:ring-white/15 hover:bg-grey99 dark:hover:bg-white/[0.06]'
                                                            }`}
                                                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                                        disabled={currentPage === 1}
                                                    >
                                                        Previous
                                                    </button>

                                                    <div className="flex items-center gap-1">
                                                        {/* First page */}
                                                        {currentPage > 3 && (
                                                            <>
                                                                <button
                                                                    className="w-10 h-10 flex items-center justify-center rounded-full bg-grey99 text-grey10 dark:bg-white/[0.06] dark:text-nightTextSecondary text-sm font-semibold hover:bg-grey95 dark:hover:bg-white/10 transition-colors"
                                                                    onClick={() => setCurrentPage(1)}
                                                                >
                                                                    1
                                                                </button>
                                                                {currentPage > 4 && (
                                                                    <span className="px-1 text-grey40 dark:text-nightMuted text-xs">...</span>
                                                                )}
                                                            </>
                                                        )}

                                                        {/* Current page and neighbors */}
                                                        {(() => {
                                                            let startPage = Math.max(1, currentPage - 1);
                                                            let endPage = Math.min(totalPages, currentPage + 1);

                                                            // Adjust for edge cases
                                                            if (currentPage === 1) {
                                                                endPage = Math.min(totalPages, 3);
                                                            } else if (currentPage === totalPages) {
                                                                startPage = Math.max(1, totalPages - 2);
                                                            }

                                                            const pages = [];
                                                            for (let i = startPage; i <= endPage; i++) {
                                                                pages.push(i);
                                                            }

                                                            return pages.map(pageNum => (
                                                                <button
                                                                    key={pageNum}
                                                                    className={`w-10 h-10 flex items-center justify-center rounded-full text-sm font-semibold transition-colors ${pageNum === currentPage
                                                                        ? 'bg-grey10 text-white dark:bg-white dark:text-nightBg'
                                                                        : 'bg-grey99 text-grey10 dark:bg-white/[0.06] dark:text-nightTextSecondary hover:bg-grey95 dark:hover:bg-white/10'
                                                                        }`}
                                                                    onClick={() => setCurrentPage(pageNum)}
                                                                >
                                                                    {pageNum}
                                                                </button>
                                                            ));
                                                        })()}

                                                        {/* Last page */}
                                                        {currentPage < totalPages - 2 && (
                                                            <>
                                                                {currentPage < totalPages - 3 && (
                                                                    <span className="px-1 text-grey40 dark:text-nightMuted text-xs">...</span>
                                                                )}
                                                                <button
                                                                    className="w-10 h-10 flex items-center justify-center rounded-full bg-grey99 text-grey10 dark:bg-white/[0.06] dark:text-nightTextSecondary text-sm font-semibold hover:bg-grey95 dark:hover:bg-white/10 transition-colors"
                                                                    onClick={() => setCurrentPage(totalPages)}
                                                                >
                                                                    {totalPages}
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>

                                                    <button
                                                        className={`px-4 h-10 rounded-full text-sm font-semibold transition-colors min-w-[80px] ${currentPage === totalPages
                                                            ? 'text-grey70 dark:text-nightMuted bg-grey99 dark:bg-white/[0.04] cursor-not-allowed'
                                                            : 'text-grey10 dark:text-white ring-1 ring-grey90 dark:ring-white/15 hover:bg-grey99 dark:hover:bg-white/[0.06]'
                                                            }`}
                                                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                                        disabled={currentPage === totalPages}
                                                    >
                                                        Next
                                                    </button>
                                                </div>

                                                {/* Go to page input */}
                                                <div className="flex items-center justify-center gap-2">
                                                    <span className="text-sm font-medium text-grey40 dark:text-nightMuted">Go to:</span>
                                                    <input
                                                        type="number"
                                                        className="w-16 h-10 rounded-2xl bg-grey99 ring-1 ring-black/[0.04] dark:bg-white/[0.06] dark:ring-white/10 text-grey10 dark:text-white px-2 text-sm text-center focus:outline-none focus:ring-2 focus:ring-primary/50"
                                                        min={1}
                                                        max={totalPages}
                                                        value={currentPage}
                                                        onChange={e => setCurrentPage(Math.max(1, Math.min(totalPages, Number(e.target.value))))}
                                                    />
                                                    <button
                                                        className="px-5 h-10 rounded-full bg-primary text-white font-semibold text-sm hover:bg-primary30 transition-colors dark:bg-nightAccent dark:text-nightBg dark:hover:bg-primary70"
                                                        onClick={() => setCurrentPage(currentPage)}
                                                    >
                                                        Go
                                                    </button>
                                                </div>

                                                {/* Mobile pagination info */}
                                                {totalPages > 1 && (
                                                    <div className="text-center text-xs text-grey40 dark:text-nightMuted mt-2">
                                                        Page {currentPage} of {totalPages}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Desktop Pagination — outside the orders card */}
                                <div className="hidden lg:flex items-center justify-end mt-4">
                                    <div className="flex items-center gap-1 h-11 px-1.5 rounded-full bg-grey99 ring-1 ring-black/[0.04] dark:bg-white/[0.06] dark:ring-white/[0.06]">
                                        <button
                                            className="w-8 h-8 flex items-center justify-center rounded-full text-grey10 dark:text-nightTextSecondary disabled:opacity-30 hover:bg-white dark:hover:bg-white/10 transition-colors"
                                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                            disabled={currentPage === 1}
                                            aria-label="Previous page"
                                        >
                                            <svg width="14" height="14" fill="none" viewBox="0 0 16 16"><path d="M10 12L6 8l4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                        </button>
                                        <span className="text-sm font-semibold text-grey10 dark:text-white px-2">{currentPage}/{totalPages}</span>
                                        <button
                                            className="w-8 h-8 flex items-center justify-center rounded-full text-grey10 dark:text-nightTextSecondary disabled:opacity-30 hover:bg-white dark:hover:bg-white/10 transition-colors"
                                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                            disabled={currentPage === totalPages}
                                            aria-label="Next page"
                                        >
                                            <svg width="14" height="14" fill="none" viewBox="0 0 16 16"><path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                        </button>
                                    </div>
                                </div>
                                </>
                            )}
                            {currentActiveTab === 'Info' && (
                                <div className="pt-0 pb-8 flex flex-col gap-5">
                                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-2">
                                        <div className={ecH2}>Edit Event</div>
                                        <button onClick={() => handleSave()} disabled={loading} className={`hidden lg:inline-flex flex-shrink-0 ${ecBtnAccent} h-12 px-6`}>{loading ? 'Saving...' : 'Save Changes'}</button>
                                    </div>
                                    {error && <div className="rounded-2xl bg-red-50 px-4 py-3 text-sm font-medium text-red-600 dark:bg-red-500/10 dark:text-red-400">{error}</div>}

                                    <InfoSection
                                        icon={<InfoIcon width={18} height={18} />}
                                        title="Basic Info"
                                        subtitle="Name, category & description"
                                    >
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                                            <div className="md:col-span-2">
                                                <label className="block text-sm font-semibold text-grey20 dark:text-nightTextSecondary mb-2">What's your event name?</label>
                                                <Input className="w-full mb-2" classNames={ecInputCls} placeholder="e.g. Summer Soirée: A Night of Music and Fun" value={info.name} onChange={e => handleInfoChange('name', e.target.value)} />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-semibold text-grey20 dark:text-nightTextSecondary mb-2">Custom Event URL Handle</label>
                                                <Input
                                                    className={`w-full mb-2 ${handleValidation.isAvailable === false ? 'border-red-500' :
                                                        handleValidation.isAvailable === true ? 'border-green-500' : ''
                                                        }`}
                                                    classNames={ecInputCls}
                                                    placeholder="e.g. summer-soiree-2024"
                                                    value={info.handle}
                                                    onChange={e => handleInfoChange('handle', e.target.value)}
                                                />
                                                <p className="text-xs text-grey40 dark:text-nightMuted mb-2">This will create a custom URL for your event: eventup.ai/e/{info.handle || 'your-handle'}</p>
                                                {handleValidation.message && (
                                                    <p className={`text-xs mb-2 ${handleValidation.isAvailable === true ? 'text-green-600 dark:text-green-400' :
                                                        handleValidation.isAvailable === false ? 'text-red-600 dark:text-red-400' :
                                                            'text-gray-500 dark:text-nightMuted'
                                                        }`}>
                                                        {handleValidation.isValidating && <span className="animate-spin inline-block w-3 h-3 border border-gray-300 dark:border-nightBorder border-t-gray-600 dark:border-t-nightMuted rounded-full mr-1"></span>}
                                                        {handleValidation.message}
                                                    </p>
                                                )}
                                            </div>

                                            <div>
                                                <label className="block text-sm font-semibold text-grey20 dark:text-nightTextSecondary mb-2">Category</label>
                                                <Select
                                                    aria-label="Category"
                                                    placeholder="Select"
                                                    classNames={ecSelectCls}
                                                    selectedKeys={info.category ? [info.category] : []}
                                                    onSelectionChange={keys => { const v = Array.from(keys)[0]; if (v) handleInfoChange('category', String(v)); }}
                                                >
                                                    {[
                                                        { value: 'networking', label: 'Networking' },
                                                        { value: 'business', label: 'Business' },
                                                        { value: 'music', label: 'Music' },
                                                        { value: 'art', label: 'Arts & Culture' },
                                                        { value: 'social', label: 'Social & Community' },
                                                        { value: 'health', label: 'Health & Wellness' },
                                                        { value: 'sports', label: 'Sports & Fitness' },
                                                        { value: 'science', label: 'Science & Tech' },
                                                    ].map(o => <SelectItem key={o.value}>{o.label}</SelectItem>)}
                                                </Select>
                                            </div>

                                        </div>
                                        <div>
                                            <label className="block text-sm font-semibold text-grey20 dark:text-nightTextSecondary mb-2">Describe your event:</label>
                                            <Textarea className="w-full" classNames={ecTextareaCls} rows={3} placeholder="Use this space to provide event details to ensure attendees know what to expect." value={info.description} onChange={e => handleInfoChange('description', e.target.value)} />
                                        </div>
                                    </InfoSection>

                                    <InfoSection
                                        icon={<DollarSign width={18} height={18} />}
                                        title="Refund Policy"
                                    >
                                        <div className="flex flex-col gap-4 md:w-1/2 md:pr-3">
                                            <div className="flex items-center justify-between gap-4 rounded-2xl bg-grey99 ring-1 ring-black/[0.04] dark:bg-white/[0.06] dark:ring-white/10 px-5 py-4">
                                                <div>
                                                    <div className="text-sm font-semibold text-grey10 dark:text-white mb-1">Allow Refunds</div>
                                                    <div className="text-xs text-grey40 dark:text-nightMuted">Refunds will be available until the day you set before the event starts</div>
                                                </div>
                                                <Switch
                                                    isSelected={refundable}
                                                    onValueChange={setRefundable}
                                                    className="accent-primary40"
                                                />
                                            </div>
                                            {refundable && (
                                                <div>
                                                    <label className="block text-sm font-semibold text-grey20 dark:text-nightTextSecondary mb-2">Days before event start</label>
                                                    <Input
                                                        type="number"
                                                        min="0"
                                                        max="365"
                                                        value={refundDaysBeforeStart.toString()}
                                                        isInvalid={refundDaysBeforeStart < 0 || refundDaysBeforeStart.toString() === ''}
                                                        onChange={(value) => {
                                                            setRefundDaysBeforeStart(value.target.value);
                                                        }}
                                                        classNames={{
                                                            inputWrapper: `w-full mb-2 ${ecInputCls.inputWrapper}`,
                                                            input: ecFieldText,
                                                        }}
                                                        placeholder="Enter number of days"
                                                    />
                                                    <div className="text-xs text-grey40 dark:text-nightMuted mt-1">
                                                        {refundDaysBeforeStart === 0
                                                            ? 'Refunds will be available until the event starts'
                                                            : `Refunds will be available up to ${refundDaysBeforeStart} day${refundDaysBeforeStart !== 1 ? 's' : ''} before the event starts`}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </InfoSection>

                                    <InfoSection
                                        icon={<DollarSign width={18} height={18} />}
                                        title="Currency & Fees"
                                    >
                                        <div className="flex flex-col gap-4 md:w-1/2 md:pr-3">
                                            <div>
                                                <label className="block text-sm font-semibold text-grey20 dark:text-nightTextSecondary mb-2">Currency</label>
                                                <Select
                                                    aria-label="Currency"
                                                    classNames={ecSelectCls}
                                                    selectedKeys={[currency]}
                                                    isDisabled={currencyLocked}
                                                    disallowEmptySelection
                                                    onSelectionChange={keys => { const v = Array.from(keys)[0]; if (v) setCurrency(resolveCurrency(String(v))); }}
                                                >
                                                    {CURRENCY_OPTIONS.map(o => <SelectItem key={o.key}>{o.label}</SelectItem>)}
                                                </Select>
                                                <div className="text-xs text-grey40 dark:text-nightMuted mt-1">{currencyLocked ? 'Currency is locked because tickets have already been sold for this event.' : 'Ticket prices are shown and charged in this currency.'}</div>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-semibold text-grey20 dark:text-nightTextSecondary mb-2">Service fee</label>
                                                <Select
                                                    aria-label="Service fee"
                                                    classNames={ecSelectCls}
                                                    selectedKeys={[feeMode]}
                                                    disallowEmptySelection
                                                    onSelectionChange={keys => { const v = Array.from(keys)[0]; if (v) setFeeMode(String(v) as 'added' | 'included'); }}
                                                >
                                                    <SelectItem key="included">Included in ticket price</SelectItem>
                                                    <SelectItem key="added">Added on top of ticket price</SelectItem>
                                                </Select>
                                                <div className="text-xs text-grey40 dark:text-nightMuted mt-1">Choose whether buyers pay the service fee on top of the ticket price or it is taken out of the ticket price.</div>
                                                <div className="text-xs font-semibold text-grey20 dark:text-nightTextSecondary mt-2">{feeMode === 'added' ? 'Service fee charged to buyer: ' : 'Service fee taken from your payout: '}{feeFormula}</div>
                                            </div>
                                        </div>
                                    </InfoSection>

                                    <InfoSection
                                        icon={<Calendar width={18} height={18} />}
                                        title="Date & Time"
                                        subtitle="When your event happens"
                                    >
                                        <p className="text-xs text-grey40 dark:text-nightMuted">The event's timezone is automatically set based on its location.</p>
                                        {info.times.map((time, idx) => {
                                            const startJs = time.startDate && time.startTime
                                                ? new Date(time.startDate.year, time.startDate.month - 1, time.startDate.day, time.startTime.hour, time.startTime.minute)
                                                : null;
                                            const endJs = time.endDate && time.endTime
                                                ? new Date(time.endDate.year, time.endDate.month - 1, time.endDate.day, time.endTime.hour, time.endTime.minute)
                                                : null;
                                            const durationMinutes = startJs && endJs ? Math.round((endJs.getTime() - startJs.getTime()) / 60000) : null;
                                            return (
                                                <div key={idx} className={idx > 0 ? 'pt-6 mt-6 border-t border-black/[0.06] dark:border-white/10' : 'mt-4'}>
                                                    {info.times.length > 1 && (
                                                        <div className="flex items-center justify-between mb-3">
                                                            <span className="text-xs font-bold uppercase tracking-[0.14em] text-grey40 dark:text-nightMuted">Occurrence {idx + 1}</span>
                                                            <button
                                                                type="button"
                                                                onClick={() => removeTime(idx)}
                                                                className="cursor-pointer w-9 h-9 rounded-full flex items-center justify-center bg-red-50 text-red-500 hover:bg-red-100 dark:bg-red-500/10 dark:text-red-400 dark:hover:bg-red-500/20"
                                                            >
                                                                <Trash width={16} height={16} />
                                                            </button>
                                                        </div>
                                                    )}
                                                    <div className="mb-4">
                                                        <label className="block text-sm font-semibold text-grey20 dark:text-nightTextSecondary mb-2">Start</label>
                                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-6">
                                                            <DatePicker
                                                                classNames={ecDateCls}
                                                                value={time.startDate}
                                                                minValue={today(getLocalTimeZone())}
                                                                onChange={date => handleTimeChange(idx, 'startDate', date)}
                                                            />
                                                            <TimeInput
                                                                classNames={ecDateCls}
                                                                hourCycle={12}
                                                                value={time.startTime}
                                                                onChange={timeValue => handleTimeChange(idx, 'startTime', timeValue)}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-semibold text-grey20 dark:text-nightTextSecondary mb-2">End</label>
                                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-6">
                                                            <DatePicker
                                                                classNames={ecDateCls}
                                                                value={time.endDate}
                                                                onChange={date => handleTimeChange(idx, 'endDate', date)}
                                                                minValue={time.startDate}
                                                            />
                                                            <TimeInput
                                                                classNames={ecDateCls}
                                                                hourCycle={12}
                                                                value={time.endTime}
                                                                onChange={timeValue => handleTimeChange(idx, 'endTime', timeValue)}
                                                            />
                                                        </div>
                                                    </div>
                                                    {durationMinutes !== null && durationMinutes >= 0 && (
                                                        <p className="text-xs text-grey40 dark:text-nightMuted mt-3">
                                                            Your event will last <span className="font-semibold text-grey10 dark:text-white">{formatDurationMinutes(durationMinutes)}</span>.
                                                        </p>
                                                    )}
                                                </div>
                                            );
                                        })}
                                        <button
                                            type="button"
                                            onClick={addTime}
                                            className={`${ecBtnGhost} mt-2 w-fit`}
                                        >
                                            + Add another time
                                        </button>
                                    </InfoSection>

                                    <InfoSection
                                        icon={<ImagePlus width={18} height={18} />}
                                        title="Media"
                                        subtitle="Banner, images & video"
                                    >
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div>
                                                <label className="block text-sm font-semibold text-grey20 dark:text-nightTextSecondary mb-2">Event page banner photo <span className="font-normal text-grey50 dark:text-nightMuted">(16:9, Min 1024 x 576, Max 5MB)</span></label>
                                                <label className="w-full aspect-video bg-grey99 ring-1 ring-black/[0.04] dark:bg-white/[0.04] dark:ring-white/[0.06] rounded-[22px] flex flex-col items-center justify-center cursor-pointer relative overflow-hidden transition-colors hover:bg-grey95 dark:hover:bg-white/[0.06]">
                                                    <input type="file" accept="image/*" className="hidden" onChange={handleBannerUpload} />
                                                    {bannerUploading ? (
                                                        <div className="flex flex-col items-center justify-center w-full h-full absolute inset-0 bg-white/80 dark:bg-nightBg/80 z-10">
                                                            <svg className="animate-spin h-6 w-6 text-primary40" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                            </svg>
                                                            <p className="text-sm font-medium text-grey40 dark:text-nightMuted mt-2">Uploading...</p>
                                                        </div>
                                                    ) : bannerToShow ? (
                                                        <img src={bannerToShow} alt="Banner preview" className="absolute inset-0 w-full h-full object-cover rounded-[22px] z-0" />
                                                    ) : (
                                                        <>
                                                            <UploadImagePlaceholder />
                                                            <span className="mt-1 text-sm text-primary dark:text-nightAccent font-semibold">Upload Image</span>
                                                        </>
                                                    )}
                                                    
                                                </label>
                                                {/* Additional Banner Photos */}
                                                {bannerUploadError && <p className="text-xs text-red-500 mt-1">{bannerUploadError}</p>}
                                                <input type="file" accept="image/*" multiple className="hidden" id="additional-banner-upload" onChange={handleAdditionalBannerPhotosUpload} />
                                                <label htmlFor="additional-banner-upload" className="mt-3 inline-flex h-9 cursor-pointer items-center rounded-full bg-primary/10 px-4 text-xs font-semibold text-primary transition-colors hover:bg-primary/15 dark:bg-nightAccent/15 dark:text-nightAccent dark:hover:bg-nightAccent/20">
                                                    {additionalBannerUploading ? 'Uploading...' : '+ Add additional banner photos'}
                                                </label>
                                                {info.additionalBannerPhotoPreviews.length > 0 && (
                                                    <div className="flex flex-wrap gap-2 mt-2">
                                                        {info.additionalBannerPhotoPreviews.map((item, idx) => (
                                                            <div key={idx} className="relative w-[120px] h-[80px] rounded-2xl overflow-hidden ring-1 ring-black/[0.06] dark:ring-white/10 bg-grey99 dark:bg-white/[0.04] flex items-center justify-center">
                                                                <img src={item.link} alt={`Additional Banner ${idx + 1}`} className="w-full h-full object-cover" />
                                                                <button type="button" className="absolute top-1.5 right-1.5 bg-white/90 dark:bg-nightBg/80 rounded-full p-1 hover:bg-white dark:hover:bg-nightBg" onClick={() => handleRemoveAdditionalBannerPhoto(idx)}>
                                                                    <svg width="18" height="18" fill="none" viewBox="0 0 20 20"><path d="M6 6l8 8M6 14L14 6" stroke="#B0B0B0" strokeWidth="2" strokeLinecap="round" /></svg>
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            <div>
                                                {/* YouTube Video Link */}
                                                <div className="mb-6">
                                                    <label className="block text-sm font-semibold text-grey20 dark:text-nightTextSecondary mb-2">YouTube Video Link <span className="font-normal text-grey50 dark:text-nightMuted">(Optional)</span></label>
                                                    <div className="flex gap-2">
                                                        <Input
                                                            className="w-full flex-1"
                                                            classNames={ecInputCls}
                                                            placeholder="https://www.youtube.com/watch?v=..."
                                                            value={info.youtubeVideoLink}
                                                            onChange={e => handleInfoChange('youtubeVideoLink', e.target.value)}
                                                        />
                                                        {info.youtubeVideoLink && (
                                                            <button
                                                                type="button"
                                                                onClick={() => handleInfoChange('youtubeVideoLink', '')}
                                                                className="shrink-0 inline-flex h-12 items-center px-4 text-xs font-semibold text-red-600 bg-red-50 hover:bg-red-100 dark:bg-red-500/10 dark:text-red-400 dark:hover:bg-red-500/20 rounded-full transition-colors"
                                                            >
                                                                Remove link
                                                            </button>
                                                        )}
                                                    </div>
                                                    <p className="text-xs text-grey40 dark:text-nightMuted mt-1">Add a YouTube video link to display in the banner area</p>
                                                </div>
                                                <label className="block text-sm font-semibold text-grey20 dark:text-nightTextSecondary mb-2">Ticket & Social media image <span className="font-normal text-grey50 dark:text-nightMuted">(Min 400 x 400, Max 2MB)</span></label>
                                                <label className="w-full aspect-square max-w-[240px] bg-grey99 ring-1 ring-black/[0.04] dark:bg-white/[0.04] dark:ring-white/[0.06] rounded-[22px] overflow-hidden flex flex-col items-center justify-center cursor-pointer transition-colors hover:bg-grey95 dark:hover:bg-white/[0.06]">
                                                    <input type="file" accept="image/*" className="hidden" onChange={handleSocialImgUpload} />
                                                    {socialImgUploading ? (
                                                        <div className="flex flex-col items-center">
                                                            <svg className="animate-spin h-6 w-6 text-primary40" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                            </svg>
                                                            <p className="text-sm font-medium text-grey40 dark:text-nightMuted mt-2">Uploading...</p>
                                                        </div>
                                                    ) : socialImgToShow ? (
                                                        <img src={socialImgToShow} alt="Social preview" className="w-full h-full object-cover rounded-[22px]" />
                                                    ) : (
                                                        <>
                                                            <UploadImagePlaceholder />
                                                            <span className="mt-1 text-sm text-primary dark:text-nightAccent font-semibold">Upload Image</span>
                                                        </>
                                                    )}
                                                    {socialImgUploadError && <p className="text-xs text-red-500 mt-1">{socialImgUploadError}</p>}
                                                </label>
                                                <input type="file" accept="image/*" multiple className="hidden" id="additional-ticket-upload" onChange={handleAdditionalSocialImagesUpload} />
                                                {/* <label htmlFor="additional-ticket-upload" className="text-xs text-primary40 mt-2 cursor-pointer">+ Add additional images</label> */}
                                                {info.additionalSocialImagePreviews.length > 0 && (
                                                    <div className="flex flex-wrap gap-2 mt-2">
                                                        {info.additionalSocialImagePreviews.map((src, idx) => (
                                                            <div key={idx} className="relative w-[120px] h-[80px] rounded-2xl overflow-hidden ring-1 ring-black/[0.06] dark:ring-white/10 bg-grey99 dark:bg-white/[0.04] flex items-center justify-center">
                                                                <img src={src} alt={`Additional Social ${idx + 1}`} className="w-full h-full object-cover" />
                                                                <button type="button" className="absolute top-1.5 right-1.5 bg-white/90 dark:bg-nightBg/80 rounded-full p-1 hover:bg-white dark:hover:bg-nightBg" onClick={() => handleRemoveAdditionalSocialImage(idx)}>
                                                                    <svg width="18" height="18" fill="none" viewBox="0 0 20 20"><path d="M6 6l8 8M6 14L14 6" stroke="#B0B0B0" strokeWidth="2" strokeLinecap="round" /></svg>
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </InfoSection>

                                    <InfoSection
                                        icon={<MapPin width={18} height={18} />}
                                        title="Location & Venue"
                                        subtitle="Address, venue & directions"
                                    >
                                        <div>
                                            <label className="block text-sm font-semibold text-grey20 dark:text-nightTextSecondary mb-2">Event location: <span className="text-red-500">*</span></label>
                                            {isLoaded && (
                                                <div>
                                                    <Autocomplete onLoad={onLoadAutocomplete} onPlaceChanged={onPlaceChanged}>
                                                        <Input
                                                            className="w-full mb-2"
                                                            classNames={ecInputCls}
                                                            placeholder="Search for a place or address"
                                                            value={addressLoading ? 'Loading address...' : (info.address || '')}
                                                            onChange={e => {
                                                                console.log('Address input changed:', e.target.value);
                                                                handleInfoChange('address', e.target.value);
                                                            }}
                                                            disabled={addressLoading}
                                                        />
                                                    </Autocomplete>

                                                    <div style={{ position: 'relative' }} className="overflow-hidden rounded-[22px] ring-1 ring-black/[0.06] dark:ring-white/10">
                                                        <GoogleMap
                                                            mapContainerStyle={mapContainerStyle}
                                                            zoom={14}
                                                            onLoad={onMapLoad}
                                                            onIdle={handleMapIdle}
                                                            center={{ lat, lng }}
                                                        />

                                                        {/* Custom marker icon centered over the map */}
                                                        <div style={{
                                                            position: 'absolute',
                                                            left: '50%',
                                                            top: '50%',
                                                            transform: 'translate(-50%, -100%)',
                                                            pointerEvents: 'none',
                                                            zIndex: 10
                                                        }}>
                                                            <img src="/marker.png" />
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Venue field with search functionality */}
                                        <div id={EVENT_VENUE_ANCHOR} className="mb-6 scroll-mt-24">
                                            <label className="block text-sm font-semibold text-grey20 dark:text-nightTextSecondary mb-2">Venue name <span className="text-red-500">*</span></label>
                                            <VenuePicker
                                                name={info.venue || ''}
                                                venueId={info.venueId || null}
                                                onChange={applyVenueSelection}
                                            />
                                            {info.venueId && venue?.seatMap && eventData?.venueId && info.venueId !== String(eventData.venueId) && (
                                                <p className="text-xs text-grey40 dark:text-nightMuted mt-1.5">Seat assignments from the previous venue won't carry over.</p>
                                            )}
                                            {eventVenues.length > 0 && (
                                                <div className="mt-3">
                                                    <div className="text-xs font-semibold text-grey40 dark:text-nightMuted mb-2">This event's venues</div>
                                                    <div className="flex flex-wrap gap-2">
                                                        {eventVenues.map(v => {
                                                            const active = String(info.venueId) === v._id;
                                                            return (
                                                                <button
                                                                    key={v._id}
                                                                    type="button"
                                                                    aria-pressed={active}
                                                                    onClick={() => !active && applyVenueSelection({ name: v.name, venueId: v._id, venue: v })}
                                                                    className={`h-9 px-4 rounded-full text-xs font-semibold whitespace-nowrap ring-1 transition-colors ${active ? 'bg-grey10 text-white ring-grey10 dark:bg-white dark:text-nightBg dark:ring-white' : 'text-grey10 ring-grey90 hover:bg-grey99 dark:text-white dark:ring-white/15 dark:hover:bg-white/[0.06]'}`}
                                                                >
                                                                    {v.name}{v._id === eventVenuesResponse?.activeVenueId ? ' · In use' : ''}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-semibold text-grey20 dark:text-nightTextSecondary mb-2">How to get there:</label>
                                            <Textarea className="w-full" classNames={ecTextareaCls} rows={2} placeholder="Use this space to provide event details: venue, parking, accessibility." value={info.howto} onChange={e => handleInfoChange('howto', e.target.value)} />
                                        </div>
                                    </InfoSection>

                                    <InfoSection
                                        id={EVENT_SETTINGS_ANCHOR}
                                        icon={<SettingsIcon width={18} height={18} />}
                                        title="Settings"
                                        subtitle="Privacy, comments & attendee info"
                                    >
                                        <div>
                                            <label className="block text-sm font-semibold text-grey20 dark:text-nightTextSecondary mb-2">Event visibility</label>
                                            <PillChoice
                                                value={info.isPrivate === 1 ? 'private' : 'public'}
                                                onChange={value => handleInfoChange('isPrivate', value === 'private' ? 1 : 0)}
                                                options={[
                                                    { value: 'public', label: 'Public' },
                                                    { value: 'private', label: 'Private' },
                                                ]}
                                            />
                                            <p className="text-xs text-grey40 dark:text-nightMuted mt-2">
                                                {info.isPrivate === 1
                                                    ? 'Only people with the private link to your event will be able to see it.'
                                                    : 'Public events are visible to everyone.'}
                                            </p>
                                        </div>
                                        <div>
                                            <div className="flex flex-col gap-4">
                                                {/* Ticket attendee info mode */}
                                                <div>
                                                    <div className="text-sm font-semibold text-grey20 dark:text-nightTextSecondary mb-1">Attendee info per ticket</div>
                                                    <div className="text-xs text-grey40 dark:text-nightMuted mb-2">Choose whether buyers can/must enter attendee info for each ticket</div>
                                                    <PillChoice
                                                        value={String(info.ticketInfoMode)}
                                                        onChange={value => handleInfoChange('ticketInfoMode', Number(value))}
                                                        options={[
                                                            { value: '0', label: 'Disabled' },
                                                            { value: '1', label: 'Optional' },
                                                            { value: '2', label: 'Required' },
                                                        ]}
                                                    />
                                                </div>

                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    {/* Address Display */}
                                                    <div className="flex items-center justify-between gap-4 rounded-2xl bg-grey99 ring-1 ring-black/[0.04] dark:bg-white/[0.06] dark:ring-white/10 px-5 py-4">
                                                        <div>
                                                            <div className="text-sm font-semibold text-grey10 dark:text-white mb-1">Show only city</div>
                                                            <div className="text-xs text-grey40 dark:text-nightMuted">Show only city name instead of full address to attendees</div>
                                                        </div>
                                                        <Switch
                                                            isSelected={info.showOnlyCity === 1}
                                                            onValueChange={checked => handleInfoChange('showOnlyCity', checked ? 1 : 0)}
                                                            className="accent-primary40"
                                                        />
                                                    </div>

                                                    {/* Disable Comments */}
                                                    <div className="flex items-center justify-between gap-4 rounded-2xl bg-grey99 ring-1 ring-black/[0.04] dark:bg-white/[0.06] dark:ring-white/10 px-5 py-4">
                                                        <div>
                                                            <div className="text-sm font-semibold text-grey10 dark:text-white mb-1">Disable comments</div>
                                                            <div className="text-xs text-grey40 dark:text-nightMuted">Prevent attendees from posting comments on this event</div>
                                                        </div>
                                                        <Switch
                                                            isSelected={info.commentsDisabled === 1}
                                                            onValueChange={checked => handleInfoChange('commentsDisabled', checked ? 1 : 0)}
                                                            className="accent-primary40"
                                                        />
                                                    </div>

                                                    {/* Liability waiver */}
                                                    <div className="flex items-center justify-between gap-4 rounded-2xl bg-grey99 ring-1 ring-black/[0.04] dark:bg-white/[0.06] dark:ring-white/10 px-5 py-4">
                                                    <div>
                                                        <div className="text-sm font-semibold text-grey10 dark:text-white mb-1">Enable liability waiver</div>
                                                        <div className="text-xs text-grey40 dark:text-nightMuted">Attendees must accept the waiver during checkout</div>
                                                    </div>
                                                    <Switch
                                                        isSelected={info.liabilityWaiverEnabled === true}
                                                        onValueChange={(checked) => {
                                                            handleInfoChange('liabilityWaiverEnabled', !!checked);
                                                            if (!checked) {
                                                                handleInfoChange('liabilityWaiverText', '');
                                                            }
                                                        }}
                                                        className="accent-primary40"
                                                    />
                                                    </div>
                                                </div>

                                                {info.liabilityWaiverEnabled && (
                                                    <div className="mt-2">
                                                        <label className="block text-sm font-semibold text-grey20 dark:text-nightTextSecondary mb-2">Liability waiver text <span className="text-red-500">*</span></label>
                                                        <div className="max-h-[240px] overflow-auto rounded-2xl">
                                                            <Textarea
                                                                className="w-full"
                                                                classNames={ecTextareaCls}
                                                                rows={6}
                                                                placeholder="Enter your liability waiver text..."
                                                                value={info.liabilityWaiverText}
                                                                onChange={(e) => handleInfoChange('liabilityWaiverText', e.target.value)}
                                                            />
                                                        </div>
                                                        {!info.liabilityWaiverText.trim() && (
                                                            <div className="text-xs text-red-500 mt-1">Waiver text is required when enabled.</div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </InfoSection>

                                    {/* <InfoSection
                                        icon={<Building2 width={18} height={18} />}
                                        title="Organizer"
                                        subtitle="Who's running this event"
                                    >
                                        <div>
                                            <label className="block text-sm font-semibold mb-2 dark:text-nightTextSecondary">Event Organizer</label>
                                            <div className="bg-[#F9F9F9] rounded p-4">
                                                {eventData?.organizerId ? (
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-10 h-10 bg-primary40 rounded-full flex items-center justify-center">
                                                            <span className="text-white font-semibold text-sm">
                                                                {eventOrganizer.orgName ? eventOrganizer.orgName.charAt(0).toUpperCase() : 'O'}
                                                            </span>
                                                        </div>
                                                        <div>
                                                            <div className="text-sm font-medium text-[#222]">
                                                                {eventOrganizer.orgName || 'Organizer'}
                                                            </div>
                                                            <div className="text-xs text-gray-500">Event organizer</div>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                                                                <span className="text-gray-500 font-semibold text-sm">?</span>
                                                            </div>
                                                            <div>
                                                                <div className="text-sm font-medium text-[#222]">No organizer assigned</div>
                                                                <div className="text-xs text-gray-500">This event doesn't have an organizer yet</div>
                                                            </div>
                                                        </div>
                                                        {user?.organizer && eventData?.userId === user?.userId && (
                                                            <button
                                                                onClick={() => {
                                                                    if (user?.userId) {
                                                                        connectOrganizerMutation.mutate(user.userId);
                                                                    }
                                                                }}
                                                                disabled={connectOrganizerMutation.isPending}
                                                                className="px-4 py-2 bg-primary40 text-white rounded text-sm font-medium hover:bg-[#00796b] disabled:opacity-60 transition"
                                                            >
                                                                {connectOrganizerMutation.isPending ? 'Connecting...' : 'Connect as Organizer'}
                                                            </button>
                                                        )}
                                                        {user?.organizer && eventData?.userId !== user?.userId && (
                                                            <div className="text-xs text-gray-500">
                                                                Only the event owner can connect this event to an organizer
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </InfoSection> */}

                                    {/* Authorized Emails Management */}
                                    {/* <div className="mb-6">
                                    <AuthorizedEmailsManager
                                        eventId={effectiveEventId}
                                        currentAuthorizedEmails={eventData.authorizedEmails || []}
                                        onUpdate={() => {
                                            // Refresh event data after update
                                            queryClient.invalidateQueries({ queryKey: ['organizerEvent', effectiveEventId] });
                                        }}
                                    />
                                </div> */}
                                </div>
                            )}
                            {currentActiveTab === 'Tickets' && (() => {
                                const visibleTickets = tickets.filter(tk => tk._id);
                                return (
                                    <div className="pt-0 pb-8">
                                        <div className="mb-8 hidden lg:block">
                                            <div className={ecH2}>Tickets Listing</div>
                                        </div>

                                        <VenueMapPreview
                                            venueName={venue?.name}
                                            seatMap={venue?.seatMap}
                                            hasVenue={Boolean(eventData.venueId)}
                                            isLoading={Boolean(eventData.venueId) && isVenueLoading}
                                            isError={Boolean(eventData.venueId) && isVenueError}
                                            onRetry={() => refetchVenue()}
                                            canEditVenue={canEdit && venue?.canEdit !== false}
                                            onEditVenue={() => venue?._id && navigate(`/seat-map-editor?venue=${venue._id}&event=${effectiveEventId}&returnTo=${encodeURIComponent(window.location.pathname + window.location.search)}`)}
                                            onSelectVenue={openVenueField}
                                        />
                                        {tierMode && (
                                            <TierPricingPanel
                                                eventId={eventData._id}
                                                userId={user?.email}
                                                tickets={visibleTickets.map(tk => ({ _id: tk._id, name: tk.name, tierPrices: (tk as any).tierPrices, tierIds: (tk as any).tierIds, status: tk.status, saleStart: (eventData.tickets || []).find((et: any) => String(et._id) === String(tk._id))?.saleStart, saleEnd: (eventData.tickets || []).find((et: any) => String(et._id) === String(tk._id))?.saleEnd }))}
                                                currencySymbol={cur}
                                                canEdit={canEdit}
                                                onAddTicket={() => addTicket()}
                                                onEditTicket={(id) => { const idx = tickets.findIndex(t => t._id === id); if (idx >= 0) setOpenSettingsIdx(idx); }}
                                                onDeleteTicket={(id) => { const idx = tickets.findIndex(t => t._id === id); if (idx >= 0) removeTicket(idx); }}
                                            />
                                        )}
                                        {!tierMode && (<>
                                        <button
                                            className="hidden lg:flex flex-col items-start justify-between w-[264px] h-[112px] p-5 rounded-[28px] bg-grey99 ring-1 ring-black/[0.04] dark:bg-white/[0.04] dark:ring-white/[0.06] text-grey10 dark:text-white font-semibold text-sm hover:bg-grey95 dark:hover:bg-white/[0.06] transition-colors mb-10"
                                            onClick={() => addTicket()}
                                        >
                                            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-grey10 text-white dark:bg-white dark:text-nightBg"><Plus width={14} height={14} /></span> Add New Ticket
                                        </button>
                                        <button
                                            className={`lg:hidden w-full mb-8 ${ecBtnAccent} h-12`}
                                            onClick={() => addTicket()}
                                        >
                                            <Plus width={14} height={14} /> Add New Ticket
                                        </button>

                                        <div className="mb-4">
                                            <h3 className="text-xl md:text-2xl font-bold tracking-[-0.02em] text-grey10 dark:text-white">Tickets</h3>
                                        </div>

                                        {visibleTickets.length === 0 ? (
                                            <div className="text-center py-16 rounded-[28px] bg-grey99 dark:bg-white/[0.04]">
                                                <Ticket className="w-10 h-10 mx-auto text-grey60 dark:text-nightMuted mb-3" />
                                                <p className="text-base text-grey40 dark:text-nightMuted">No tickets yet.</p>
                                            </div>
                                        ) : (
                                            <div className="hidden lg:flex flex-col gap-3">
                                                {visibleTickets.map((tk) => {
                                                    const i = tickets.indexOf(tk);
                                                    const rawTicket = eventData?.tickets?.find((t: any) => t._id === tk._id);
                                                    const qtyNum = Number(tk.qty) || 0;
                                                    const remained = rawTicket?.remained ?? qtyNum;
                                                    const sold = Math.max(0, qtyNum - remained);
                                                    const ticketRevenue = tk._id ? calculateTicketRevenue(tk._id) : 0;
                                                    return (
                                                        <div
                                                            key={tk._id || i}
                                                            className={`${ecCard} px-6 py-5 hover:ring-black/[0.12] dark:hover:ring-white/20 transition-shadow cursor-pointer flex flex-wrap sm:flex-nowrap items-center justify-between gap-4`}
                                                            onClick={() => setOpenSettingsIdx(i)}
                                                        >
                                                            <div className="flex items-center gap-2 min-w-0 w-full sm:w-[300px]">
                                                                <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary dark:bg-nightAccent/15 dark:text-nightAccent"><Ticket className="w-5 h-5 flex-shrink-0" /></span>
                                                                <div className="font-bold tracking-[-0.01em] text-grey10 dark:text-white truncate">
                                                                    {tk.name} <span className="font-normal text-grey50 dark:text-nightMuted">. {tk.type === 'Free' ? 'Free' : `${cur}${tk.price}`}</span>
                                                                </div>
                                                            </div>

                                                            <div className="flex items-center gap-1.5 text-sm whitespace-nowrap">
                                                                <span className="text-grey50 dark:text-nightMuted">Sold:</span>
                                                                <span className="font-bold text-grey10 dark:text-white">{sold}<span className="text-grey60 dark:text-nightMuted font-normal">/{qtyNum}</span></span>
                                                            </div>

                                                            <div className="flex items-center gap-1.5 text-sm whitespace-nowrap">
                                                                <span className="text-grey50 dark:text-nightMuted">Revenue:</span>
                                                                <span className="font-bold text-grey10 dark:text-white">{cur}{ticketRevenue.toFixed(0)}</span>
                                                            </div>

                                                            <span className={`${ecPill} w-fit ${tk.status ? 'bg-primary/10 text-primary dark:bg-nightAccent/15 dark:text-nightAccent' : 'bg-red-50 text-red-600 dark:bg-red-500/15 dark:text-red-400'}`}>
                                                                {tk.status ? 'Active' : 'Deactivated'}
                                                            </span>

                                                            <div className="flex items-center gap-2">
                                                                <button
                                                                    className="flex h-10 w-10 items-center justify-center rounded-full bg-red-50 text-red-500 transition-colors hover:bg-red-100 dark:bg-red-500/10 dark:text-red-400 dark:hover:bg-red-500/20 disabled:opacity-40"
                                                                    title="Delete ticket"
                                                                    disabled={deleteTicketMutation.isPending || isDeletingTicket}
                                                                    onClick={(e) => { e.stopPropagation(); removeTicket(i); }}
                                                                >
                                                                    <Trash />

                                                                </button>
                                                                <button
                                                                    className={ecIconBtn}
                                                                    title="Duplicate"
                                                                    onClick={(e) => { e.stopPropagation(); duplicateTicket(i); }}
                                                                >
                                                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                        <path d="M16 8V6C16 5.46957 15.7893 4.96086 15.4142 4.58579C15.0391 4.21071 14.5304 4 14 4H6C5.46957 4 4.96086 4.21071 4.58579 4.58579C4.21071 4.96086 4 5.46957 4 6V14C4 14.5304 4.21071 15.0391 4.58579 15.4142C4.96086 15.7893 5.46957 16 6 16H8M8 10C8 9.46957 8.21071 8.96086 8.58579 8.58579C8.96086 8.21071 9.46957 8 10 8H18C18.5304 8 19.0391 8.21071 19.4142 8.58579C19.7893 8.96086 20 9.46957 20 10V18C20 18.5304 19.7893 19.0391 19.4142 19.4142C19.0391 19.7893 18.5304 20 18 20H10C9.46957 20 8.96086 19.7893 8.58579 19.4142C8.21071 19.0391 8 18.5304 8 18V10Z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" />
                                                                    </svg>

                                                                </button>
                                                                <button
                                                                    className={ecIconBtn}
                                                                    title="Edit"
                                                                    onClick={(e) => { e.stopPropagation(); setOpenSettingsIdx(i); }}
                                                                >
                                                                    <EditIcon />

                                                                </button>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {/* Mobile Card View (hidden on desktop) */}
                                        {visibleTickets.length > 0 && (
                                            <div className="lg:hidden flex flex-col gap-3">
                                                {visibleTickets.map((tk) => {
                                                    const i = tickets.indexOf(tk);
                                                    const rawTicket = eventData?.tickets?.find((t: any) => t._id === tk._id);
                                                    const qtyNum = Number(tk.qty) || 0;
                                                    const remained = rawTicket?.remained ?? qtyNum;
                                                    const sold = Math.max(0, qtyNum - remained);
                                                    const ticketRevenue = tk._id ? calculateTicketRevenue(tk._id) : 0;
                                                    return (
                                                        <div
                                                            key={tk._id || i}
                                                            className="rounded-[24px] bg-grey99 ring-1 ring-black/[0.04] dark:bg-white/[0.04] dark:ring-white/[0.06] p-5"
                                                            onClick={() => setOpenSettingsIdx(i)}
                                                        >
                                                            <div className="flex items-center justify-between gap-2 mb-3">
                                                                <div className="flex items-center gap-2 min-w-0">
                                                                    <Ticket className="w-5 h-5 text-primary dark:text-nightAccent flex-shrink-0" />
                                                                    <div className="text-base font-bold tracking-[-0.01em] text-grey10 dark:text-white truncate">
                                                                        {tk.name} <span className="text-grey50 dark:text-nightMuted font-normal">. {tk.type === 'Free' ? 'Free' : `${cur}${tk.price}`}</span>
                                                                    </div>
                                                                </div>
                                                                <div className="relative flex-shrink-0 mobile-ticket-menu-container">
                                                                    <button
                                                                        type="button"
                                                                        className="flex h-9 w-9 items-center justify-center rounded-full bg-white ring-1 ring-black/[0.06] text-grey10 dark:bg-white/[0.06] dark:ring-white/10 dark:text-nightTextSecondary"
                                                                        onClick={(e) => { e.stopPropagation(); setMobileTicketMenuIdx(v => v === i ? null : i); }}
                                                                        aria-label="Ticket options"
                                                                    >
                                                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                                                                            <circle cx="12" cy="5" r="2" />
                                                                            <circle cx="12" cy="12" r="2" />
                                                                            <circle cx="12" cy="19" r="2" />
                                                                        </svg>
                                                                    </button>
                                                                    {mobileTicketMenuIdx === i && (
                                                                        <div className={`absolute right-0 top-full mt-2 z-50 min-w-[160px] ${ecMenu}`}>
                                                                            <button
                                                                                type="button"
                                                                                className="w-full text-left rounded-xl px-3 py-2 text-sm font-semibold text-grey10 dark:text-nightTextSecondary hover:bg-grey99 dark:hover:bg-white/10"
                                                                                onClick={(e) => { e.stopPropagation(); setMobileTicketMenuIdx(null); setOpenSettingsIdx(i); }}
                                                                            >
                                                                                Edit
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                className="w-full text-left rounded-xl px-3 py-2 text-sm font-semibold text-grey10 dark:text-nightTextSecondary hover:bg-grey99 dark:hover:bg-white/10"
                                                                                onClick={(e) => { e.stopPropagation(); setMobileTicketMenuIdx(null); duplicateTicket(i); }}
                                                                            >
                                                                                Duplicate
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                className="w-full text-left rounded-xl px-3 py-2 text-sm font-semibold text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 disabled:opacity-40"
                                                                                disabled={deleteTicketMutation.isPending || isDeletingTicket}
                                                                                onClick={(e) => { e.stopPropagation(); setMobileTicketMenuIdx(null); removeTicket(i); }}
                                                                            >
                                                                                Delete
                                                                            </button>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center justify-between text-sm mb-3">
                                                                <span className="text-grey50 dark:text-nightMuted">Sold: <span className="font-bold text-grey10 dark:text-white">{sold}/{qtyNum}</span></span>
                                                                <span className="text-grey50 dark:text-nightMuted">Revenue: <span className="font-bold text-grey10 dark:text-white">{cur}{ticketRevenue.toFixed(0)}</span></span>
                                                            </div>
                                                            <span className={`${ecPill} ${tk.status ? 'bg-primary/10 text-primary dark:bg-nightAccent/15 dark:text-nightAccent' : 'bg-red-50 text-red-600 dark:bg-red-500/15 dark:text-red-400'}`}>
                                                                {tk.status ? 'Active' : 'Deactivated'}
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        </>)}

                                        {/* Add/Edit ticket modal */}
                                        {openSettingsIdx !== null && tickets[openSettingsIdx] && (() => {
                                            const i = openSettingsIdx;
                                            const tk = tickets[i];
                                            return (
                                                <Drawer
                                                    isOpen
                                                    placement="right"
                                                    size={isMobile ? 'full' : 'lg'}
                                                    scrollBehavior="inside"
                                                    hideCloseButton
                                                    isDismissable={false}
                                                    onOpenChange={(open: boolean) => { if (!open) closeTicketModal(); }}
                                                    classNames={{
                                                        wrapper: isMobile ? 'z-[100000]' : 'top-[80px] h-[calc(100dvh-80px)] z-[100000]',
                                                        base: isMobile ? 'bg-white dark:bg-nightSurface h-[100dvh] my-0 z-[100000]' : 'bg-white dark:bg-nightSurface h-[calc(100dvh-80px)] my-0 z-[100000] rounded-l-[28px] ring-1 ring-black/[0.06] dark:ring-white/10',
                                                        backdrop: isMobile ? 'z-[100000]' : 'top-[80px] h-[calc(100dvh-80px)] z-[100000] bg-black/50',
                                                    }}
                                                >
                                                    <DrawerContent>
                                                        {() => {
                                                            const fieldClassNames = {
                                                                inputWrapper: `border-0 ${ecFieldWrap}`,
                                                                input: ecFieldText,
                                                            };
                                                            return (
                                                                <>
                                                                    <DrawerHeader className="flex items-center justify-between px-6 pt-6 pb-4">
                                                                        <h3 className="text-[28px] font-bold leading-[1] tracking-[-0.04em] text-grey10 dark:text-white">{tk._id ? 'Edit Ticket' : 'New Ticket'}</h3>
                                                                        <button
                                                                            type="button"
                                                                            onClick={closeTicketModal}
                                                                            className={ecIconBtn}
                                                                            aria-label="Close"
                                                                        >
                                                                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                                                                <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                                                            </svg>
                                                                        </button>
                                                                    </DrawerHeader>
                                                                    <DrawerBody className="px-6 py-2 gap-6">
                                                                        <div>
                                                                            <label className="block text-sm font-semibold text-grey20 dark:text-nightTextSecondary mb-2">Ticket Name</label>
                                                                            <Input classNames={fieldClassNames} className="w-full" placeholder="Ticket Name..." value={tk.name} onChange={e => handleTicketChange(i, 'name', e.target.value)} />
                                                                        </div>

                                                                        {tierMode ? (
                                                                            <TicketTierPricing
                                                                                tiers={tierList}
                                                                                tierIds={tk.tierIds || []}
                                                                                overrides={tk.tierPrices || []}
                                                                                onChange={next => { handleTicketChange(i, 'tierIds', next.tierIds); handleTicketChange(i, 'tierPrices', next.tierPrices); }}
                                                                                soldByTier={Object.fromEntries(((drawerTierSummary?.tickets ?? []).find((t: any) => t.ticketId === tk._id)?.tiers ?? []).map((r: any) => [r.tier, r.sold]))}
                                                                                currencySymbol={cur}
                                                                                fieldClassNames={fieldClassNames}
                                                                            />
                                                                        ) : (
                                                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                                            <div>
                                                                                <label className="block text-sm font-semibold text-grey20 dark:text-nightTextSecondary mb-2">Price</label>
                                                                                <Input classNames={fieldClassNames} className="w-full" placeholder="$Price..." value={tk.price} onChange={e => handleTicketChange(i, 'price', e.target.value)} />
                                                                            </div>
                                                                            <div>
                                                                                <label className="block text-sm font-semibold text-grey20 dark:text-nightTextSecondary mb-2">Quantity</label>
                                                                                <Input classNames={fieldClassNames} className="w-full" placeholder="Quantity..." value={tk.qty} onChange={e => handleTicketChange(i, 'qty', e.target.value)} />
                                                                            </div>
                                                                        </div>
                                                                        )}

                                                                        <div>
                                                                            <div className="text-lg font-bold tracking-[-0.02em] text-grey10 dark:text-white mb-3">Start Selling At{tierMode && <span className="ml-2 text-xs font-normal text-grey40 dark:text-nightMuted">Optional</span>}{tierMode && (tk.start || tk.startTime) && <button type="button" className="ml-3 text-xs font-normal underline text-grey40 dark:text-nightMuted" onClick={() => { handleTicketChange(i, 'start', null as any); handleTicketChange(i, 'startTime', null as any); }}>Clear</button>}</div>
                                                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                                                <div>
                                                                                    <label className="block text-sm font-semibold text-grey20 dark:text-nightTextSecondary mb-2">Date</label>
                                                                                    <DatePicker
                                                                                        classNames={fieldClassNames}
                                                                                        className={`w-full ${ticketErrorIdx[i] ? 'rounded-2xl ring-1 ring-danger' : ''}`}
                                                                                        isInvalid={!!ticketErrorIdx[i]}
                                                                                        value={tk.start}
                                                                                        onChange={v => handleTicketChange(i, 'start', v as any)}
                                                                                    />
                                                                                </div>
                                                                                <div>
                                                                                    <label className="block text-sm font-semibold text-grey20 dark:text-nightTextSecondary mb-2">Time</label>
                                                                                    <TimeInput
                                                                                        classNames={fieldClassNames}
                                                                                        className={`w-full ${ticketErrorIdx[i] ? 'rounded-2xl ring-1 ring-danger' : ''}`}
                                                                                        isInvalid={!!ticketErrorIdx[i]}
                                                                                        hourCycle={12}
                                                                                        value={tk.startTime}
                                                                                        onChange={v => handleTicketChange(i, 'startTime', v as any)}
                                                                                    />
                                                                                </div>
                                                                            </div>
                                                                        </div>

                                                                        <div>
                                                                            <div className="text-lg font-bold tracking-[-0.02em] text-grey10 dark:text-white mb-3">End Selling At{tierMode && <span className="ml-2 text-xs font-normal text-grey40 dark:text-nightMuted">Optional</span>}{tierMode && (tk.end || tk.endTime) && <button type="button" className="ml-3 text-xs font-normal underline text-grey40 dark:text-nightMuted" onClick={() => { handleTicketChange(i, 'end', null as any); handleTicketChange(i, 'endTime', null as any); }}>Clear</button>}</div>
                                                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                                                <div>
                                                                                    <label className="block text-sm font-semibold text-grey20 dark:text-nightTextSecondary mb-2">Date</label>
                                                                                    <DatePicker
                                                                                        classNames={fieldClassNames}
                                                                                        className={`w-full ${ticketErrorIdx[i] ? 'rounded-2xl ring-1 ring-danger' : ''}`}
                                                                                        isInvalid={!!ticketErrorIdx[i]}
                                                                                        value={tk.end}
                                                                                        onChange={v => handleTicketChange(i, 'end', v as any)}
                                                                                        minValue={tk.start}
                                                                                    />
                                                                                </div>
                                                                                <div>
                                                                                    <label className="block text-sm font-semibold text-grey20 dark:text-nightTextSecondary mb-2">Time</label>
                                                                                    <TimeInput
                                                                                        classNames={fieldClassNames}
                                                                                        className={`w-full ${ticketErrorIdx[i] ? 'rounded-2xl ring-1 ring-danger' : ''}`}
                                                                                        isInvalid={!!ticketErrorIdx[i]}
                                                                                        hourCycle={12}
                                                                                        value={tk.endTime}
                                                                                        onChange={v => handleTicketChange(i, 'endTime', v as any)}
                                                                                    />
                                                                                </div>
                                                                            </div>
                                                                        </div>

                                                                        <div>
                                                                            <label className="block text-sm font-semibold text-grey20 dark:text-nightTextSecondary mb-2">Description</label>
                                                                            <Textarea classNames={fieldClassNames} className="w-full" rows={3} placeholder="Description..." value={tk.desc} onChange={e => handleTicketChange(i, 'desc', e.target.value)} />
                                                                        </div>

                                                                        <div className="flex items-center gap-4">
                                                                            <span className="text-sm text-grey20 dark:text-nightTextSecondary font-semibold">Active</span>
                                                                            <Switch isSelected={tk.status} onValueChange={checked => handleTicketChange(i, 'status', checked)} className="accent-primary40" />
                                                                        </div>

                                                                        {/* Promocodes Section */}
                                                                        <div className="pt-6 border-t border-black/[0.06] dark:border-white/10">
                                                                            <div className="flex justify-between items-center gap-3 mb-4">
                                                                                <h3 className="text-lg font-bold tracking-[-0.02em] text-grey10 dark:text-white">Promo Code</h3>
                                                                                <button
                                                                                    onClick={() => handleAddPromocode(tk._id || '')}
                                                                                    className={`${ecBtnGhost} h-10 px-4 disabled:opacity-50`}
                                                                                    disabled={!tk._id}
                                                                                >
                                                                                    <Plus width={14} height={14} />
                                                                                    Add Promo Code
                                                                                </button>
                                                                            </div>

                                                                            {tk.promocodes && tk.promocodes.length > 0 ? (
                                                                                <div className="space-y-2">
                                                                                    {tk.promocodes.map((promo: any) => (
                                                                                        <div key={promo._id} className="flex items-center justify-between gap-3 p-4 bg-grey99 ring-1 ring-black/[0.04] dark:bg-white/[0.04] dark:ring-white/[0.06] rounded-2xl">
                                                                                            <div className="flex-1 min-w-0">
                                                                                                <div className="flex flex-wrap items-center gap-2 mb-1">
                                                                                                    <span className="rounded-full bg-grey10 px-3 py-1 font-mono text-xs font-bold tracking-wide text-white dark:bg-white dark:text-nightBg">{promo.codeName}</span>
                                                                                                    <span className="text-xs text-grey40 dark:text-nightMuted">
                                                                                                        {promo.discountType === 'percentage' ? `${promo.discount}%` : `${cur}${promo.discount}`}
                                                                                                    </span>
                                                                                                    {promo.isActive && (
                                                                                                        <span className={`${ecPill} bg-green-50 text-green-700 dark:bg-green-500/10 dark:text-green-400`}>Active</span>
                                                                                                    )}
                                                                                                </div>
                                                                                                <div className="text-xs text-grey40 dark:text-nightMuted">
                                                                                                    {moment(promo.startDate).format('MM/DD/YYYY')} - {moment(promo.endDate).format('MM/DD/YYYY')}
                                                                                                </div>
                                                                                            </div>
                                                                                            <div className="flex items-center gap-2">
                                                                                                <button
                                                                                                    onClick={() => handleEditPromocode(tk._id || '', promo._id)}
                                                                                                    className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-primary ring-1 ring-black/[0.06] hover:bg-primary99 dark:bg-white/[0.06] dark:text-nightAccent dark:ring-white/10 dark:hover:bg-white/10"
                                                                                                    title="Edit promocode"
                                                                                                    disabled={!tk._id}
                                                                                                >
                                                                                                    <EditIcon width={16} height={16} />
                                                                                                </button>
                                                                                                <button
                                                                                                    onClick={() => handleRemovePromocode(tk._id || '', promo._id)}
                                                                                                    className="flex h-9 w-9 items-center justify-center rounded-full bg-red-50 text-red-500 hover:bg-red-100 dark:bg-red-500/10 dark:text-red-400 dark:hover:bg-red-500/20"
                                                                                                    title="Delete promocode"
                                                                                                    disabled={!tk._id}
                                                                                                >
                                                                                                    <Trash width={14} height={14} />
                                                                                                </button>
                                                                                            </div>
                                                                                        </div>
                                                                                    ))}
                                                                                </div>
                                                                            ) : (
                                                                                <div className="text-center py-6 rounded-2xl bg-grey99 dark:bg-white/[0.04] text-sm text-grey40 dark:text-nightMuted">
                                                                                    {tk._id ? 'No promocodes yet. Click "Add Promocode" to create one.' : 'Save the ticket to add promocodes.'}
                                                                                </div>
                                                                            )}
                                                                        </div>

                                                                        {/* Member Pricing Section - a flat discount applied to any buyer whose email is in the organization's Members list */}
                                                                        <div className="pt-6 border-t border-black/[0.06] dark:border-white/10">
                                                                            <div className="mb-4">
                                                                                <h3 className="text-lg font-bold tracking-[-0.02em] text-grey10 dark:text-white">Member Pricing</h3>
                                                                                <p className="text-sm text-grey40 dark:text-nightMuted mt-1">Buyers whose email is in your organization's Members list get this discount automatically at checkout.</p>
                                                                            </div>
                                                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                                                <div>
                                                                                    <label className="block text-sm font-semibold text-grey20 dark:text-nightTextSecondary mb-2">Discount Type</label>
                                                                                    <Select aria-label="Select"
                                                                                        classNames={{ trigger: `border-0 ${ecFieldWrap}`, value: ecFieldText }}
                                                                                        selectedKeys={[tk.memberPricingDiscountType || 'percentage']}
                                                                                        onSelectionChange={(keys) => handleTicketChange(i, 'memberPricingDiscountType', Array.from(keys)[0] as 'fixed' | 'percentage')}
                                                                                    >
                                                                                        <SelectItem key="percentage">Percentage</SelectItem>
                                                                                        <SelectItem key="fixed">Fixed Amount</SelectItem>
                                                                                    </Select>
                                                                                </div>
                                                                                <div>
                                                                                    <label className="block text-sm font-semibold text-grey20 dark:text-nightTextSecondary mb-2">
                                                                                        {tk.memberPricingDiscountType === 'fixed' ? 'Discount Amount ($)' : 'Discount Percentage (%)'}
                                                                                    </label>
                                                                                    <Input
                                                                                        type="number"
                                                                                        min="0"
                                                                                        max={tk.memberPricingDiscountType === 'fixed' ? undefined : 100}
                                                                                        step={tk.memberPricingDiscountType === 'fixed' ? '0.01' : '1'}
                                                                                        classNames={fieldClassNames}
                                                                                        value={tk.memberPricingDiscount || ''}
                                                                                        onChange={(e) => handleTicketChange(i, 'memberPricingDiscount', e.target.value)}
                                                                                        placeholder={tk.memberPricingDiscountType === 'fixed' ? '10.00' : '20'}
                                                                                    />
                                                                                </div>
                                                                            </div>
                                                                            <div className="flex items-center gap-4 mt-4">
                                                                                <Switch
                                                                                    isSelected={tk.allowPromoCodeWithMemberPricing === true}
                                                                                    onValueChange={checked => handleTicketChange(i, 'allowPromoCodeWithMemberPricing', checked)}
                                                                                />
                                                                                <span className="text-sm font-medium text-grey20 dark:text-nightTextSecondary">Allow combining with promo codes at checkout</span>
                                                                            </div>
                                                                        </div>

                                                                        {ticketErrorIdx[i] && <div className="text-xs text-red-500 mt-2">{ticketErrorIdx[i]}</div>}
                                                                    </DrawerBody>
                                                                    <DrawerFooter className="px-6 py-5 gap-2 border-t border-black/[0.06] dark:border-white/10">
                                                                        <Button variant="light" className={`${ecBtnGhost} bg-transparent`} onPress={closeTicketModal}>Cancel</Button>
                                                                        <Button
                                                                            className={ecBtnAccent}
                                                                            onPress={() => handleSaveTicket(i)}
                                                                            isDisabled={ticketLoadingIdx === i || ticketLoadingIndices.has(i)}
                                                                            isLoading={ticketLoadingIdx === i || ticketLoadingIndices.has(i)}
                                                                        >
                                                                            Save Ticket
                                                                        </Button>
                                                                    </DrawerFooter>
                                                                </>
                                                            );
                                                        }}
                                                    </DrawerContent>
                                                </Drawer>
                                            );
                                        })()}

                                        {/* Removed standalone refund settings; merged into Info tab above */}
                                    </div>
                                );
                            })()}
                        </div>
                    </RoleBasedAccess>
                    {/* Info and Tickets tabs can be implemented similarly if needed */}
                </div>
            </div>



            {/* Global QR Scanner Modal */}
            <GlobalQRScannerModal
                isOpen={globalQRScannerOpen}
                onClose={() => setGlobalQRScannerOpen(false)}
                eventId={effectiveEventId}
            />

            {/* Ticket Check-in Modal */}
            {selectedTicketForCheckIn && selectedOrderForTicketCheckIn && (
                <TicketCheckInModal
                    isOpen={ticketCheckInModalOpen}
                    onClose={() => {
                        setTicketCheckInModalOpen(false);
                        setSelectedTicketForCheckIn(null);
                        setSelectedOrderForTicketCheckIn(null);
                    }}
                    order={selectedOrderForTicketCheckIn}
                    ticket={selectedTicketForCheckIn}
                    eventId={effectiveEventId}
                />
            )}

            {/* Walk-in Order Modal */}
            <Drawer
                placement="right"
                hideCloseButton
                isOpen={walkInModalOpen}
                onClose={() => setWalkInModalOpen(false)}
                size={isMobile ? 'full' : '2xl'}
                scrollBehavior="inside"
                classNames={{
                    wrapper: isMobile ? 'z-[100000]' : 'top-[80px] h-[calc(100dvh-80px)] z-[100000]',
                    base: isMobile ? 'bg-white dark:bg-nightSurface h-[100dvh] my-0 z-[100000]' : 'bg-white dark:bg-nightSurface h-[calc(100dvh-80px)] my-0 z-[100000] rounded-l-[28px] ring-1 ring-black/[0.06] dark:ring-white/10',
                    backdrop: isMobile ? 'z-[100000]' : 'top-[80px] h-[calc(100dvh-80px)] z-[100000] bg-black/50',
                    header: 'border-b border-black/[0.06] dark:border-white/10',
                    footer: 'border-t border-black/[0.06] dark:border-white/10',
                }}
            >
                <DrawerContent>
                    <DrawerHeader className="flex items-center justify-between px-6 pt-6 pb-4">
                        <h2 className="text-[28px] font-bold leading-[1] tracking-[-0.04em] text-grey10 dark:text-white">Create Walk-in Order</h2>
                        <button
                            type="button"
                            onClick={() => setWalkInModalOpen(false)}
                            className={ecIconBtn}
                            aria-label="Close"
                        >
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                            </svg>
                        </button>
                    </DrawerHeader>

                    <DrawerBody className="py-6">
                        {/* Customer Information */}
                        <div className="mb-6">
                            <h3 className="text-lg font-bold tracking-[-0.02em] text-grey10 dark:text-white mb-4">Customer Information</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-grey20 dark:text-nightTextSecondary mb-2">Name *</label>
                                    <input
                                        type="text"
                                        value={walkInForm.customerName}
                                        onChange={(e) => {
                                            setWalkInForm(prev => ({ ...prev, customerName: e.target.value }));
                                            if (walkInErrors.customerName) {
                                                setWalkInErrors(prev => ({ ...prev, customerName: '' }));
                                            }
                                        }}
                                        className={`w-full h-12 px-4 rounded-2xl border-0 bg-grey99 dark:bg-white/[0.06] text-base text-grey10 dark:text-white placeholder:text-grey60 dark:placeholder:text-nightMuted focus:outline-none focus:ring-2 focus:ring-primary/50 dark:focus:ring-nightAccent/60 ${walkInErrors.customerName ? 'ring-1 ring-red-500' : ''}`}
                                        placeholder="Name..."
                                    />
                                    {walkInErrors.customerName && (
                                        <p className="text-red-500 text-xs mt-1">{walkInErrors.customerName}</p>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-grey20 dark:text-nightTextSecondary mb-2">Email *</label>
                                    <input
                                        type="email"
                                        value={walkInForm.customerEmail}
                                        onChange={(e) => {
                                            const email = e.target.value;
                                            setWalkInForm(prev => ({ ...prev, customerEmail: email }));

                                            // Real-time email validation
                                            if (email.trim()) {
                                                if (!validateEmail(email.trim())) {
                                                    setWalkInErrors(prev => ({ ...prev, customerEmail: 'Please enter a valid email address' }));
                                                } else {
                                                    setWalkInErrors(prev => ({ ...prev, customerEmail: '' }));
                                                }
                                            } else {
                                                setWalkInErrors(prev => ({ ...prev, customerEmail: '' }));
                                            }
                                        }}
                                        onBlur={(e) => {
                                            const email = e.target.value.trim();
                                            if (!email) {
                                                setWalkInErrors(prev => ({ ...prev, customerEmail: 'Email is required' }));
                                            } else if (!validateEmail(email)) {
                                                setWalkInErrors(prev => ({ ...prev, customerEmail: 'Please enter a valid email address' }));
                                            }
                                        }}
                                        className={`w-full h-12 px-4 rounded-2xl border-0 bg-grey99 dark:bg-white/[0.06] text-base text-grey10 dark:text-white placeholder:text-grey60 dark:placeholder:text-nightMuted focus:outline-none focus:ring-2 focus:ring-primary/50 dark:focus:ring-nightAccent/60 ${walkInErrors.customerEmail ? 'ring-1 ring-red-500' : ''}`}
                                        placeholder="Email..."
                                    />
                                    {walkInErrors.customerEmail && (
                                        <p className="text-red-500 text-xs mt-1">{walkInErrors.customerEmail}</p>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-grey20 dark:text-nightTextSecondary mb-2">Phone</label>
                                    <input
                                        type="tel"
                                        value={walkInForm.customerPhone}
                                        onChange={(e) => {
                                            setWalkInForm(prev => ({ ...prev, customerPhone: e.target.value }));
                                            if (walkInErrors.customerPhone) {
                                                setWalkInErrors(prev => ({ ...prev, customerPhone: '' }));
                                            }
                                        }}
                                        className={`w-full h-12 px-4 rounded-2xl border-0 bg-grey99 dark:bg-white/[0.06] text-base text-grey10 dark:text-white placeholder:text-grey60 dark:placeholder:text-nightMuted focus:outline-none focus:ring-2 focus:ring-primary/50 dark:focus:ring-nightAccent/60 ${walkInErrors.customerPhone ? 'ring-1 ring-red-500' : ''}`}
                                        placeholder="Phone..."
                                    />
                                    {walkInErrors.customerPhone && (
                                        <p className="text-red-500 text-xs mt-1">{walkInErrors.customerPhone}</p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {walkInSeatMode && (
                            <div className="mb-6">
                                <h3 className="text-lg font-bold tracking-[-0.02em] text-grey10 dark:text-white mb-4">Select Seats</h3>
                                <SeatMapSelector
                                    eventId={effectiveEventId}
                                    venueId={eventData.venueId as string}
                                    seatMapType="client"
                                    selectedSeats={walkInSeats}
                                    onSeatSelect={(seatUuid, label) => {
                                        setWalkInSeats(prev => prev.includes(seatUuid) ? prev : [...prev, seatUuid]);
                                        setWalkInSeatLabels(prev => ({ ...prev, [seatUuid]: label }));
                                    }}
                                    onSeatDeselect={seatUuid => setWalkInSeats(prev => prev.filter(id => id !== seatUuid))}
                                    tickets={Array.isArray(ticketsData?.data) ? ticketsData.data : []}
                                    onSeatAssignmentsLoaded={setWalkInAssignments}
                                    onUnresolvedChange={setWalkInUnresolved}
                                    currency={eventData.currency}
                                />
                                {walkInSeats.length > 0 && (
                                    <ul className="mt-4 space-y-2">
                                        {walkInSeats.map(seatUuid => {
                                            const assignment = walkInAssignments.find(a => a.seatUuid === seatUuid);
                                            const ticket = (Array.isArray(ticketsData?.data) ? ticketsData.data : []).find((t: any) => t._id === assignment?.ticketId);
                                            const price = typeof assignment?.price === 'number' ? assignment.price : Number(ticket?.price) || 0;
                                            return (
                                                <li key={seatUuid} className="flex items-center justify-between gap-3 rounded-2xl bg-grey99 dark:bg-white/[0.04] px-4 py-3 text-sm">
                                                    <span className="min-w-0 truncate text-grey10 dark:text-white">{walkInSeatLabels[seatUuid] ?? 'Seat'}{ticket ? ` · ${ticket.name}` : ''}</span>
                                                    <span className="flex items-center gap-3 whitespace-nowrap">
                                                        <span className="font-semibold">{cur}{price.toFixed(2)}</span>
                                                        <button type="button" aria-label="Remove seat" className="text-red-500" onClick={() => setWalkInSeats(prev => prev.filter(id => id !== seatUuid))}>Remove</button>
                                                    </span>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </div>
                        )}

                        {/* Available Tickets */}
                        {!walkInSeatMode && (<>
                        <div className="mb-6">
                            <h3 className="text-lg font-bold tracking-[-0.02em] text-grey10 dark:text-white mb-4">Available Tickets</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {Array.isArray(ticketsData?.data) && ticketsData.data.map((ticket: any) => (
                                    <div key={ticket._id} className="rounded-[22px] bg-grey99 ring-1 ring-black/[0.04] dark:bg-white/[0.04] dark:ring-white/[0.06] p-5">
                                        <div className="flex justify-between items-start gap-3 mb-2">
                                            <h4 className="font-bold tracking-[-0.01em] text-grey10 dark:text-white">{ticket.name}</h4>
                                            <span className="text-lg font-bold text-primary dark:text-nightAccent">{cur}{ticket.price}</span>
                                        </div>
                                        <p className="text-sm text-grey40 dark:text-nightMuted mb-4">{ticket.description}</p>
                                        <button
                                            onClick={() => addTicketToWalkIn(ticket)}
                                            className={`w-full ${ecBtnAccent} h-10`}
                                        >
                                            Add Ticket
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        </>)}

                        {/* Selected Tickets */}
                        {!walkInSeatMode && walkInForm.selectedTickets.length > 0 && (
                            <div className="mb-6">
                                <h3 className="text-lg font-bold tracking-[-0.02em] text-grey10 dark:text-white mb-4">Selected Tickets</h3>
                                <div className="space-y-3">
                                    {walkInForm.selectedTickets.map((ticket) => {
                                        const eventTimes = eventData?.times || [];
                                        const hasMultipleDates = eventTimes.length > 1;

                                        return (
                                            <div key={ticket.ticketId} className="p-4 bg-grey99 ring-1 ring-black/[0.04] dark:bg-white/[0.04] dark:ring-white/[0.06] rounded-[22px]">
                                                <div className="flex items-center justify-between gap-3 mb-3">
                                                    <div className="flex-1 min-w-0">
                                                        <div className="font-bold text-grey10 dark:text-white">{ticket.name}</div>
                                                        <div className="text-sm text-grey40 dark:text-nightMuted">{cur}{ticket.price} each</div>
                                                    </div>
                                                    <div className="flex items-center gap-3">
                                                        <div className="flex items-center gap-2">
                                                            <button
                                                                onClick={() => updateWalkInTicketQuantity(ticket.ticketId, Math.max(0, ticket.quantity - 1))}
                                                                className="w-9 h-9 flex items-center justify-center bg-white ring-1 ring-black/[0.06] dark:bg-white/[0.06] dark:ring-white/10 rounded-full hover:bg-grey95 dark:hover:bg-white/10"
                                                            >
                                                                <svg width="16" height="16" fill="none" viewBox="0 0 16 16">
                                                                    <path d="M4 8h8" stroke="#666" strokeWidth="1.5" strokeLinecap="round" />
                                                                </svg>
                                                            </button>
                                                            <span className="w-8 text-center font-bold text-grey10 dark:text-white">{ticket.quantity}</span>
                                                            <button
                                                                onClick={() => updateWalkInTicketQuantity(ticket.ticketId, ticket.quantity + 1)}
                                                                className="w-9 h-9 flex items-center justify-center bg-white ring-1 ring-black/[0.06] dark:bg-white/[0.06] dark:ring-white/10 rounded-full hover:bg-grey95 dark:hover:bg-white/10"
                                                            >
                                                                <svg width="16" height="16" fill="none" viewBox="0 0 16 16">
                                                                    <path d="M8 4v8M4 8h8" stroke="#666" strokeWidth="1.5" strokeLinecap="round" />
                                                                </svg>
                                                            </button>
                                                        </div>
                                                        <button
                                                            onClick={() => removeTicketFromWalkIn(ticket.ticketId)}
                                                            className="flex h-9 w-9 items-center justify-center rounded-full bg-red-50 text-red-500 hover:bg-red-100 dark:bg-red-500/10 dark:text-red-400 dark:hover:bg-red-500/20"
                                                        >
                                                            <svg width="16" height="16" fill="none" viewBox="0 0 16 16">
                                                                <path d="M3 3l10 10M3 13l10-10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Date Selection */}
                                                {hasMultipleDates && (
                                                    <div className="mt-3 pt-3 border-t border-black/[0.06] dark:border-white/10">
                                                        <label className="block text-sm font-semibold text-grey20 dark:text-nightTextSecondary mb-2">Select Date:</label>

                                                        {/* Visual Date Selection */}
                                                        {(() => {
                                                            const availableDates = getAvailableDates();
                                                            const first5 = availableDates.slice(0, 5);
                                                            const moreCount = Math.max(0, availableDates.length - 5);

                                                            return (
                                                                <div>
                                                                    <div className="flex gap-2 overflow-x-auto mb-2">
                                                                        {first5.map((date, index) => {
                                                                            const isSelected = ticket.selectedDate === date.toISOString().split('T')[0];
                                                                            const isDatePassed = date < new Date();

                                                                            return (
                                                                                <button
                                                                                    key={index}
                                                                                    className={`flex flex-col justify-between items-center px-2 py-2 rounded-2xl min-w-[60px] h-[80px] bg-white dark:bg-white/[0.04] transition-colors ${isSelected ? 'border-[#009688] dark:border-nightAccent border-2' : 'border-black/[0.06] dark:border-white/10 hover:bg-grey95 dark:hover:bg-white/10 border'} ${isDatePassed ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                                                    onClick={() => {
                                                                                        if (!isDatePassed) {
                                                                                            updateWalkInTicketDate(ticket.ticketId, moment(date).format('YYYY-MM-DD HH:mm'));
                                                                                        }
                                                                                    }}
                                                                                    disabled={isDatePassed}
                                                                                >
                                                                                    <span className="text-xs font-bold text-grey40 dark:text-nightMuted">{date.toLocaleDateString('en-US', { weekday: 'short' })}</span>
                                                                                    <span className="text-xs text-grey40 dark:text-nightMuted">{date.toLocaleDateString('en-US', { month: 'short' })}</span>
                                                                                    <span className={`w-6 h-6 flex items-center justify-center rounded-full text-sm font-bold ${isSelected ? 'bg-[#009688] text-white' : isDatePassed ? 'bg-gray-300 dark:bg-nightBorder text-gray-500 dark:text-nightMuted' : 'bg-[#E6F6F4] dark:bg-nightAccent/10 text-[#009688] dark:text-nightAccent'}`}>
                                                                                        {date.getDate()}
                                                                                    </span>
                                                                                    <span className="text-xs text-grey40 dark:text-nightMuted">
                                                                                        {date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                                                                                    </span>
                                                                                </button>
                                                                            );
                                                                        })}

                                                                        {moreCount > 0 && (
                                                                            <button
                                                                                className="flex flex-col items-center justify-center px-2 py-2 rounded-2xl border border-black/[0.06] dark:border-white/10 min-w-[60px] h-[80px] bg-white dark:bg-white/[0.04] hover:bg-grey95 dark:hover:bg-white/10"
                                                                                onClick={() => {
                                                                                    setSelectedDateForTicket(ticket.ticketId);
                                                                                    setShowDateCalendar(true);
                                                                                }}
                                                                            >
                                                                                <svg width="16" height="16" fill="none" viewBox="0 0 24 24" className="text-[#009688] dark:text-nightAccent mb-1">
                                                                                    <path d="M8 2v4M16 2v4M3 10h18M21 8v12a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h14a2 2 0 012 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                                                </svg>
                                                                                <span className="text-xs font-bold text-[#009688] dark:text-nightAccent">More</span>
                                                                            </button>
                                                                        )}
                                                                    </div>

                                                                    {ticket.selectedDate && (
                                                                        <div className="bg-primary99 dark:bg-nightAccent/10 ring-1 ring-primary/40 dark:ring-nightAccent/40 rounded-2xl px-3 py-2">
                                                                            <div className="text-xs font-semibold text-[#009688] dark:text-nightAccent mb-1">Selected Date</div>
                                                                            <div className="text-xs text-grey40 dark:text-nightMuted">
                                                                                {new Date(ticket.selectedDate).toLocaleDateString('en-US', {
                                                                                    weekday: 'long',
                                                                                    month: 'long',
                                                                                    day: 'numeric',
                                                                                    year: 'numeric'
                                                                                })}
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })()}
                                                    </div>
                                                )}

                                                {/* Show selected date for single date events */}
                                                {!hasMultipleDates && eventTimes.length === 1 && (
                                                    <div className="mt-3 pt-3 border-t border-black/[0.06] dark:border-white/10">
                                                        <div className="text-sm text-grey40 dark:text-nightMuted">
                                                            <span className="font-medium">Date:</span> {new Date(eventTimes[0].startDate).toLocaleDateString()} {eventTimes[0].startTime && `at ${eventTimes[0].startTime}`}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Total */}
                        {walkInForm.selectedTickets.length > 0 && (
                            <div className="mb-6 p-5 bg-primary99 ring-1 ring-primary/20 dark:bg-nightAccent/10 dark:ring-nightAccent/20 rounded-[22px]">
                                <div className="space-y-2">
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm font-medium text-grey40 dark:text-nightMuted">Subtotal:</span>
                                        <span className="text-sm font-medium text-grey40 dark:text-nightMuted">{cur}{calculateWalkInTotal().toFixed(2)}</span>
                                    </div>
                                    <div className="border-t border-black/[0.06] dark:border-white/10 pt-3">
                                        <div className="flex justify-between items-center">
                                            <span className="text-lg font-bold text-grey10 dark:text-white">Total Amount:</span>
                                            <span className="text-[28px] font-bold tracking-[-0.03em] text-primary dark:text-nightAccent">{cur}{calculateWalkInTotal().toFixed(2)}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </DrawerBody>

                    <DrawerFooter className="flex flex-col sm:flex-row gap-3">
                        <Button variant="light" onPress={() => setWalkInModalOpen(false)} className={`w-full sm:w-auto ${ecBtnGhost} bg-transparent`}>
                            Cancel
                        </Button>
                        <Button
                            color="primary"
                            onPress={handleCreateWalkInOrder}
                            isLoading={walkInOrderMutation.isPending}
                            isDisabled={
                                walkInForm.selectedTickets.length === 0 ||
                                walkInUnresolved.length > 0 ||
                                !walkInForm.customerName.trim() ||
                                !walkInForm.customerEmail.trim() ||
                                !validateEmail(walkInForm.customerEmail.trim()) ||
                                (() => {
                                    const eventTimes = eventData?.times || [];
                                    const hasMultipleDates = eventTimes.length > 1;
                                    if (hasMultipleDates) {
                                        return walkInForm.selectedTickets.some(ticket => !ticket.selectedDate);
                                    }
                                    return false;
                                })()
                            }
                            className={`w-full sm:w-auto ${ecBtnAccent}`}
                        >
                            Create Order
                        </Button>
                    </DrawerFooter>
                </DrawerContent>
            </Drawer>

            {/* Calendar Popup for Date Selection */}
            {showDateCalendar && (
                <div onClick={() => setShowDateCalendar(false)} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div onClick={(e) => e.stopPropagation()} className="bg-white dark:bg-nightSurface ring-1 ring-black/[0.06] dark:ring-white/10 rounded-[28px] flex flex-col max-w-md w-full mx-4 pb-4">
                        <div className="p-5">
                            {/* Month Navigation */}
                            <div className="flex items-center justify-between mb-4">
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        const allMonths = getAllEventMonths();
                                        const currentIndex = allMonths.findIndex(m =>
                                            m.getFullYear() === currentMonth.getFullYear() &&
                                            m.getMonth() === currentMonth.getMonth()
                                        );
                                        if (currentIndex > 0) {
                                            setCurrentMonth(allMonths[currentIndex - 1]);
                                        }
                                    }}
                                    className="flex h-9 w-9 items-center justify-center text-grey10 dark:text-white hover:bg-grey99 dark:hover:bg-white/10 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
                                    disabled={(() => {
                                        const allMonths = getAllEventMonths();
                                        const currentIndex = allMonths.findIndex(m =>
                                            m.getFullYear() === currentMonth.getFullYear() &&
                                            m.getMonth() === currentMonth.getMonth()
                                        );
                                        return currentIndex <= 0;
                                    })()}
                                >
                                    <svg width="16" height="16" fill="none" viewBox="0 0 24 24">
                                        <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </button>
                                <div className="text-base font-bold tracking-[-0.01em] text-grey10 dark:text-white">
                                    {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                                </div>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        const allMonths = getAllEventMonths();
                                        const currentIndex = allMonths.findIndex(m =>
                                            m.getFullYear() === currentMonth.getFullYear() &&
                                            m.getMonth() === currentMonth.getMonth()
                                        );
                                        if (currentIndex < allMonths.length - 1) {
                                            setCurrentMonth(allMonths[currentIndex + 1]);
                                        }
                                    }}
                                    className="flex h-9 w-9 items-center justify-center text-grey10 dark:text-white hover:bg-grey99 dark:hover:bg-white/10 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
                                    disabled={(() => {
                                        const allMonths = getAllEventMonths();
                                        const currentIndex = allMonths.findIndex(m =>
                                            m.getFullYear() === currentMonth.getFullYear() &&
                                            m.getMonth() === currentMonth.getMonth()
                                        );
                                        return currentIndex >= allMonths.length - 1;
                                    })()}
                                >
                                    <svg width="16" height="16" fill="none" viewBox="0 0 24 24">
                                        <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </button>
                            </div>
                            <div className="grid grid-cols-7 gap-1">
                                {/* Calendar headers */}
                                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                                    <div key={day} className="text-xs text-grey40 dark:text-nightMuted text-center py-1">
                                        {day}
                                    </div>
                                ))}

                                {/* Calendar days */}
                                {(() => {
                                    const allDays: Date[] = [];
                                    const allStartDates: Date[] = [];
                                    eventData?.times?.forEach((range: any) => {
                                        const start = new Date(range.startDate);
                                        const end = new Date(range.endDate);
                                        allStartDates.push(start);
                                        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
                                            allDays.push(new Date(d));
                                        }
                                    });

                                    const earliestStart = new Date(Math.min(...allStartDates.map(d => d.getTime())));
                                    const now = new Date();
                                    const firstDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
                                    const lastDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
                                    const startPadding = firstDay.getDay();
                                    const daysInMonth = lastDay.getDate();

                                    const days = [];

                                    // Add padding for first week
                                    for (let i = 0; i < startPadding; i++) {
                                        days.push(<div key={`empty-${i}`} className="h-8"></div>);
                                    }

                                    // Add days of the month
                                    for (let day = 1; day <= daysInMonth; day++) {
                                        const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
                                        const isInEventDays = allDays.some(d => d.toISOString() === date.toISOString());
                                        const isBeforeEventStart = date < earliestStart;
                                        const isPassed = isInEventDays && date < now;
                                        const isSelected = selectedDateForTicket && walkInForm.selectedTickets.find(t => t.ticketId === selectedDateForTicket)?.selectedDate === date.toISOString().split('T')[0];
                                        const isAvailable = isInEventDays && !isBeforeEventStart && date >= now;

                                        let className = 'h-8 w-8 flex items-center justify-center text-sm rounded-full';
                                        let onClick: (() => void) | undefined = undefined;
                                        let onMouseDown: ((e: React.MouseEvent) => void) | undefined = undefined;

                                        if (isSelected) {
                                            className += ' bg-[#009688] text-white cursor-pointer';
                                        } else if (isPassed) {
                                            className += ' bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800/40 cursor-not-allowed';
                                            onMouseDown = (e) => e.stopPropagation();
                                        } else if (isBeforeEventStart) {
                                            className += ' bg-gray-100 dark:bg-nightSurface text-gray-400 dark:text-nightMuted border border-gray-200 dark:border-nightBorder cursor-not-allowed';
                                            onMouseDown = (e) => e.stopPropagation();
                                        } else if (isAvailable) {
                                            className += ' bg-[#E6F6F4] dark:bg-nightAccent/10 text-[#009688] dark:text-nightAccent hover:bg-[#009688] hover:text-white cursor-pointer';
                                            onClick = () => {
                                                if (selectedDateForTicket) {
                                                    updateWalkInTicketDate(selectedDateForTicket, date.toISOString().split('T')[0]);
                                                    setShowDateCalendar(false);
                                                }
                                            };
                                            onMouseDown = (e) => e.stopPropagation();
                                        } else {
                                            className += ' text-gray-300 dark:text-nightBorder cursor-not-allowed';
                                            onMouseDown = (e) => e.stopPropagation();
                                        }

                                        days.push(
                                            <div
                                                key={day}
                                                className={className}
                                                onClick={onClick}
                                                onMouseDown={onMouseDown}
                                            >
                                                {day}
                                            </div>
                                        );
                                    }

                                    return days;
                                })()}
                            </div>
                        </div>
                        <div className="px-5 py-2 text-xs text-grey40 dark:text-nightMuted">
                            <div className="flex items-center gap-2 mb-1">
                                <div className="w-3 h-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded"></div>
                                <span>Passed dates</span>
                            </div>
                            <div className="flex items-center gap-2 mb-1">
                                <div className="w-3 h-3 bg-gray-100 dark:bg-nightSurface border border-gray-300 dark:border-nightBorder rounded"></div>
                                <span>Before event start</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 bg-[#E6F6F4] dark:bg-nightAccent/10 rounded"></div>
                                <span>Available dates</span>
                            </div>
                        </div>
                        <button
                            className={`mt-4 mx-5 ${ecBtnGhost} h-10`}
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowDateCalendar(false);
                            }}
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}

            {/* Refund Modal */}
            {selectedOrderForRefund && (
                <RefundModal
                    isOpen={refundModalOpen}
                    onClose={() => {
                        setRefundModalOpen(false);
                        setSelectedOrderForRefund(null);
                    }}
                    order={selectedOrderForRefund}
                    eventId={effectiveEventId}
                />
            )}

            <Modal isOpen={!!infoOrder} onOpenChange={(open) => { if (!open) setInfoOrder(null); }} scrollBehavior="inside" size="2xl">
                <ModalContent>
                    <ModalHeader>Edit order info</ModalHeader>
                    <ModalBody>
                        <Input label="Name" value={infoForm.name} onValueChange={(v) => setInfoForm((f) => ({ ...f, name: v }))} />
                        <Input type="email" label="Email" value={infoForm.email} onValueChange={(v) => setInfoForm((f) => ({ ...f, email: v }))} isInvalid={!!infoForm.email && !emailPattern.test(infoForm.email.trim())} />
                        <Input label="Phone" value={infoForm.phone} onValueChange={(v) => setInfoForm((f) => ({ ...f, phone: v }))} />
                        {infoTickets.length > 0 && <div className="text-sm font-semibold pt-2">Attendees</div>}
                        {infoTickets.map((t) => (
                            <div key={t.index} className="flex flex-col gap-2 rounded-xl border border-default-200 p-3">
                                <div className="text-xs font-semibold">{t.label}</div>
                                <Input size="sm" label="Attendee name" value={t.attendeeName} onValueChange={(v) => patchInfoTicket(t.index, { attendeeName: v })} />
                                <Input size="sm" type="email" label="Attendee email" value={t.attendeeEmail} onValueChange={(v) => patchInfoTicket(t.index, { attendeeEmail: v })} isInvalid={!!t.attendeeEmail && !emailPattern.test(t.attendeeEmail.trim())} />
                                <Input size="sm" label="Attendee phone" value={t.attendeePhone} onValueChange={(v) => patchInfoTicket(t.index, { attendeePhone: v })} />
                            </div>
                        ))}
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="light" onPress={() => setInfoOrder(null)}>Cancel</Button>
                        <Button
                            color="primary"
                            isLoading={updateOrderInfoMutation.isPending}
                            isDisabled={!infoValid}
                            onPress={() => updateOrderInfoMutation.mutate(infoOrder)}
                        >
                            Save
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Email Modal */}
            {selectedOrderForEmail && (
                <EmailModal
                    isOpen={emailModalOpen}
                    onClose={() => {
                        setEmailModalOpen(false);
                        setSelectedOrderForEmail(null);
                    }}
                    order={selectedOrderForEmail}
                    eventTitle={event?.data?.title || event?.title}
                />
            )}

            {/* Promocode Drawer */}
            <Drawer
                isOpen={isPromocodeModalOpen}
                onOpenChange={onPromocodeModalOpenChange}
                placement="right"
                size={isMobile ? 'full' : 'lg'}
                scrollBehavior="inside"
                hideCloseButton
                isDismissable={false}
                classNames={{
                    wrapper: isMobile ? 'z-[100000]' : 'top-[80px] h-[calc(100dvh-80px)] z-[100000]',
                    base: isMobile ? 'bg-white dark:bg-nightSurface h-[100dvh] my-0 z-[100000]' : 'bg-white dark:bg-nightSurface h-[calc(100dvh-80px)] my-0 z-[100000] rounded-l-[28px] ring-1 ring-black/[0.06] dark:ring-white/10',
                    backdrop: isMobile ? 'z-[100000]' : 'top-[80px] h-[calc(100dvh-80px)] z-[100000] bg-black/50',
                }}
            >
                <DrawerContent>
                    {(onClose) => (
                        <>
                            <DrawerHeader className="flex items-center justify-between px-6 pt-6 pb-4">
                                <div>
                                    <h3 className="text-[28px] font-bold leading-[1] tracking-[-0.04em] text-grey10 dark:text-white">
                                        {editingPromocode ? 'Edit Promocode' : 'Create a Promocode'}
                                    </h3>
                                    <p className="text-sm font-normal text-grey40 dark:text-nightMuted mt-2">
                                        {editingPromocode ? 'Update your promocode details' : 'Set up a new promotional code for this ticket'}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className={ecIconBtn}
                                    aria-label="Close"
                                >
                                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                        <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                    </svg>
                                </button>
                            </DrawerHeader>
                            <DrawerBody className="px-6 py-2">
                                {promocodeModalTicketId !== null && (() => {
                                    const currentTicket = tickets.find((t: any) => t._id === promocodeModalTicketId);
                                    const currentPromocode = editingPromocode && currentTicket?.promocodes
                                        ? currentTicket.promocodes.find((p: any) => p._id === editingPromocode)
                                        : null;
                                    const dateRanges = eventData?.times?.map((t: any) => ({
                                        start: t.startDate ? parseDate(t.startDate.split(' ')[0]) : today(getLocalTimeZone()),
                                        end: t.endDate ? parseDate(t.endDate.split(' ')[0]) : today(getLocalTimeZone()),
                                    })) || [];
                                    return (
                                        <PromocodeForm
                                            dateRanges={dateRanges}
                                            promocode={currentPromocode}
                                            onSave={handleSavePromocode}
                                            onCancel={onClose}
                                            ticketId={promocodeModalTicketId}
                                            formRef={promocodeFormRef}
                                        />
                                    );
                                })()}
                            </DrawerBody>
                            <DrawerFooter className="px-6 py-5 gap-2 border-t border-black/[0.06] dark:border-white/10">
                                <Button variant="light" onPress={onClose} className={`${ecBtnGhost} bg-transparent`}>
                                    Cancel
                                </Button>
                                <Button
                                    className={ecBtnAccent}
                                    onPress={() => {
                                        if (promocodeFormRef.current) {
                                            promocodeFormRef.current();
                                        }
                                    }}
                                >
                                    {editingPromocode ? 'Update' : 'Create'}
                                </Button>
                            </DrawerFooter>
                        </>
                    )}
                </DrawerContent>
            </Drawer>

            {/* Bulk Email Modal */}
            <BulkEmailModal
                isOpen={bulkEmailModalOpen}
                onClose={() => setBulkEmailModalOpen(false)}
                orders={orders}
                eventTitle={event?.data?.title || event?.title}
            />

            {/* Bulk Free Tickets Modal */}
            <BulkFreeTicketsModal
                isOpen={bulkFreeTicketsModalOpen}
                onClose={() => setBulkFreeTicketsModalOpen(false)}
                onSuccess={() => refetchOrders()}
                eventId={eventData?._id || eventData?.id || ''}
                currency={eventData?.currency}
                tickets={(eventData?.tickets || []).map((t: any) => ({
                    _id: t._id,
                    name: t.name,
                    remained: t.remained ?? 0,
                    type: t.type || 'Free',
                    price: typeof t.price === 'string' ? parseFloat(t.price) || 0 : t.price ?? 0,
                    promocodes: t.promocodes ?? [],
                }))}
                eventTimes={(eventData?.times || []).map((t: any) => ({
                    startDate: t.startDate,
                    endDate: t.endDate,
                }))}
            />

            {/* Status Change Drawer */}
            <Drawer
                placement="right"
                hideCloseButton
                isDismissable={false}
                isOpen={statusChangeModalOpen && !!selectedOrderForStatusChange}
                onClose={() => {
                    setStatusChangeModalOpen(false);
                    setSelectedOrderForStatusChange(null);
                    setNewStatus('');
                    setStatusChangeNotes('');
                }}
                size={isMobile ? 'full' : 'md'}
                scrollBehavior="inside"
                classNames={{
                    wrapper: isMobile ? 'z-[100000]' : 'top-[80px] h-[calc(100dvh-80px)] z-[100000]',
                    base: isMobile ? 'bg-white dark:bg-nightSurface h-[100dvh] my-0 z-[100000]' : 'bg-white dark:bg-nightSurface h-[calc(100dvh-80px)] my-0 z-[100000] rounded-l-[28px] ring-1 ring-black/[0.06] dark:ring-white/10',
                    backdrop: isMobile ? 'z-[100000]' : 'top-[80px] h-[calc(100dvh-80px)] z-[100000] bg-black/50',
                }}
            >
                <DrawerContent>
                    {selectedOrderForStatusChange && (
                        <>
                            <DrawerHeader className="flex items-center justify-between px-6 pt-6 pb-4">
                                <h2 className="text-[28px] font-bold leading-[1] tracking-[-0.04em] text-grey10 dark:text-white">Change Order Status</h2>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setStatusChangeModalOpen(false);
                                        setSelectedOrderForStatusChange(null);
                                        setNewStatus('');
                                        setStatusChangeNotes('');
                                    }}
                                    className={ecIconBtn}
                                    aria-label="Close"
                                >
                                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                        <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                    </svg>
                                </button>
                            </DrawerHeader>

                            <DrawerBody className="px-6 py-2 gap-6">
                                <div>
                                    <div className="text-xs font-bold uppercase tracking-[0.14em] text-grey40 dark:text-nightMuted mb-3">Order Details</div>
                                    <div className="bg-grey99 ring-1 ring-black/[0.04] dark:bg-white/[0.04] dark:ring-white/[0.06] rounded-[22px] p-5">
                                        <div className="text-sm flex flex-col gap-3">
                                            <div className="flex justify-between items-center">
                                                <span className="text-grey40 dark:text-nightMuted">Customer:</span>
                                                <span className="font-semibold text-grey10 dark:text-white">{selectedOrderForStatusChange.customerName || selectedOrderForStatusChange.guestName || selectedOrderForStatusChange.attendeeName || 'N/A'}</span>
                                            </div>
                                            <div className="flex justify-between mb-1">
                                                <span className="text-grey40 dark:text-nightMuted">Order ID:</span>
                                                <span className="font-semibold text-grey10 dark:text-white">{selectedOrderForStatusChange.hashedUniqueId || selectedOrderForStatusChange.orderId}</span>
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <span className="text-grey40 dark:text-nightMuted">Current Status:</span>
                                                <span className="font-medium">{getStatusBadge(selectedOrderForStatusChange.status)}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-grey20 dark:text-nightTextSecondary mb-2">New Status</label>
                                    <Select
                                        className="w-full"
                                        classNames={ecSelectCls}
                                        selectedKeys={[newStatus]}
                                        onSelectionChange={value => setNewStatus(value.currentKey as string)}
                                        aria-label="New status"
                                        placeholder="Select a status..."
                                        items={[
                                            { value: 'pending', label: 'Pending' },
                                            { value: 'completed', label: 'Completed' },
                                            { value: 'cancelled', label: 'Cancelled' },
                                            { value: 'refunded', label: 'Refunded' },
                                            { value: 'pending-refund', label: 'Pending Refund' },
                                            { value: 'disputed', label: 'Disputed' }
                                        ]}
                                    >
                                        {(item) => <SelectItem key={item.value}>{item.label}</SelectItem>}
                                    </Select>
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-grey20 dark:text-nightTextSecondary mb-2">Notes (Optional)</label>
                                    <Textarea
                                        className="w-full"
                                        classNames={ecTextareaCls}
                                        rows={3}
                                        aria-label="Notes"
                                        placeholder="Notes..."
                                        value={statusChangeNotes}
                                        onChange={e => setStatusChangeNotes(e.target.value)}
                                    />
                                </div>
                            </DrawerBody>

                            <DrawerFooter className="px-6 py-5 gap-2 border-t border-black/[0.06] dark:border-white/10">
                                <Button
                                    variant="light"
                                    onPress={() => {
                                        setStatusChangeModalOpen(false);
                                        setSelectedOrderForStatusChange(null);
                                        setNewStatus('');
                                        setStatusChangeNotes('');
                                    }}
                                    className={`${ecBtnGhost} bg-transparent`}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onPress={handleStatusChangeSubmit}
                                    isDisabled={!newStatus}
                                    isLoading={updateOrderStatusMutation.isPending}
                                    className={ecBtnAccent}
                                >
                                    Update Status
                                </Button>
                            </DrawerFooter>
                        </>
                    )}
                </DrawerContent>
            </Drawer>

            {/* Ticket Delete Confirmation Modal */}
            <DeleteConfirmModal
                isOpen={ticketDeleteConfirmModalOpen && ticketToDelete !== null}
                onClose={() => {
                    setTicketDeleteConfirmModalOpen(false);
                    setTicketToDelete(null);
                }}
                onConfirm={confirmRemoveTicket}
                title="Delete Ticket"
                message="This removes the ticket type and stops it from being used for new purchases. Existing orders are preserved. Ticket types with sales cannot be deleted; deactivate them instead."
                itemName={ticketToDelete !== null ? tickets[ticketToDelete]?.name : undefined}
                loading={deleteTicketMutation.isPending || isDeletingTicket}
            />

        </>
    );
} 